import React, { useState, useEffect, useMemo, useCallback, Suspense, lazy, useRef, startTransition } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { auth, db, storage, functions } from './firebase';
import { onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, GoogleAuthProvider, OAuthProvider, signInWithCredential, updateProfile } from 'firebase/auth';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { doc, collection, onSnapshot, getDoc, getDocFromServer, setDoc, updateDoc, arrayUnion, arrayRemove, serverTimestamp, query, where, getDocs, orderBy, writeBatch, Timestamp, addDoc, deleteDoc, deleteField, runTransaction } from 'firebase/firestore';
import { ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';

import ArrowLeftIcon from './components/icons/ArrowLeftIcon';
import BottomNav from './components/BottomNav';
import PostViewerPage from './components/PostViewerPage';
import PostCommentsPage from './components/PostCommentsPage';
import ZipCommentsPage from './components/ZipCommentsPage';
import NewsViewerPage from './components/NewsViewerPage';
import SharePostPage from './components/SharePostPage';
import ShareZipPage from './components/ShareZipPage';
import ShareSpotPage from './components/ShareSpotPage';
import ShareJamSeshPage from './components/ShareJamSeshPage';
import ShareEventPage from './components/ShareEventPage';
import ShareLocationPage from './components/ShareLocationPage';
import LegalDocumentPage from './components/LegalDocumentPage';
import DeveloperDashboard from './components/DeveloperDashboard';
import OGWelcomePopup from './components/OGWelcomePopup';
const OpenMapsPage = lazy(() => import('./sections/OpenMapsPage'));
import { FeedSkeleton, ProfileSkeleton, ListSkeleton, DiscoverSkeleton, MarketplaceSkeleton, MessengerSkeleton, NotificationsSkeleton, ZipsSkeleton, MapSkeleton, FullScreenSkeleton, ChatSkeleton } from './components/Skeleton';
import UploadPostPage, { type UploadPostDraft } from './components/UploadPostPage';
import CreateMediaPage, { type CreateMode } from './components/CreateMediaPage';
import UploadNewsPage from './components/UploadNewsPage';
import { setAuthPending } from './utils/authPending';
import BannedPage from './components/BannedPage';
import { cleanText, cleanLine, cleanPrice, isValidEmail, TEXT_LIMITS } from './utils/inputSafety';
import { allowAction, TOO_FAST_MESSAGE } from './utils/requestThrottle';
import UploadZipPage, { type UploadZipDraft } from './components/UploadZipPage';
import type { Listing, ListingOffer, ListingReview, MediaOverlays, MediaTextOverlay, NavItem, MessageThread, User, Sport, Post, Spot, Shop, JamSesh, Event, UserStory, StoryItem, ChatMessage, LiveLocationShare, Zip, SpotRatingMap, LocationTag, MessageReplyPreview, ChatEvent } from './types';
import { APP_LOGO_SRC, NAV_ITEMS, SPORTS, NOTIFICATIONS_NAV } from './constants';
import { censorInappropriateText, hasInappropriateUsername, isValidUsernameFormat } from './utils/contentModeration';
import { getStaticMapTileUrl } from './utils/staticMap';
import { loadCachedBootData, saveCachedBootData } from './utils/dataCache';
import { useSwipeBack } from './hooks/useSwipeBack';
import { appGeolocation } from './utils/geolocation';
import {
  convertImageToJpeg,
  compressAndDownscaleImage,
  compressAndDownscaleVideo,
  readVideoMetadata,
  runWithTimeout,
  uploadFileWithTimeout
} from './utils/mediaUtils';

// Lazy load all heavy sections for better initial load performance
const Feed = lazy(() => import('./sections/Feed'));
const Zips = lazy(() => import('./sections/Zips'));
const Discover = lazy(() => import('./sections/Discover'));
const Marketplace = lazy(() => import('./sections/Marketplace'));
const SpotMap = lazy(() => import('./sections/SpotMap'));
const Profile = lazy(() => import('./sections/Profile'));
const Messenger = lazy(() => import('./sections/Messenger'));
const ChatDetail = lazy(() => import('./sections/ChatDetail'));
const CallScreen = lazy(() => import('./components/CallScreen'));
import CallWatcher from './components/CallWatcher';
import { RidazCalls, hasNativeCalls } from './utils/nativeCalls';
import { finishBootScreen } from './utils/bootLanding';
import type { NativeCallAccept } from './utils/nativeCalls';
const CreateGroupChat = lazy(() => import('./sections/CreateGroupChat'));
const Auth = lazy(() => import('./sections/Auth'));
const Notifications = lazy(() => import('./sections/Notifications'));
const RatingProfilePage = lazy(() => import('./components/RatingProfilePage'));
const GoogleOnboarding = lazy(() => import('./sections/GoogleOnboarding'));
const WelcomeOnboarding = lazy(() => import('./sections/WelcomeOnboarding'));


const getInitialLegalPage = (): { type: 'privacy' | 'terms' | 'support' | 'marketing' | 'eula'; pushedHistory?: boolean } | null => {
  if (typeof window === 'undefined') return null;
  if (window.location.pathname === '/privacy-policy') {
    return { type: 'privacy', pushedHistory: false };
  }
  if (window.location.pathname === '/terms-of-service') {
    return { type: 'terms', pushedHistory: false };
  }
  if (window.location.pathname === '/customer-support') {
    return { type: 'support', pushedHistory: false };
  }
  if (window.location.pathname === '/marketing') {
    return { type: 'marketing', pushedHistory: false };
  }
  if (window.location.pathname === '/eula') {
    return { type: 'eula', pushedHistory: false };
  }
  return null;
};

// Username login: the server checks the password first and only then returns the email (so emails can't be harvested).
const verifyUsernameLoginCallable = httpsCallable<{ username: string; password: string }, { email: string }>(functions, 'verifyUsernameLogin');
// Moderator-only: ban a user and erase all their content (runs for a while on the server, so a long timeout).
const banUserAndEraseCallable = httpsCallable<{ userId: string }, { success: boolean }>(functions, 'banUserAndErase', { timeout: 540000 });

// The account that was last signed in on this device. It is cleared only when the person presses Log out (or deletes their
// account), so if the app later finds itself signed out without that happening - e.g. a moderator banned the account while the
// phone was offline, and once it reconnected Firebase refused its session - the app can look up that account and, if it was
// banned, show the banned page instead of silently dropping to the sign-in screen.
const LAST_UID_KEY = 'ridaz_last_uid';
const readLastUid = (): string => { try { return window.localStorage.getItem(LAST_UID_KEY) || ''; } catch { return ''; } };
const writeLastUid = (uid: string) => { try { window.localStorage.setItem(LAST_UID_KEY, uid); } catch { /* storage unavailable */ } };
const clearLastUid = () => { try { window.localStorage.removeItem(LAST_UID_KEY); } catch { /* storage unavailable */ } };
// users/{uid} is publicly readable (guest profile browsing), so this works even after the session has been revoked.
// Reads from the server on purpose (a cached copy from before the ban would say "not banned"); offline, it just answers false.
const isUidBanned = async (uid: string): Promise<boolean> => {
  try {
    const snap = await getDocFromServer(doc(db, 'users', uid));
    return snap.exists() && snap.data()?.isBanned === true;
  } catch {
    return false;
  }
};
const deleteAccountCallable = httpsCallable<void, { success: boolean }>(functions, 'deleteAccount');

const App: React.FC = () => {
  // Public, no-login, no-app-bootstrap route - short-circuits before any of
  // this component's ~150 hooks/state or the auth/data fetch below run, so
  // it loads fast for someone who doesn't have the app (e.g. a "Share
  // elsewhere" link recipient). Stable for the lifetime of this mount since
  // there's no client-side router changing the path afterward, so this
  // doesn't violate the rules of hooks - see sections/OpenMapsPage.tsx.
  if (typeof window !== 'undefined' && window.location.pathname === '/openmaps') {
    return (
      <Suspense fallback={null}>
        <OpenMapsPage />
      </Suspense>
    );
  }

  type LegalPageType = 'privacy' | 'terms' | 'support' | 'marketing' | 'eula';
  type LegalReturnTarget = 'main-profile-settings' | 'overlay-profile-settings' | null;
  const [users, setUsers] = useState<User[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [zips, setZips] = useState<Zip[]>([]);
  const [spots, setSpots] = useState<Spot[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [listings, setListings] = useState<Listing[]>([]);
  const [listingOffers, setListingOffers] = useState<ListingOffer[]>([]);
  const [listingReviews, setListingReviews] = useState<ListingReview[]>([]);
  const [focusedListingId, setFocusedListingId] = useState<string | null>(null);
  const [jamSeshes, setJamSeshes] = useState<JamSesh[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [messageThreads, setMessageThreads] = useState<MessageThread[]>([]);
  const [stories, setStories] = useState<UserStory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const bootLoaderMountTimeRef = useRef(Date.now());
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  // The @ridaz account is RIDAZ's developer/moderator account - gates the
  // moderation controls (ban user, take down posts/comments, remove map
  // markers) surfaced elsewhere in the app. Mirrors the Firestore rules'
  // isModerator() check, which is what actually enforces this server-side;
  // this flag only controls whether the UI is shown.
  const isRidazModerator = currentUser?.username?.toLowerCase() === 'ridaz';
  const [isDeveloperDashboardOpen, setIsDeveloperDashboardOpen] = useState(false);
  const [activeNav, setActiveNavState] = useState<NavItem>(NAV_ITEMS[0]);
  const [marketplaceResetSignal, setMarketplaceResetSignal] = useState(0);
  // Messages tab: whether the single "Shop" row has been opened to show all shop chats.
  const [shopChatsOpen, setShopChatsOpen] = useState(false);
  // Audio/video call screen (CallScreen) - lives here so it survives navigating around the app.
  const [callSession, setCallSession] = useState<{ thread: MessageThread; type: 'audio' | 'video'; joinOnly?: boolean } | null>(null);
  // Native ringing (CallKit / full-screen notification) is registered, so skip the in-app ring screen.
  const [nativeRingReady, setNativeRingReady] = useState(false);
  // A call the user accepted from the OS ring UI; joined as soon as they're signed in.
  const [nativeAccept, setNativeAccept] = useState<NativeCallAccept | null>(null);
  const activeNavIdRef = useRef(activeNav.id);
  useEffect(() => {
    activeNavIdRef.current = activeNav.id;
  }, [activeNav.id]);
  const [hasVisitedSpotMap, setHasVisitedSpotMap] = useState(false);
  useEffect(() => {
    if (activeNav.id === 'spot-map') setHasVisitedSpotMap(true);
  }, [activeNav.id]);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [isCreateGroupOpen, setCreateGroupOpen] = useState(false);
  const [viewedUser, setViewedUser] = useState<User | null>(null);
  const [profileOverlay, setProfileOverlay] = useState<User | null>(null);
  const [isGuest, setIsGuest] = useState(false);
  // True once onAuthStateChanged has fired at least once. Both currentUser
  // and isGuest start out as their "signed out" defaults, so gating only on
  // isLoading (which can now clear early thanks to cached boot data) isn't
  // enough - it previously let the render fall through to the signed-out
  // Auth screen for a frame before auth had actually resolved. This flag
  // closes that gap regardless of how fast/slow the data fetch is.
  const [isAuthResolved, setIsAuthResolved] = useState(false);
  // Set right after a brand-new account is created (either signup path);
  // watched below so the OG popup only ever shows once, right after the
  // assignOGBadge Cloud Function's isOG write arrives via the live
  // users/{uid} listener - never on a later login/relaunch.
  const [justSignedUpUid, setJustSignedUpUid] = useState<string | null>(null);
  const [showOGPopup, setShowOGPopup] = useState(false);
  // True when a banned person tries to get in: shows the full-screen "You have been banned" page.
  const [showBanned, setShowBanned] = useState(false);
  const [fallbackThread, setFallbackThread] = useState<MessageThread | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));
  const [unseenNotifications, setUnseenNotifications] = useState(0);
  const [notificationFilterType, setNotificationFilterType] = useState<'all' | 'likes' | 'comments' | 'tags'>('all');
  const [postViewer, setPostViewer] = useState<{
    posts: Post[];
    startIndex: number;
    sourceNav: NavItem;
    scrollY: number;
  } | null>(null);
  const [postCommentsPage, setPostCommentsPage] = useState<{
    postId: string;
    fallbackPost: Post;
    pushedHistory?: boolean;
  } | null>(null);
  const [zipCommentsPage, setZipCommentsPage] = useState<{
    zipId: string;
    fallbackZip: Zip;
    pushedHistory?: boolean;
  } | null>(null);
  const [sharePostPage, setSharePostPage] = useState<{
    postId: string;
    fallbackPost: Post;
    pushedHistory?: boolean;
  } | null>(null);
  const [shareZipPage, setShareZipPage] = useState<{
    zipId: string;
    fallbackZip: Zip;
    pushedHistory?: boolean;
  } | null>(null);
  const [shareSpotPage, setShareSpotPage] = useState<{
    spotId: string;
    fallbackSpot: Spot;
    pushedHistory?: boolean;
  } | null>(null);
  const [shareJamSeshPage, setShareJamSeshPage] = useState<{
    jamSeshId: string;
    fallbackJamSesh: JamSesh;
    pushedHistory?: boolean;
  } | null>(null);
  const [shareEventPage, setShareEventPage] = useState<{
    eventId: string;
    fallbackEvent: Event;
    pushedHistory?: boolean;
  } | null>(null);
  const [shareLocationPageOpen, setShareLocationPageOpen] = useState<{
    pushedHistory?: boolean;
  } | null>(null);
  const [legalPage, setLegalPage] = useState<{
    type: LegalPageType;
    pushedHistory?: boolean;
  } | null>(() => getInitialLegalPage());
  const [legalReturnTarget, setLegalReturnTarget] = useState<LegalReturnTarget>(null);
  const [mapFocusLocation, setMapFocusLocation] = useState<{ lat: number; lng: number; username: string } | null>(null);
  const [focusedSpotId, setFocusedSpotId] = useState<string | null>(null);
  const [focusedJamSeshId, setFocusedJamSeshId] = useState<string | null>(null);
  const [focusedEventId, setFocusedEventId] = useState<string | null>(null);
  const [newsViewer, setNewsViewer] = useState<{
    stories: UserStory[];
    startIndex: number;
    scrollY: number;
    pushedHistory?: boolean;
  } | null>(null);
  const [isUploadPageOpen, setIsUploadPageOpen] = useState(false);
  const [isDeleteAccountPageOpen, setIsDeleteAccountPageOpen] = useState(false);
  const deleteAccountPagePushedHistoryRef = useRef(false);
  // Notifications opens as an overlay on top of the (still mounted) home feed instead of swapping pages, so the 3D
  // logo you tapped is never torn down - that teardown/rebuild is what flashed on screen.
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  // Whose Buy & Sell rating page is open (from a chat banner, or "My Profile" in Buy & Sell).
  const [ratingProfileUser, setRatingProfileUser] = useState<User | null>(null);
  const [isPostUploading, setIsPostUploading] = useState(false);
  // Progress of the background post upload, shown as a bar under the news row on the home feed: 0-1, plus what it's doing.
  const [postUploadProgress, setPostUploadProgress] = useState(0);
  const [postUploadStage, setPostUploadStage] = useState<'preparing' | 'uploading' | 'finishing'>('preparing');
  const [isUploadNewsPageOpen, setIsUploadNewsPageOpen] = useState(false);
  const [isUploadZipPageOpen, setIsUploadZipPageOpen] = useState(false);
  const [postDraft, setPostDraft] = useState<UploadPostDraft | null>(null);
  const [newsInitialFile, setNewsInitialFile] = useState<File | null>(null);
  const [zipDraft, setZipDraft] = useState<UploadZipDraft | null>(null);
  const [isPickingLocationFor, setIsPickingLocationFor] = useState<'post' | 'zip' | null>(null);
  // Where "Back to post/zip" on the map should take the user: the tab they were on (and how far they had scrolled),
  // or - if they were inside the post viewer (e.g. opened from Discover or a profile) - that same viewer.
  const [returnTo, setReturnTo] = useState<
    | { type: 'post'; postId: string; fromNav: NavItem; scrollY: number; viewer: { posts: Post[]; startIndex: number } | null }
    | { type: 'zip'; zipId: string }
    | null
  >(null);
  const [deviceLocation, setDeviceLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [liveLocationShares, setLiveLocationShares] = useState<LiveLocationShare[]>([]);
  const [focusedZipId, setFocusedZipId] = useState<string | null>(null);
  const uploadPageScrollRef = useRef(0);
  const uploadNewsPageScrollRef = useRef(0);
  const uploadZipPageScrollRef = useRef(0);
  const locationSyncTimestampRef = useRef(0);
  const lastSyncedLocationRef = useRef<{ lat: number; lng: number } | null>(null);
  const lastWatchedLocationRef = useRef<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateViewportHeight = () => {
      document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
    };

    updateViewportHeight();
    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', updateViewportHeight);

    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const orientation = window.screen?.orientation;
    if (!orientation || typeof orientation.lock !== 'function') return;

    orientation.lock('portrait').catch(() => undefined);
  }, []);

  // The boot screen (index.html) is now a continuously-looping spinner (logo
  // + orbiting dot), not a one-shot narrative animation, so it only needs a
  // brief guaranteed-visible minimum to avoid an instant flash-and-gone on a
  // very fast load - it waits for BOTH the real data load (isLoading) and
  // that minimum, whichever finishes last.
  useEffect(() => {
    // Was gated on isLoading alone, but the render-gate below also waits on
    // isAuthResolved - so whenever data finished loading before auth had
    // resolved, this hid/removed the boot screen while that gate's fallback
    // screen was still showing underneath it, briefly exposing it.
    if (isLoading || !isAuthResolved) return;
    const bootLoader = document.getElementById('boot-loading-screen');
    if (!bootLoader) return;

    // Long enough for the loading scene's intro to play and the spinning logo to be seen.
    const MIN_BOOT_ANIMATION_MS = 1200;
    const elapsed = Date.now() - bootLoaderMountTimeRef.current;
    const remaining = Math.max(0, MIN_BOOT_ANIMATION_MS - elapsed);

    let cancelled = false;
    const hideTimer = window.setTimeout(() => {
      // The 3D logo swooshes up and lands on the Feed header's logo (see utils/bootLanding.ts); with
      // nothing to land on it's just a quick fade. Either way the loader is removed afterwards.
      finishBootScreen(bootLoader, { expectHeaderLogo: Boolean(currentUser || isGuest) && activeNav.id === 'feed' }).then(() => {
        if (!cancelled) bootLoader.remove();
      });
    }, remaining);

    return () => {
      cancelled = true;
      window.clearTimeout(hideTimer);
    };
  }, [isLoading, isAuthResolved]);

  // Debounced state setters to batch rapid Firestore updates
  const debouncedSetPosts = useCallback((newPosts: Post[]) => {
    setPosts(newPosts);
  }, []);

  const debouncedSetUsers = useCallback((newUsers: User[]) => {
    setUsers(newUsers);
  }, []);

  const hydrateThreadParticipants = useCallback((thread: MessageThread): MessageThread => {
    const hydratedParticipants = (thread.participants || []).map((participant) => {
      const liveUser = users.find((user) => user.id === participant.id);
      return liveUser ? { ...participant, ...liveUser } : participant;
    });

    return {
      ...thread,
      participants: hydratedParticipants
    };
  }, [users]);

  const getThreadType = useCallback((threadLike: Partial<MessageThread> | null | undefined): MessageThread['chatType'] => {
    if (threadLike?.chatType === 'group') return 'group';
    if (threadLike?.chatType === 'crew') return 'crew';
    return threadLike?.isGroup ? 'group' : 'direct';
  }, []);

  const openPostViewer = useCallback((postsList: Post[], startIndex: number) => {
    const scrollEl = document.getElementById('app-scroll');
    const currentScroll = scrollEl ? scrollEl.scrollTop : (typeof window !== 'undefined' ? window.scrollY : 0);
    setPostViewer({
      posts: postsList,
      startIndex,
      sourceNav: activeNav,
      scrollY: currentScroll,
    });
  }, [activeNav]);

  const closePostViewer = useCallback(() => {
    setPostViewer(prev => {
      if (typeof window !== 'undefined' && prev) {
        const scrollEl = document.getElementById('app-scroll');
        if (scrollEl) {
          scrollEl.scrollTop = prev.scrollY;
        } else {
          window.scrollTo({ top: prev.scrollY, behavior: 'auto' });
        }
      }
      return null;
    });
  }, []);

  const openPostComments = useCallback((post: Post, options?: { skipPush?: boolean }) => {
    setPostCommentsPage(prev => {
      const alreadyOpen = !!prev;
      if (!alreadyOpen && typeof window !== 'undefined' && !options?.skipPush) {
        window.history.pushState({ postComments: true }, '', window.location.href);
      }
      return {
        postId: post.id,
        fallbackPost: post,
        pushedHistory: !alreadyOpen && !options?.skipPush,
      };
    });
  }, []);

  const closePostComments = useCallback((skipHistory?: boolean) => {
    setPostCommentsPage(prev => {
      if (!prev) return null;
      if (!skipHistory && prev.pushedHistory && typeof window !== 'undefined' && window.history.state?.postComments) {
        window.history.back();
      }
      return null;
    });
  }, []);

  const openZipComments = useCallback((zip: Zip, options?: { skipPush?: boolean }) => {
    setZipCommentsPage(prev => {
      const alreadyOpen = !!prev;
      if (!alreadyOpen && typeof window !== 'undefined' && !options?.skipPush) {
        window.history.pushState({ zipComments: true }, '', window.location.href);
      }
      return {
        zipId: zip.id,
        fallbackZip: zip,
        pushedHistory: !alreadyOpen && !options?.skipPush,
      };
    });
  }, []);

  const closeZipComments = useCallback((skipHistory?: boolean) => {
    setZipCommentsPage(prev => {
      if (!prev) return null;
      if (!skipHistory && prev.pushedHistory && typeof window !== 'undefined' && window.history.state?.zipComments) {
        window.history.back();
      }
      return null;
    });
  }, []);

  const openSharePostPage = useCallback((post: Post, options?: { skipPush?: boolean }) => {
    setSharePostPage((prev) => {
      const alreadyOpen = !!prev;
      if (!alreadyOpen && typeof window !== 'undefined' && !options?.skipPush) {
        window.history.pushState({ sharePost: true }, '', window.location.href);
      }
      return {
        postId: post.id,
        fallbackPost: post,
        pushedHistory: !alreadyOpen && !options?.skipPush,
      };
    });
  }, []);

  const closeSharePostPage = useCallback((skipHistory?: boolean) => {
    setSharePostPage((prev) => {
      if (!prev) return null;
      if (!skipHistory && prev.pushedHistory && typeof window !== 'undefined' && window.history.state?.sharePost) {
        window.history.back();
      }
      return null;
    });
  }, []);

  const openShareZipPage = useCallback((zip: Zip, options?: { skipPush?: boolean }) => {
    setShareZipPage((prev) => {
      const alreadyOpen = !!prev;
      if (!alreadyOpen && typeof window !== 'undefined' && !options?.skipPush) {
        window.history.pushState({ shareZip: true }, '', window.location.href);
      }
      return {
        zipId: zip.id,
        fallbackZip: zip,
        pushedHistory: !alreadyOpen && !options?.skipPush,
      };
    });
  }, []);

  const closeShareZipPage = useCallback((skipHistory?: boolean) => {
    setShareZipPage((prev) => {
      if (!prev) return null;
      if (!skipHistory && prev.pushedHistory && typeof window !== 'undefined' && window.history.state?.shareZip) {
        window.history.back();
      }
      return null;
    });
  }, []);

  const openShareSpotPage = useCallback((spot: Spot, options?: { skipPush?: boolean }) => {
    setShareSpotPage((prev) => {
      const alreadyOpen = !!prev;
      if (!alreadyOpen && typeof window !== 'undefined' && !options?.skipPush) {
        window.history.pushState({ shareSpot: true }, '', window.location.href);
      }
      return {
        spotId: spot.id,
        fallbackSpot: spot,
        pushedHistory: !alreadyOpen && !options?.skipPush,
      };
    });
  }, []);

  const closeShareSpotPage = useCallback((skipHistory?: boolean) => {
    setShareSpotPage((prev) => {
      if (!prev) return null;
      if (!skipHistory && prev.pushedHistory && typeof window !== 'undefined' && window.history.state?.shareSpot) {
        window.history.back();
      }
      return null;
    });
  }, []);

  const openShareJamSeshPage = useCallback((jamSesh: JamSesh, options?: { skipPush?: boolean }) => {
    setShareJamSeshPage((prev) => {
      const alreadyOpen = !!prev;
      if (!alreadyOpen && typeof window !== 'undefined' && !options?.skipPush) {
        window.history.pushState({ shareJamSesh: true }, '', window.location.href);
      }
      return {
        jamSeshId: jamSesh.id,
        fallbackJamSesh: jamSesh,
        pushedHistory: !alreadyOpen && !options?.skipPush,
      };
    });
  }, []);

  const closeShareJamSeshPage = useCallback((skipHistory?: boolean) => {
    setShareJamSeshPage((prev) => {
      if (!prev) return null;
      if (!skipHistory && prev.pushedHistory && typeof window !== 'undefined' && window.history.state?.shareJamSesh) {
        window.history.back();
      }
      return null;
    });
  }, []);

  const openShareEventPage = useCallback((event: Event, options?: { skipPush?: boolean }) => {
    setShareEventPage((prev) => {
      const alreadyOpen = !!prev;
      if (!alreadyOpen && typeof window !== 'undefined' && !options?.skipPush) {
        window.history.pushState({ shareEvent: true }, '', window.location.href);
      }
      return {
        eventId: event.id,
        fallbackEvent: event,
        pushedHistory: !alreadyOpen && !options?.skipPush,
      };
    });
  }, []);

  const closeShareEventPage = useCallback((skipHistory?: boolean) => {
    setShareEventPage((prev) => {
      if (!prev) return null;
      if (!skipHistory && prev.pushedHistory && typeof window !== 'undefined' && window.history.state?.shareEvent) {
        window.history.back();
      }
      return null;
    });
  }, []);

  const openShareLocationPage = useCallback((options?: { skipPush?: boolean }) => {
    setShareLocationPageOpen((prev) => {
      const alreadyOpen = !!prev;
      if (!alreadyOpen && typeof window !== 'undefined' && !options?.skipPush) {
        window.history.pushState({ shareLocation: true }, '', window.location.href);
      }
      return {
        pushedHistory: !alreadyOpen && !options?.skipPush,
      };
    });
  }, []);

  const closeShareLocationPage = useCallback((skipHistory?: boolean) => {
    setShareLocationPageOpen((prev) => {
      if (!prev) return null;
      if (!skipHistory && prev.pushedHistory && typeof window !== 'undefined' && window.history.state?.shareLocation) {
        window.history.back();
      }
      return null;
    });
  }, []);

  const openLegalPage = useCallback((type: LegalPageType, options?: { skipPush?: boolean; returnTarget?: LegalReturnTarget }) => {
    setLegalPage((prev) => {
      const alreadyOpen = !!prev;
      if (!alreadyOpen && typeof window !== 'undefined' && !options?.skipPush) {
        const nextPath = type === 'privacy'
          ? '/privacy-policy'
          : type === 'terms'
            ? '/terms-of-service'
            : type === 'marketing'
              ? '/marketing'
              : type === 'eula'
                ? '/eula'
                : '/customer-support';
        window.history.pushState({ legalPage: type }, '', nextPath);
      }
      return {
        type,
        pushedHistory: !alreadyOpen && !options?.skipPush,
      };
    });
    setLegalReturnTarget(options?.returnTarget ?? null);
  }, []);

  const closeLegalPage = useCallback((skipHistory?: boolean) => {
    setLegalPage((prev) => {
      if (!prev) return null;
      if (!skipHistory && prev.pushedHistory && typeof window !== 'undefined' && window.history.state?.legalPage) {
        window.history.back();
      } else if (typeof window !== 'undefined' && (window.location.pathname === '/privacy-policy' || window.location.pathname === '/terms-of-service' || window.location.pathname === '/customer-support' || window.location.pathname === '/eula')) {
        window.history.replaceState({}, '', '/');
      }
      return null;
    });
    setLegalReturnTarget((prev) => prev);
  }, []);

  const openDeleteAccountPage = useCallback(() => {
    if (typeof window !== 'undefined' && window.location.pathname !== '/delete-account') {
      window.history.pushState({ deleteAccount: true }, '', '/delete-account');
      deleteAccountPagePushedHistoryRef.current = true;
    }
    setIsDeleteAccountPageOpen(true);
  }, []);

  const closeDeleteAccountPage = useCallback((skipHistory?: boolean) => {
    setIsDeleteAccountPageOpen(false);
    if (typeof window === 'undefined') return;
    if (!skipHistory && deleteAccountPagePushedHistoryRef.current && window.history.state?.deleteAccount) {
      window.history.back();
    } else if (window.location.pathname === '/delete-account') {
      window.history.replaceState({}, '', '/');
    }
    deleteAccountPagePushedHistoryRef.current = false;
  }, []);

  const openNewsViewer = useCallback((newsStories: UserStory[], startIndex: number, options?: { skipPush?: boolean }) => {
    const sourceStories = newsStories || [];
    const validStories = sourceStories.filter(s => (s?.items || []).length > 0);

    let storiesToUse = validStories.length
      ? validStories
      : sourceStories.map((s, idx) => ({
          ...s,
          items: [{
            id: `placeholder_${s.user?.id || idx}`,
            type: 'image',
            url: s.user?.avatarUrl || '/logo.png',
            duration: 5,
            timestamp: Date.now()
          }]
        })).filter(s => s.user); // ensure user exists

    if (!storiesToUse.length && currentUser) {
      storiesToUse = [{
        user: currentUser,
        items: [{
          id: `placeholder_${currentUser.id}`,
          type: 'image',
          url: currentUser.avatarUrl || '/logo.png',
          duration: 5,
          timestamp: Date.now()
        }]
      }];
    }

    if (!storiesToUse.length) return;

    const safeIndex = startIndex >= 0 && startIndex < storiesToUse.length ? startIndex : 0;
    setNewsViewer(prev => {
      const alreadyOpen = !!prev;
      const scrollEl = document.getElementById('app-scroll');
      const currentScroll = scrollEl ? scrollEl.scrollTop : (typeof window !== 'undefined' ? window.scrollY : 0);
      if (!alreadyOpen && typeof window !== 'undefined' && !options?.skipPush) {
        const backTo = window.location.pathname + window.location.search + window.location.hash;
        // Push a history entry (so the hardware/browser back button can close
        // the viewer) without changing the visible URL - it previously
        // rewrote the path to '/news', which stuck in the address bar.
        window.history.pushState({ news: true, backTo }, '', backTo);
      }
      return {
        stories: storiesToUse,
        startIndex: safeIndex,
        scrollY: currentScroll,
        pushedHistory: !alreadyOpen && !options?.skipPush,
      };
    });
  }, []);

  // Set after the owner deletes their news: the viewer closes onto the top of the home page instead of the old scroll spot.
  const newsDeletedGoHomeRef = useRef(false);
  const closeNewsViewer = useCallback((skipHistory?: boolean) => {
    setNewsViewer(prev => {
      const savedScroll = newsDeletedGoHomeRef.current ? 0 : prev?.scrollY;
      newsDeletedGoHomeRef.current = false;
      if (typeof window !== 'undefined' && savedScroll !== undefined) {
        // Use requestAnimationFrame to ensure the layout has re-mounted before restoring scroll
        requestAnimationFrame(() => {
          const scrollEl = document.getElementById('app-scroll');
          if (scrollEl) {
            scrollEl.scrollTop = savedScroll;
          } else {
            window.scrollTo({ top: savedScroll, behavior: 'auto' });
          }
        });
      }
      if (!skipHistory && prev?.pushedHistory) {
        if (window.history.state?.news) {
          window.history.back();
        } else if (window.history.state?.backTo) {
          window.history.replaceState({}, '', window.history.state.backTo as string);
        }
      }
      // Stay on whichever tab the viewer was opened from (e.g. the profile it was opened on).
      return null;
    });
  }, []);

  useEffect(() => {
    if (!newsViewer) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeNewsViewer();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [newsViewer, closeNewsViewer]);

  useEffect(() => {
    if (!postCommentsPage) return;
    const handlePop = () => {
      if (!window.history.state?.postComments) {
        closePostComments(true);
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [postCommentsPage, closePostComments]);

  useEffect(() => {
    if (!zipCommentsPage) return;
    const handlePop = () => {
      if (!window.history.state?.zipComments) {
        closeZipComments(true);
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [zipCommentsPage, closeZipComments]);

  useEffect(() => {
    if (!sharePostPage) return;
    const handlePop = () => {
      if (!window.history.state?.sharePost) {
        closeSharePostPage(true);
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [sharePostPage, closeSharePostPage]);

  useEffect(() => {
    if (!shareZipPage) return;
    const handlePop = () => {
      if (!window.history.state?.shareZip) {
        closeShareZipPage(true);
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [shareZipPage, closeShareZipPage]);

  useEffect(() => {
    if (!shareSpotPage) return;
    const handlePop = () => {
      if (!window.history.state?.shareSpot) {
        closeShareSpotPage(true);
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [shareSpotPage, closeShareSpotPage]);

  useEffect(() => {
    if (!shareJamSeshPage) return;
    const handlePop = () => {
      if (!window.history.state?.shareJamSesh) {
        closeShareJamSeshPage(true);
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [shareJamSeshPage, closeShareJamSeshPage]);

  useEffect(() => {
    if (!shareEventPage) return;
    const handlePop = () => {
      if (!window.history.state?.shareEvent) {
        closeShareEventPage(true);
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [shareEventPage, closeShareEventPage]);

  useEffect(() => {
    if (!shareLocationPageOpen) return;
    const handlePop = () => {
      if (!window.history.state?.shareLocation) {
        closeShareLocationPage(true);
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [shareLocationPageOpen, closeShareLocationPage]);

  useEffect(() => {
    if (!legalPage) return;
    const handlePop = () => {
      if (!window.history.state?.legalPage) {
        closeLegalPage(true);
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [legalPage, closeLegalPage]);

  useEffect(() => {
    if (!isDeleteAccountPageOpen) return;
    const handlePop = () => {
      if (!window.history.state?.deleteAccount) {
        closeDeleteAccountPage(true);
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [isDeleteAccountPageOpen, closeDeleteAccountPage]);

  const openUploadPage = useCallback(() => {
    if (typeof window !== 'undefined') {
      const scrollEl = document.getElementById('app-scroll');
      uploadPageScrollRef.current = scrollEl ? scrollEl.scrollTop : window.scrollY;
      window.scrollTo({ top: 0, behavior: 'auto' });
      if (window.location.hash !== '#upload-post') {
        window.history.pushState({ uploadPost: true }, '', '#upload-post');
      }
    }
    setIsUploadPageOpen(true);
  }, []);

  const openUploadZipPage = useCallback(() => {
    if (typeof window !== 'undefined') {
      const scrollEl = document.getElementById('app-scroll');
      uploadZipPageScrollRef.current = scrollEl ? scrollEl.scrollTop : window.scrollY;
      window.scrollTo({ top: 0, behavior: 'auto' });
      if (window.location.hash !== '#upload-zip') {
        window.history.pushState({ uploadZip: true }, '', '#upload-zip');
      }
    }
    setIsUploadZipPageOpen(true);
  }, []);

  const openUploadNewsPage = useCallback(() => {
    setNewsInitialFile(null);
    if (typeof window !== 'undefined') {
      const scrollEl = document.getElementById('app-scroll');
      uploadNewsPageScrollRef.current = scrollEl ? scrollEl.scrollTop : window.scrollY;
      window.scrollTo({ top: 0, behavior: 'auto' });
      if (window.location.hash !== '#upload-news') {
        window.history.pushState({ uploadNews: true }, '', '#upload-news');
      }
    }
    setIsUploadNewsPageOpen(true);
  }, []);

  const closeUploadPage = useCallback(() => {
    setIsUploadPageOpen(false);
    if (typeof window !== 'undefined') {
      if (window.location.hash === '#upload-post') {
        window.history.replaceState({}, '', window.location.pathname + window.location.search);
      }
      requestAnimationFrame(() => {
        const scrollEl = document.getElementById('app-scroll');
        if (scrollEl) {
          scrollEl.scrollTop = uploadPageScrollRef.current || 0;
        } else {
          window.scrollTo({ top: uploadPageScrollRef.current || 0, behavior: 'auto' });
        }
      });
    }
  }, []);

  const closeUploadZipPage = useCallback(() => {
    setIsUploadZipPageOpen(false);
    if (typeof window !== 'undefined') {
      if (window.location.hash === '#upload-zip') {
        window.history.replaceState({}, '', window.location.pathname + window.location.search);
      }
      requestAnimationFrame(() => {
        const scrollEl = document.getElementById('app-scroll');
        if (scrollEl) {
          scrollEl.scrollTop = uploadZipPageScrollRef.current || 0;
        } else {
          window.scrollTo({ top: uploadZipPageScrollRef.current || 0, behavior: 'auto' });
        }
      });
    }
  }, []);

  const closeUploadNewsPage = useCallback(() => {
    setIsUploadNewsPageOpen(false);
    if (typeof window !== 'undefined') {
      if (window.location.hash === '#upload-news') {
        window.history.replaceState({}, '', window.location.pathname + window.location.search);
      }
      requestAnimationFrame(() => {
        const scrollEl = document.getElementById('app-scroll');
        if (scrollEl) {
          scrollEl.scrollTop = uploadNewsPageScrollRef.current || 0;
        } else {
          window.scrollTo({ top: uploadNewsPageScrollRef.current || 0, behavior: 'auto' });
        }
      });
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleHash = () => {
      if (window.location.hash === '#upload-post') {
        openUploadPage();
      } else if (window.location.hash === '#upload-news') {
        openUploadNewsPage();
      } else if (window.location.hash === '#upload-zip') {
        openUploadZipPage();
      } else if (isUploadPageOpen) {
        closeUploadPage();
      } else if (isUploadNewsPageOpen) {
        closeUploadNewsPage();
      } else if (isUploadZipPageOpen) {
        closeUploadZipPage();
      }
    };
    handleHash();
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, [openUploadPage, closeUploadPage, isUploadPageOpen, openUploadNewsPage, closeUploadNewsPage, isUploadNewsPageOpen, openUploadZipPage, closeUploadZipPage, isUploadZipPageOpen]);

  // Close news viewer when browser back is used
  useEffect(() => {
    if (!newsViewer) return;
    const handlePop = () => {
      if (!window.history.state?.news) {
        closeNewsViewer(true);
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [newsViewer, closeNewsViewer]);

  // Lock body scroll when news page is open
  useEffect(() => {
    if (!newsViewer) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [newsViewer]);

  // Clear stale #news hash if present (but do not auto-open)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!newsViewer && window.location.hash === '#news') {
      const base = window.location.pathname + window.location.search;
      window.history.replaceState({}, '', base);
    }
  }, [newsViewer]);

  // When a post is deleted (by its owner on another device, a moderator, or an account deletion) while it is open -
  // in the post viewer, its comments sheet, or the share page - close it automatically instead of leaving a ghost.
  const postsEverLoadedRef = useRef(false);
  useEffect(() => {
    if (posts.length > 0) postsEverLoadedRef.current = true;
    if (!postsEverLoadedRef.current) return;
    const exists = (id: string) => posts.some((p) => p.id === id);
    if (postViewer) {
      const remaining = postViewer.posts.filter((vp) => exists(vp.id));
      if (remaining.length === 0) closePostViewer();
      else if (remaining.length !== postViewer.posts.length) {
        setPostViewer((prev) => (prev ? { ...prev, posts: remaining, startIndex: Math.min(prev.startIndex, remaining.length - 1) } : prev));
      }
    }
    if (postCommentsPage && !exists(postCommentsPage.postId)) closePostComments(true);
    if (sharePostPage && !exists(sharePostPage.postId)) closeSharePostPage(true);
  }, [posts, postViewer, postCommentsPage, sharePostPage, closePostViewer, closePostComments, closeSharePostPage]);

  useEffect(() => { setIsNotificationsOpen(false); }, [activeNav.id]);

  // Prefetch lazy chunks on idle so nav switches feel instant
  useEffect(() => {
    const timer = setTimeout(() => {
      // Prefetch all sections after initial render
      import('./sections/Feed');
      import('./sections/Notifications');
      import('./sections/Zips');
      import('./sections/Messenger');
      import('./sections/Discover');
      import('./sections/Profile');
      import('./sections/SpotMap');
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const findNavIndex = useCallback((id: string) => {
    const allNavs = [...NAV_ITEMS, NOTIFICATIONS_NAV];
    return allNavs.findIndex(n => n.id === id);
  }, []);

  const handleNavChange = useCallback((item: NavItem) => {
    const currentIndex = findNavIndex(activeNav.id);
    const nextIndex = findNavIndex(item.id);
    if (nextIndex === -1) return;

    // Allow re-navigation to same page to refresh/reset state
    // Only skip if already on notifications and trying to go to notifications again
    if (nextIndex === currentIndex && item.id === 'notifications' && activeNav.id === 'notifications') {
      return;
    }

    // Navigate immediately — startTransition defers too aggressively on mobile,
    // causing the page to appear frozen while React waits for a quiet moment.
    setActiveNavState(item);
    if (item.id === 'marketplace' && activeNav.id === 'marketplace') setMarketplaceResetSignal((n) => n + 1);
    // Leaving Messages, or re-tapping its tab, returns to the main inbox.
    if (item.id !== 'messenger' || activeNav.id === 'messenger') setShopChatsOpen(false);

    // Defer non-visual cleanup so it doesn't block the page switch.
    startTransition(() => {
      if (item.id !== 'messenger') {
        setSelectedThreadId(null);
        setFallbackThread(null);
      }
      if (item.id !== 'profile' || activeNav.id === 'profile') {
        setViewedUser(null);
        setProfileOverlay(null);
      }
      if (item.id !== 'zips') {
        setFocusedZipId(null);
      }
      // The "Back to post" shortcut only lives while the user is on the map.
      if (item.id !== 'spot-map') {
        setReturnTo(null);
      }
      if (item.id === 'feed') {
        setUnseenNotifications(0);
      }
    });
  }, [activeNav.id, findNavIndex]);

  const messengerNav = useMemo(
    () => NAV_ITEMS.find(n => n.id === 'messenger') || NAV_ITEMS[3],
    []
  );
  const zipsNav = useMemo(
    () => NAV_ITEMS.find(n => n.id === 'zips') || NAV_ITEMS[1],
    []
  );
  const profileNav = useMemo(
    () => NAV_ITEMS.find(n => n.id === 'profile') || NAV_ITEMS[4],
    []
  );
  const spotMapNav = useMemo(
    () => NAV_ITEMS.find(n => n.id === 'spot-map') || NAV_ITEMS[3],
    []
  );

  const openZip = useCallback((zipId: string) => {
    setFocusedZipId(zipId);
    handleNavChange(zipsNav);
  }, [handleNavChange, zipsNav]);

  // Newest zips first so a fresh upload lands at the front of the row.
  const feedZips = useMemo(
    () => [...zips].sort((a, b) => (new Date(b.timestamp).getTime() || 0) - (new Date(a.timestamp).getTime() || 0)).slice(0, 10),
    [zips]
  );

  const openThread = useCallback((threadId: string) => {
    if (!threadId) return;
    const existing = messageThreads.find(t => t.id === threadId);
    if (existing) {
      setFallbackThread(existing);
    } else {
      setFallbackThread(prev => (prev?.id === threadId ? prev : null));
    }
    setSelectedThreadId(threadId);
    handleNavChange(messengerNav);
  }, [messageThreads, handleNavChange, messengerNav]);

  const handleOpenLocationInMap = useCallback((location: { lat: number; lng: number; username: string }) => {
    setMapFocusLocation(location);
    handleNavChange(spotMapNav);
  }, [handleNavChange, spotMapNav]);

  const handleOpenSpotInMap = useCallback((spotId: string) => {
    if (!spotId) return;
    setFocusedSpotId(spotId);
    handleNavChange(spotMapNav);
  }, [handleNavChange, spotMapNav]);

  // Offers the signed-in user made or received (rules only allow reading your own).
  useEffect(() => {
    if (!currentUser) { setListingOffers([]); return; }
    const merged = new Map<string, ListingOffer>();
    const publish = () => setListingOffers(Array.from(merged.values()));
    const onErr = (error: unknown) => console.warn('Offers listener failed (rules deployed?)', error);
    const asBuyer = onSnapshot(query(collection(db, 'offers'), where('buyerId', '==', currentUser.id)), (snap) => {
      snap.docChanges().forEach((c) => c.type === 'removed' ? merged.delete(c.doc.id) : merged.set(c.doc.id, { id: c.doc.id, ...c.doc.data() } as ListingOffer));
      publish();
    }, onErr);
    const asSeller = onSnapshot(query(collection(db, 'offers'), where('sellerId', '==', currentUser.id)), (snap) => {
      snap.docChanges().forEach((c) => c.type === 'removed' ? merged.delete(c.doc.id) : merged.set(c.doc.id, { id: c.doc.id, ...c.doc.data() } as ListingOffer));
      publish();
    }, onErr);
    return () => { asBuyer(); asSeller(); };
  }, [currentUser?.id]);

  // Buyers who opened a Shop chat about each of the current user's listings - the "Sold to" page lists them.
  const listingContacts = useMemo(() => {
    const map: Record<string, User[]> = {};
    if (!currentUser) return map;
    messageThreads.forEach((thread) => {
      if (!thread.listingId || thread.isGroup) return;
      const listing = listings.find((l) => l.id === thread.listingId);
      if (!listing || listing.seller.id !== currentUser.id) return;
      thread.participantIds?.forEach((id) => {
        if (id === currentUser.id) return;
        const user = users.find((u) => u.id === id) || thread.participants?.find((p) => p.id === id);
        if (!user) return;
        const list = (map[thread.listingId as string] ||= []);
        if (!list.some((u) => u.id === id)) list.push(user as User);
      });
    });
    return map;
  }, [messageThreads, listings, users, currentUser?.id]);

  const marketplaceNav = useMemo(
    () => NAV_ITEMS.find(n => n.id === 'marketplace') || NAV_ITEMS[3],
    []
  );

  const handleOpenListing = useCallback((listingId: string) => {
    if (!listingId) return;
    setFocusedListingId(listingId);
    handleNavChange(marketplaceNav);
  }, [handleNavChange, marketplaceNav]);

  // Shared links (ridaz.app/marketplace?listing=ID) open that listing once the app has loaded.
  const pendingListingLinkRef = useRef<string | null>(
    typeof window !== 'undefined' && window.location.pathname === '/marketplace'
      ? new URLSearchParams(window.location.search).get('listing')
      : null
  );
  useEffect(() => {
    const pending = pendingListingLinkRef.current;
    if (!pending) return;
    if (!listings.some((l) => l.id === pending)) return;
    pendingListingLinkRef.current = null;
    handleOpenListing(pending);
  }, [listings, handleOpenListing]);

  const handleOpenJamSeshInMap = useCallback((jamSeshId: string) => {
    if (!jamSeshId) return;
    setFocusedJamSeshId(jamSeshId);
    handleNavChange(spotMapNav);
  }, [handleNavChange, spotMapNav]);

  const handleOpenEventInMap = useCallback((eventId: string) => {
    if (!eventId) return;
    setFocusedEventId(eventId);
    handleNavChange(spotMapNav);
  }, [handleNavChange, spotMapNav]);

  const handleCloseUploadPost = useCallback(() => {
    setPostDraft(null);
    closeUploadPage();
  }, [closeUploadPage]);

  const handleCloseUploadZip = useCallback(() => {
    setZipDraft(null);
    closeUploadZipPage();
  }, [closeUploadZipPage]);

  // Hands the picker's selection to the matching details flow (post / zip / news).
  const handleCreateNext = useCallback((mode: CreateMode, files: File[]) => {
    const swapHash = (hash: string) => {
      if (typeof window !== 'undefined') window.history.replaceState({}, '', window.location.pathname + window.location.search + hash);
    };
    const withPreview = files.map((file) => ({ file, previewUrl: URL.createObjectURL(file) }));
    if (mode === 'post') {
      setPostDraft({ mediaItems: withPreview, caption: '', tricks: [], sport: SPORTS[0], locationTag: null });
    } else if (mode === 'zip') {
      setZipDraft({ video: withPreview[0], caption: '', tricks: [], sport: SPORTS[0], locationTag: null });
      swapHash('#upload-zip');
      setIsUploadPageOpen(false);
      setIsUploadZipPageOpen(true);
    } else {
      setNewsInitialFile(files[0]);
      swapHash('#upload-news');
      setIsUploadPageOpen(false);
      setIsUploadNewsPageOpen(true);
    }
  }, []);

  const handleRequestPostLocationPick = useCallback((draft: UploadPostDraft) => {
    setPostDraft(draft);
    setIsPickingLocationFor('post');
    closeUploadPage();
    handleNavChange(spotMapNav);
  }, [closeUploadPage, handleNavChange, spotMapNav]);

  const handleRequestZipLocationPick = useCallback((draft: UploadZipDraft) => {
    setZipDraft(draft);
    setIsPickingLocationFor('zip');
    closeUploadZipPage();
    handleNavChange(spotMapNav);
  }, [closeUploadZipPage, handleNavChange, spotMapNav]);

  const handleLocationPicked = useCallback((spot: Spot) => {
    const spotImageUrl = spot.imageUrl || spot.photos?.[0]?.url;
    const locationTag: LocationTag = {
      spotId: spot.id,
      name: spot.name,
      lat: spot.location.lat,
      lng: spot.location.lng,
      ...(spotImageUrl ? { imageUrl: spotImageUrl } : {}),
    };
    if (isPickingLocationFor === 'post') {
      setPostDraft(prev => (prev ? { ...prev, locationTag } : prev));
      setIsPickingLocationFor(null);
      openUploadPage();
    } else if (isPickingLocationFor === 'zip') {
      setZipDraft(prev => (prev ? { ...prev, locationTag } : prev));
      setIsPickingLocationFor(null);
      openUploadZipPage();
    }
  }, [isPickingLocationFor, openUploadPage, openUploadZipPage]);

  const handleCancelLocationPick = useCallback(() => {
    if (isPickingLocationFor === 'post') {
      setIsPickingLocationFor(null);
      openUploadPage();
    } else if (isPickingLocationFor === 'zip') {
      setIsPickingLocationFor(null);
      openUploadZipPage();
    }
  }, [isPickingLocationFor, openUploadPage, openUploadZipPage]);

  const handleOpenPostLocation = useCallback((post: Post) => {
    if (!post.locationTag?.spotId) return;
    const scrollEl = document.getElementById('app-scroll');
    setReturnTo({
      type: 'post',
      postId: post.id,
      fromNav: postViewer?.sourceNav ?? activeNav,
      scrollY: postViewer ? postViewer.scrollY : (scrollEl ? scrollEl.scrollTop : 0),
      viewer: postViewer ? { posts: postViewer.posts, startIndex: postViewer.startIndex } : null,
    });
    setPostViewer(null);
    setFocusedSpotId(post.locationTag.spotId);
    handleNavChange(spotMapNav);
  }, [handleNavChange, spotMapNav, postViewer, activeNav]);

  const handleOpenZipLocation = useCallback((zip: Zip) => {
    if (!zip.locationTag?.spotId) return;
    setReturnTo({ type: 'zip', zipId: zip.id });
    setFocusedSpotId(zip.locationTag.spotId);
    handleNavChange(spotMapNav);
  }, [handleNavChange, spotMapNav]);

  const handleReturnFromMap = useCallback(() => {
    if (!returnTo) return;
    const target = returnTo;
    if (target.type === 'post') {
      // Go back to exactly where the user was (home feed, Discover, a profile...) instead of opening the post on a
      // separate page: restore the tab, then either re-open the viewer they were in or scroll back to the post.
      handleNavChange(target.fromNav);
      window.setTimeout(() => {
        if (target.viewer) {
          setPostViewer({ posts: target.viewer.posts, startIndex: target.viewer.startIndex, sourceNav: target.fromNav, scrollY: target.scrollY });
        }
        const restore = () => {
          const scrollEl = document.getElementById('app-scroll');
          if (scrollEl) scrollEl.scrollTop = target.scrollY;
        };
        restore();
        window.setTimeout(() => {
          restore();
          if (!target.viewer) {
            const el = document.querySelector(`[data-post-id="${target.postId}"]`);
            if (el) {
              const rect = el.getBoundingClientRect();
              if (rect.bottom < 0 || rect.top > window.innerHeight) el.scrollIntoView({ block: 'center' });
            }
          }
        }, 350);
      }, 60);
    } else if (target.type === 'zip') {
      openZip(target.zipId);
    }
    setReturnTo(null);
  }, [returnTo, handleNavChange, openZip]);

  const getPinRateLimitStatus = useCallback((): { allowed: boolean; unblockAtMs: number | null } => {
    if (!currentUser) return { allowed: true, unblockAtMs: null };
    const toMillis = (value: any): number => {
      if (!value) return 0;
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const parsed = new Date(value).getTime();
        return Number.isFinite(parsed) ? parsed : 0;
      }
      if (typeof value?.toMillis === 'function') return value.toMillis();
      if (typeof value?.seconds === 'number') return value.seconds * 1000;
      if (value instanceof Date) return value.getTime();
      return 0;
    };
    const now = Date.now();
    const windowMs = 24 * 60 * 60 * 1000;
    const timestamps = [
      ...spots.filter(s => s.addedBy?.id === currentUser.id).map(s => toMillis((s as any).createdAt)),
      ...jamSeshes.filter(j => j.createdBy?.id === currentUser.id).map(j => toMillis((j as any).createdAt)),
      ...events.filter(e => e.createdBy?.id === currentUser.id).map(e => toMillis((e as any).createdAt)),
    ].filter(ts => ts > 0 && now - ts < windowMs).sort((a, b) => a - b);
    if (timestamps.length < 5) return { allowed: true, unblockAtMs: null };
    return { allowed: false, unblockAtMs: timestamps[0] + windowMs };
  }, [currentUser, spots, jamSeshes, events]);

  // Client-side speed bump against spam/cost-abuse uploads (storage +
  // bandwidth + the video-transcode Cloud Function all cost real money per
  // upload) - not a real security boundary on its own (a direct API call
  // could bypass it), but it stops accidental or casual mass-uploading and
  // gives immediate feedback. Mirrors getPinRateLimitStatus's approach:
  // count the user's own posts/zips created in the last 24h using data
  // that's already loaded locally, no extra reads.
  const getUploadRateLimitStatus = useCallback((): { allowed: boolean; unblockAtMs: number | null } => {
    if (!currentUser) return { allowed: true, unblockAtMs: null };
    const toMillis = (value: any): number => {
      if (!value) return 0;
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const parsed = new Date(value).getTime();
        return Number.isFinite(parsed) ? parsed : 0;
      }
      if (typeof value?.toMillis === 'function') return value.toMillis();
      if (typeof value?.seconds === 'number') return value.seconds * 1000;
      if (value instanceof Date) return value.getTime();
      return 0;
    };
    const now = Date.now();
    const windowMs = 24 * 60 * 60 * 1000;
    const UPLOAD_LIMIT_PER_WINDOW = 15;
    const timestamps = [
      ...posts.filter((p: Post) => p.user?.id === currentUser.id).map((p: Post) => toMillis(p.timestamp)),
      ...zips.filter((z: Zip) => z.user?.id === currentUser.id).map((z: Zip) => toMillis(z.timestamp)),
    ].filter(ts => ts > 0 && now - ts < windowMs).sort((a, b) => a - b);
    if (timestamps.length < UPLOAD_LIMIT_PER_WINDOW) return { allowed: true, unblockAtMs: null };
    return { allowed: false, unblockAtMs: timestamps[0] + windowMs };
  }, [currentUser, posts, zips]);

  // --- DATA FETCHING & REALTIME LISTENERS ---
  const fetchAllData = useCallback(async () => {
      setIsLoading(true);

      // Initial one-shot fetch using Firestore REST API — works reliably in WKWebView (Capacitor iOS)
      // onSnapshot (streaming) can silently fail in native WebViews; getDocs never does.
      console.log('[RIDAZ] fetchAllData: starting getDocs initial fetch');
      try {
        const [usersSnap, postsSnap, zipsSnap, spotsSnap, jamSeshesSnap, eventsSnap, storiesSnap] = await Promise.all([
          getDocs(collection(db, 'users')),
          getDocs(query(collection(db, 'posts'), orderBy('timestamp', 'desc'))),
          getDocs(query(collection(db, 'zips'), orderBy('timestamp', 'desc'))),
          getDocs(collection(db, 'spots')),
          getDocs(collection(db, 'jam_seshes')),
          getDocs(collection(db, 'events')),
          getDocs(query(collection(db, 'stories'), where('expiresAt', '>', Timestamp.now()))),
        ]);

        console.log(`[RIDAZ] getDocs done — users:${usersSnap.size} posts:${postsSnap.size} zips:${zipsSnap.size} spots:${spotsSnap.size} jams:${jamSeshesSnap.size}`);

        const freshUsers = usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));
        setUsers(freshUsers);

        const freshPosts = postsSnap.docs.map(doc => {
          const data = doc.data();
          if (data.deleted) return null;
          return { id: doc.id, ...data, sport: data.sport ? (SPORTS.find(sp => sp.id === data.sport?.id) || data.sport) : data.sport, timestamp: (data.timestamp as Timestamp)?.toDate().toISOString() || new Date().toISOString(), likedBy: data.likedBy || [], savedBy: data.savedBy || [], tags: data.tags || [], mentionUsernames: data.mentionUsernames || [] } as Post;
        }).filter((p): p is Post => Boolean(p))
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        setPosts(freshPosts);

        const freshZips = zipsSnap.docs.map(docSnapshot => {
          const data = docSnapshot.data();
          if (data.deleted || !data.videoUrl) return null;
          return { id: docSnapshot.id, ...data, sport: SPORTS.find(sport => sport.id === data.sport?.id) || data.sport, likedBy: data.likedBy || [], durationSeconds: Number(data.durationSeconds) || 0, timestamp: (data.timestamp as Timestamp)?.toDate().toISOString() || new Date().toISOString() } as Zip;
        }).filter((z): z is Zip => Boolean(z))
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        setZips(freshZips);

        const freshSpots = spotsSnap.docs.map(docSnapshot => {
          const data = docSnapshot.data();
          return { id: docSnapshot.id, ...data, sport: SPORTS.find(sport => sport.id === data.sport?.id) || data.sport, mediaLikes: data.mediaLikes || {}, overallRatings: data.overallRatings || {}, difficultyRatings: data.difficultyRatings || {}, securityRatings: data.securityRatings || {} } as Spot;
        });
        setSpots(freshSpots);

        const freshJamSeshes = jamSeshesSnap.docs.map(doc => {
          const data = doc.data();
          return { id: doc.id, ...data, sport: SPORTS.find(sport => sport.id === data.sport?.id) || data.sport } as JamSesh;
        }).sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
        setJamSeshes(freshJamSeshes);

        const freshEvents = eventsSnap.docs.map(doc => {
          const data = doc.data();
          return { id: doc.id, ...data, sport: SPORTS.find(sport => sport.id === data.sport?.id) || data.sport, interestedUserIds: data.interestedUserIds || [] } as Event;
        }).sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
        setEvents(freshEvents);

        const storyResults = await Promise.all(storiesSnap.docs.map(async (doc) => {
          const storyData = doc.data();
          let storyUser: User | null = null;
          if (storyData.user) { storyUser = { id: storyData.user.id, ...storyData.user } as User; }
          if (!storyUser) return null;
          const now = Date.now();
          const freshItems = (storyData.items || []).filter((itm: any) => {
            const t = typeof itm.timestamp === 'number' ? itm.timestamp : new Date(itm.timestamp || 0).getTime();
            return now - t < 24 * 60 * 60 * 1000;
          });
          if (freshItems.length === 0) return null;
          return { user: storyUser, items: freshItems } as UserStory;
        }));
        const freshStories = storyResults.filter((s): s is UserStory => s !== null);
        setStories(freshStories);

        saveCachedBootData({
          users: freshUsers,
          posts: freshPosts,
          zips: freshZips,
          spots: freshSpots,
          jamSeshes: freshJamSeshes,
          events: freshEvents,
          stories: freshStories,
        });
      } catch (e: any) {
        console.error('[RIDAZ] getDocs fetch FAILED:', e?.code, e?.message, e);
      }

      setIsLoading(false);
      console.log('[RIDAZ] fetchAllData: loading complete, setting up onSnapshot listeners');

      // Real-time listeners — update state after initial load; may not connect in all native environments
      const usersUnsub = onSnapshot(collection(db, 'users'), (snapshot) => {
          setUsers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as User)));
      });

      const postsUnsub = onSnapshot(query(collection(db, 'posts'), orderBy('timestamp', 'desc')), (snapshot) => {
          setPosts(snapshot.docs.map(doc => {
            const data = doc.data();
            if (data.deleted) return null;
            return { id: doc.id, ...data, sport: data.sport ? (SPORTS.find(sp => sp.id === data.sport?.id) || data.sport) : data.sport, timestamp: (data.timestamp as Timestamp)?.toDate().toISOString() || new Date().toISOString(), likedBy: data.likedBy || [], savedBy: data.savedBy || [], tags: data.tags || [], mentionUsernames: data.mentionUsernames || [] } as Post;
          }).filter((p): p is Post => Boolean(p))
          // Server-side orderBy can't account for a serverTimestamp() write that
          // hasn't resolved yet (briefly null), which can misplace a just-created
          // post — re-sort client-side against the resolved date as a safety net.
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()));
      });

      const zipsUnsub = onSnapshot(query(collection(db, 'zips'), orderBy('timestamp', 'desc')), (snapshot) => {
          setZips(snapshot.docs.map((docSnapshot) => {
            const data = docSnapshot.data();
            if (data.deleted || !data.videoUrl) return null;
            return { id: docSnapshot.id, ...data, sport: SPORTS.find(sport => sport.id === data.sport?.id) || data.sport, likedBy: data.likedBy || [], durationSeconds: Number(data.durationSeconds) || 0, timestamp: (data.timestamp as Timestamp)?.toDate().toISOString() || new Date().toISOString() } as Zip;
          }).filter((zip): zip is Zip => Boolean(zip))
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()));
      });

      const spotsUnsub = onSnapshot(collection(db, 'spots'), (snapshot) => {
          setSpots(snapshot.docs.map((docSnapshot) => {
            const data = docSnapshot.data();
            return { id: docSnapshot.id, ...data, sport: SPORTS.find(sport => sport.id === data.sport?.id) || data.sport, mediaLikes: data.mediaLikes || {}, overallRatings: data.overallRatings || {}, difficultyRatings: data.difficultyRatings || {}, securityRatings: data.securityRatings || {} } as Spot;
          }));
      });

      const shopsUnsub = onSnapshot(collection(db, 'shops'), (snapshot) => {
          setShops(snapshot.docs.map((docSnapshot) => {
            const data = docSnapshot.data();
            return {
              id: docSnapshot.id,
              ...data,
              sports: Array.isArray(data.sports) ? data.sports.map((s: any) => SPORTS.find(sport => sport.id === s?.id) || s) : [],
              overallRatings: data.overallRatings || {},
              photos: data.photos || [],
            } as Shop;
          }));
      });

      const jamSeshesUnsub = onSnapshot(collection(db, 'jam_seshes'), (snapshot) => {
          setJamSeshes(snapshot.docs.map(doc => {
            const data = doc.data();
            return { id: doc.id, ...data, sport: SPORTS.find(sport => sport.id === data.sport?.id) || data.sport } as JamSesh;
          }).sort((a, b) => (a.startTime || '').localeCompare(b.startTime || '')));
      });

      const listingsUnsub = onSnapshot(collection(db, 'listings'), (snapshot) => {
          setListings(snapshot.docs.map(d => ({ id: d.id, ...d.data(), savedBy: d.data().savedBy || [], photos: d.data().photos || [] } as Listing)));
      }, (error) => console.warn('Listings listener failed (rules deployed?)', error));

      const ratingsUnsub = onSnapshot(collection(db, 'listingReviews'), (snapshot) => {
          setListingReviews(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ListingReview)));
      }, (error) => console.warn('Listing reviews listener failed (rules deployed?)', error));

      const eventsUnsub = onSnapshot(collection(db, 'events'), (snapshot) => {
          setEvents(snapshot.docs.map(doc => {
            const data = doc.data();
            return { id: doc.id, ...data, sport: SPORTS.find(sport => sport.id === data.sport?.id) || data.sport, interestedUserIds: data.interestedUserIds || [] } as Event;
          }).sort((a, b) => (a.startTime || '').localeCompare(b.startTime || '')));
      });

      // Safety-net poll: onSnapshot can silently stall (seen on WKWebView/native),
      // which can leave a just-created post/zip invisible even though the write
      // itself succeeded. Refetch periodically as a backup to the listeners above.
      const pollPostsAndZips = async () => {
        try {
          const [postsSnap2, zipsSnap2] = await Promise.all([
            getDocs(query(collection(db, 'posts'), orderBy('timestamp', 'desc'))),
            getDocs(query(collection(db, 'zips'), orderBy('timestamp', 'desc'))),
          ]);
          setPosts(postsSnap2.docs.map(doc => {
            const data = doc.data();
            if (data.deleted) return null;
            return { id: doc.id, ...data, sport: data.sport ? (SPORTS.find(sp => sp.id === data.sport?.id) || data.sport) : data.sport, timestamp: (data.timestamp as Timestamp)?.toDate().toISOString() || new Date().toISOString(), likedBy: data.likedBy || [], savedBy: data.savedBy || [], tags: data.tags || [], mentionUsernames: data.mentionUsernames || [] } as Post;
          }).filter((p): p is Post => Boolean(p))
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()));
          setZips(zipsSnap2.docs.map((docSnapshot) => {
            const data = docSnapshot.data();
            if (data.deleted || !data.videoUrl) return null;
            return { id: docSnapshot.id, ...data, sport: SPORTS.find(sport => sport.id === data.sport?.id) || data.sport, likedBy: data.likedBy || [], durationSeconds: Number(data.durationSeconds) || 0, timestamp: (data.timestamp as Timestamp)?.toDate().toISOString() || new Date().toISOString() } as Zip;
          }).filter((zip): zip is Zip => Boolean(zip))
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()));
        } catch (err) {
          // Ignore — the realtime listeners remain the primary source.
        }
      };
      const postsZipsPollInterval = window.setInterval(pollPostsAndZips, 8000);

      const storiesUnsub = onSnapshot(
        query(collection(db, 'stories'), where('expiresAt', '>', Timestamp.now())),
        (snapshot) => {
          const userStoryPromises = snapshot.docs.map(async (doc) => {
            const storyData = doc.data();
            let storyUser: User | null = null;
            if (storyData.user) {
              storyUser = { id: storyData.user.id, ...storyData.user } as User;
            } else if (doc.ref.parent && doc.ref.parent.parent) {
              const userDoc = await getDoc(doc.ref.parent.parent);
              if (userDoc.exists()) {
                storyUser = { id: userDoc.id, ...userDoc.data() } as User;
              }
            }
            if (!storyUser) return null;
            const now = Date.now();
            const freshItems = (storyData.items || []).filter((itm: any) => {
              const t = typeof itm.timestamp === 'number' ? itm.timestamp : new Date(itm.timestamp || 0).getTime();
              return now - t < 24 * 60 * 60 * 1000;
            });
            if (freshItems.length !== (storyData.items || []).length) {
              await setDoc(doc.ref, { items: freshItems }, { merge: true });
            }
            if (freshItems.length === 0) {
              await deleteDoc(doc.ref);
              return null;
            }
            return { user: storyUser, items: freshItems } as UserStory;
          });
          Promise.all(userStoryPromises).then((results) => {
            setStories(results.filter((s): s is UserStory => s !== null));
          });
        }
      );

      return () => {
          usersUnsub(); postsUnsub(); zipsUnsub(); spotsUnsub(); shopsUnsub();
          jamSeshesUnsub(); eventsUnsub(); storiesUnsub(); listingsUnsub(); ratingsUnsub();
          window.clearInterval(postsZipsPollInterval);
      };
  }, []);

  // Load all public data immediately on mount — does not wait for auth. A
  // cached copy of the last successful load (if any) is hydrated into state
  // right away too, so whenever the boot screen *does* come down (still
  // gated by the same isLoading/auth timing as always - deliberately not
  // shortcutted here, since doing that previously let this paint ahead of
  // onAuthStateChanged and flashed the signed-out view on refresh), real
  // content is already sitting there instead of another loading state.
  // fetchAllData() runs right after regardless, to revalidate against
  // Firestore and silently swap in fresh data once it lands.
  useEffect(() => {
    const cached = loadCachedBootData();
    if (cached) {
      setUsers(cached.users);
      setPosts(cached.posts);
      setZips(cached.zips);
      setSpots(cached.spots);
      setJamSeshes(cached.jamSeshes);
      setEvents(cached.events);
      setStories(cached.stories);
    }
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    const safetyTimer = window.setTimeout(() => {
      setIsLoading(false);
      setIsAuthResolved(true);
    }, 10000);

    const unsub = onAuthStateChanged(auth, async (user) => {
      clearTimeout(safetyTimer);
      try {
        if (user) {
          setIsGuest(false);
          writeLastUid(user.uid);
          const userDocRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.isBanned) {
              await signOut(auth);
              setCurrentUser(null);
              setAuthPending(false);
              setShowBanned(true);
              return;
            }
            const hydratedSports = userData.sports?.map((s: any) =>
              SPORTS.find(sport => sport.id === s.id) || s
            ) || [];
            setCurrentUser({ id: user.uid, ...userData, sports: hydratedSports } as User);
          } else {
            setCurrentUser(null);
          }
        } else {
          setCurrentUser(null);
          setMessageThreads([]);
          setIsGuest(true);
          // Signed out, but not by pressing Log out (that clears the key): was the account banned? If so say so.
          const lastUid = readLastUid();
          if (lastUid) {
            isUidBanned(lastUid).then((banned) => {
              if (!banned) return;
              clearLastUid();
              setAuthPending(false);
              setShowBanned(true);
            });
          }
        }
      } catch (err: any) {
        console.error('[RIDAZ] Auth state error:', err?.code, err?.message, err);
        setCurrentUser(null);
        setAuthPending(false);
      } finally {
        setIsLoading(false);
        setIsAuthResolved(true);
      }
    });

    return () => {
      clearTimeout(safetyTimer);
      unsub();
    };
  }, []);

  // The app has the account now, so the sign-in loading screen can go.
  useEffect(() => { if (currentUser) setAuthPending(false); }, [currentUser]);

  // A ban reaches a signed-in phone live (the profile listener sees isBanned). A phone that was OFFLINE when the ban happened
  // missed that, so the moment it is back (network returns, app comes to the front, or every few minutes) it asks Firebase for a
  // fresh session: a banned account's login is disabled and its sessions revoked, so that fails; it also reads the public ban flag
  // from the server. Either way the person is signed out and shown the banned page. Plain network errors are ignored (still offline).
  useEffect(() => {
    if (!currentUser?.id) return;
    let running = false;
    let lastFullCheck = 0;
    const handleBanned = () => {
      clearLastUid();
      signOut(auth).catch(() => undefined);
      setCurrentUser(null);
      setAuthPending(false);
      setShowBanned(true);
    };
    const check = async (full: boolean) => {
      const user = auth.currentUser;
      if (!user || running || (typeof navigator !== 'undefined' && navigator.onLine === false)) return;
      running = true;
      try {
        await user.getIdToken(true);
        if (full && Date.now() - lastFullCheck > 30000) {
          lastFullCheck = Date.now();
          if (await isUidBanned(user.uid)) handleBanned();
        }
      } catch (err: any) {
        const code = String(err?.code || '');
        if (code === 'auth/user-disabled') handleBanned();
        else if (code === 'auth/user-token-expired' || code === 'auth/user-not-found') {
          if (await isUidBanned(user.uid)) handleBanned();
        }
      } finally {
        running = false;
      }
    };
    const onBack = () => { void check(true); };
    const onVisible = () => { if (document.visibilityState === 'visible') void check(true); };
    void check(true);
    window.addEventListener('online', onBack);
    window.addEventListener('focus', onBack);
    document.addEventListener('visibilitychange', onVisible);
    const interval = window.setInterval(() => { void check(false); }, 5 * 60 * 1000);
    return () => {
      window.removeEventListener('online', onBack);
      window.removeEventListener('focus', onBack);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(interval);
    };
  }, [currentUser?.id]);

  // Handle presence with heartbeat - update lastActive timestamp
  useEffect(() => {
    if (!currentUser) return;

    const userDocRef = doc(db, "users", currentUser.id);

    // Update lastActive timestamp to current time
    const updatePresence = () => {
      updateDoc(userDocRef, {
        lastActive: Timestamp.now()
      }).catch(err => console.error('Error updating presence:', err));
    };

    // Initial presence update
    updatePresence();

    // Update presence every 30 seconds (heartbeat)
    const heartbeatInterval = setInterval(updatePresence, 30000);

    // Update on visibility change
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        updatePresence();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(heartbeatInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  // currentUser?.id, not currentUser: the onSnapshot sync effect below hands
  // back a brand-new currentUser object on every remote change (including
  // the lastActive write this same effect makes), so depending on the whole
  // object here created a self-triggering loop - write lastActive -> listener
  // fires -> new currentUser reference -> this effect reruns -> writes again.
  }, [currentUser?.id]);

  // Keep currentUser data in sync (seen notifications, tags, etc.)
  useEffect(() => {
    if (!currentUser?.id) return;
    const userDocRef = doc(db, 'users', currentUser.id);
    const unsub = onSnapshot(userDocRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      // A moderator can ban a user mid-session (see handleBanUser) - this
      // listener is what actually ends that session, since the ban write
      // itself doesn't revoke their existing Firebase Auth token.
      if (data.isBanned) {
        signOut(auth);
        setCurrentUser(null);
        setAuthPending(false);
        setShowBanned(true);
        return;
      }
      const hydratedSports = data.sports?.map((s: any) =>
        SPORTS.find(sport => sport.id === s.id) || s
      ) || [];
      setCurrentUser(prev => prev ? ({ id: snap.id, ...data, sports: hydratedSports } as User) : prev);
    });
    return () => unsub();
  }, [currentUser?.id]);

  // Shows the OG popup exactly once, the moment assignOGBadge's isOG write
  // lands on a freshly-created account (via the live listener above) - not
  // on a later login/relaunch, since justSignedUpUid only gets set inside
  // this same session's signup handlers.
  useEffect(() => {
    if (!justSignedUpUid || !currentUser) return;
    if (currentUser.id !== justSignedUpUid) return;
    if (currentUser.isOG) {
      setShowOGPopup(true);
      setJustSignedUpUid(null);
    }
  }, [currentUser, justSignedUpUid]);

  // Give up watching after 20s (assignOGBadge polls for up to ~10s, plus
  // margin) so this doesn't linger forever for accounts that aren't OG.
  useEffect(() => {
    if (!justSignedUpUid) return;
    const timer = window.setTimeout(() => setJustSignedUpUid(null), 20000);
    return () => window.clearTimeout(timer);
  }, [justSignedUpUid]);

  // Push notifications: request permission, register for an APNs token, and
  // save it on the user doc so Cloud Functions can target this device. Web
  // has no APNs, so this only runs inside the native iOS app. Safe to run on
  // every login - requestPermissions() no-ops if already answered, and
  // arrayUnion below won't duplicate a token that's already stored.
  useEffect(() => {
    if (!currentUser?.id || !Capacitor.isNativePlatform()) return;

    let isMounted = true;
    const userId = currentUser.id;

    const registrationListener = PushNotifications.addListener('registration', (token) => {
      if (!isMounted) return;
      updateDoc(doc(db, 'users', userId), { pushTokens: arrayUnion(token.value) }).catch(err =>
        console.error('[Push] Failed to save push token:', err)
      );
    });
    const registrationErrorListener = PushNotifications.addListener('registrationError', (err) => {
      console.error('[Push] Registration error:', err);
    });

    PushNotifications.requestPermissions()
      .then(result => {
        if (result.receive === 'granted') {
          PushNotifications.register();
        }
      })
      .catch(err => console.error('[Push] Permission request failed:', err));

    return () => {
      isMounted = false;
      registrationListener.then(l => l.remove());
      registrationErrorListener.then(l => l.remove());
    };
  }, [currentUser?.id]);

  // Native incoming calls: save this device's VoIP token (iOS) so the server can ring it, and
  // pick up calls the user accepted from the lock screen / call notification.
  useEffect(() => {
    if (!currentUser?.id || !hasNativeCalls()) return;
    let isMounted = true;
    const userId = currentUser.id;
    const handles: Promise<{ remove: () => Promise<void> }>[] = [];

    handles.push(RidazCalls.addListener('voipToken', ({ token, env }) => {
      updateDoc(doc(db, 'users', userId), { voipTokens: arrayUnion({ token, env }) }).catch((err) =>
        console.error('[Calls] Failed to save VoIP token:', err));
    }));
    handles.push(RidazCalls.addListener('callAccepted', (info) => { if (isMounted) setNativeAccept(info); }));

    RidazCalls.register()
      .then(() => { if (isMounted) setNativeRingReady(true); })
      .catch((err) => console.error('[Calls] Native call setup failed:', err));
    RidazCalls.consumePendingAccept()
      .then((res) => { if (isMounted && res.accept) setNativeAccept(res.accept); })
      .catch(() => undefined);

    return () => {
      isMounted = false;
      handles.forEach((h) => h.then((l) => l.remove()).catch(() => undefined));
    };
  }, [currentUser?.id]);

  // Join a call accepted from the native ring UI (works from a cold start: waits for sign-in).
  useEffect(() => {
    if (!nativeAccept || !currentUser) return;
    const accept = nativeAccept;
    setNativeAccept(null);
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'messageThreads', accept.threadId));
        if (!snap.exists()) return;
        const data = snap.data() as any;
        const thread = { id: snap.id, ...data, participantIds: data.participantIds || [], participants: data.participants || [] } as MessageThread;
        setCallSession((prev) => prev || { thread, type: accept.type || data.activeCall?.type || 'audio', joinOnly: true });
      } catch (err) {
        console.error('[Calls] Could not open accepted call', err);
      }
    })();
  }, [nativeAccept, currentUser?.id]);

  // Tapping a push notification - only wired up for the two proximity types
  // sent by onNewJamSesh/onNewEvent (functions/index.js) so far; other
  // existing notification types (like/follow/message) still don't navigate
  // anywhere on tap. Registered once (not tied to currentUser.id) since
  // handling a tap doesn't depend on who's currently logged in.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const actionListener = PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action.notification?.data as { type?: string; jamSeshId?: string; eventId?: string; listingId?: string } | undefined;
      if (data?.type === 'call' && (data as any).threadId) {
        openThread((data as any).threadId);
      } else if ((data?.type === 'listing' || data?.type === 'offer' || data?.type === 'listingReminder') && data.listingId) {
        handleOpenListing(data.listingId);
      } else if (data?.type === 'jamSesh' && data.jamSeshId) {
        handleOpenJamSeshInMap(data.jamSeshId);
      } else if (data?.type === 'event' && data.eventId) {
        handleOpenEventInMap(data.eventId);
      }
    });

    return () => {
      actionListener.then(l => l.remove());
    };
  }, [handleOpenJamSeshInMap, handleOpenEventInMap, handleOpenListing, openThread]);

  // fetchAllData is now always called from onAuthStateChanged — no separate guest trigger needed

  // Live comment counts for notifications
  const [commentCounts, setCommentCounts] = useState<Map<string, { total: number; latest: number; newSinceSeen: number }>>(new Map());
  const [commentMentions, setCommentMentions] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!currentUser) {
      setCommentCounts(new Map());
      setCommentMentions(new Set());
      return;
    }

    const userPosts = posts.filter(p => p.user.id === currentUser.id);
    const unsubscribers: Array<() => void> = [];

    userPosts.forEach(post => {
      const commentsRef = collection(db, 'posts', post.id, 'comments');
      const unsubscribe = onSnapshot(commentsRef, (snapshot) => {
        let count = 0; // comments bubble counts only non-mentions
        let latest = 0;
        let newSinceSeen = 0;
        let mentionFound = false;
        const lastSeen = currentUser?.lastSeenCommentsAt || 0;
        snapshot.docs.forEach(docSnap => {
          const data = docSnap.data();
          let ts = 0;
          const rawTs = data.timestamp;
          if (rawTs?.toDate) {
            ts = rawTs.toDate().getTime();
          } else if (typeof rawTs === 'string' || typeof rawTs === 'number') {
            ts = new Date(rawTs).getTime();
          }
          if (!ts || Number.isNaN(ts)) ts = Date.now();
          if (ts > latest) latest = ts;
          const text = (data.text || '').toLowerCase();
          const usernameNeedle = currentUser?.username ? `@${currentUser.username.toLowerCase()}` : '';
          const isMention = usernameNeedle && text.includes(usernameNeedle);
          if (isMention) {
            mentionFound = true;
            return; // skip mention comments from the comments bubble
          }
          count += 1;
          if (ts > lastSeen) newSinceSeen += 1;
        });
        setCommentCounts(prev => {
          const next = new Map(prev);
          if (count > 0) {
            next.set(post.id, { total: count, latest, newSinceSeen });
          } else {
            next.delete(post.id);
          }
          return next;
        });
        setCommentMentions(prev => {
          const next = new Set(prev);
          if (mentionFound) {
            next.add(post.id);
          } else {
            next.delete(post.id);
          }
          return next;
        });
      }, (error) => {
        console.error(`Error listening to comments for post ${post.id}:`, error);
      });
      unsubscribers.push(unsubscribe);
    });

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [posts, currentUser?.id, currentUser?.lastSeenCommentsAt]);

  // Update viewedUser and currentUser when users array changes (for real-time follow updates)
  useEffect(() => {
    if (viewedUser) {
      const updatedUser = users.find(u => u.id === viewedUser.id);
      if (updatedUser) {
        setViewedUser(updatedUser);
      }
    }
    if (currentUser) {
      const updatedCurrentUser = users.find(u => u.id === currentUser.id);
      if (updatedCurrentUser) {
        setCurrentUser(updatedCurrentUser);
      }
    }
  }, [users]);

  // Mark thread as read when opened - also re-runs whenever the open
  // thread's timestampMs changes (a new message arriving) so lastReadAt
  // keeps advancing while the recipient already has the thread open,
  // instead of freezing at whatever moment they first opened it. Read via
  // a fresh lookup here (not the selectedThread memo, which is declared
  // later in this component) so this effect doesn't depend on hook
  // declaration order.
  const openThreadTimestampMs = messageThreads.find(t => t.id === selectedThreadId)?.timestampMs;
  useEffect(() => {
    const markAsRead = async () => {
      if (selectedThreadId && currentUser) {
        try {
          await updateDoc(doc(db, 'messageThreads', selectedThreadId), {
            [`unreadCounts.${currentUser.id}`]: 0,
            [`lastReadAt.${currentUser.id}`]: Date.now()
          });
        } catch (error) {
          console.error('Error marking thread as read:', error);
        }
      }
    };
    markAsRead();
  }, [selectedThreadId, currentUser?.id, openThreadTimestampMs]);
  
  useEffect(() => {
    if (!currentUser) {
      return;
    }
    let unsub: (() => void) | null = null;
    let cancelled = false;

    const listenWithQuery = () => {
      const threadsQuery = query(collection(db, 'messageThreads'), where('participantIds', 'array-contains', currentUser.id));
      unsub = onSnapshot(threadsQuery, async (snapshot) => {
          const threads = await Promise.all(snapshot.docs.map(async (docSnapshot) => {
            const data = docSnapshot.data();

            const ids: string[] = Array.isArray(data.participantIds)
              ? data.participantIds
              : Array.isArray(data.participants)
                ? (data.participants as any[]).map(p => p?.id || p?.userId || p?.uid).filter(Boolean)
                : [];

            if (!ids.includes(currentUser.id)) {
              return null;
            }

            if (!data.unreadCounts) {
              const initialUnreadCounts: { [key: string]: number } = {};
              ids.forEach((pid: string) => {
                initialUnreadCounts[pid] = 0;
              });
              await updateDoc(doc(db, 'messageThreads', docSnapshot.id), {
                unreadCounts: initialUnreadCounts
              }).catch(err => console.error('Error migrating thread unreadCounts', err));
              data.unreadCounts = initialUnreadCounts;
            }

            const unreadCounts = data.unreadCounts || {};
            const typingUsers = data.typingUsers || {};
            const lastReadAt = data.lastReadAt || {};
            const timestampDate = (data.timestamp as Timestamp)?.toDate() || new Date();

            const thread = {
               id: docSnapshot.id,
               ...data,
               chatType: getThreadType(data as Partial<MessageThread>),
               participantIds: ids,
               unreadCounts,
               typingUsers,
               lastReadAt,
               timestamp: timestampDate.toLocaleString(),
               timestampMs: timestampDate.getTime()
            } as MessageThread;

            return thread;
          }));

          const filtered = threads.filter((t): t is MessageThread => t !== null);
          setMessageThreads(filtered);
      }, (err) => {
        console.error('Threads listener error, falling back to full collection', err);
        // Fallback to broad listener if indexed query fails
        if (unsub) unsub();
        listenBroad();
      });
    };

    const listenBroad = () => {
      const broad = collection(db, 'messageThreads');
      unsub = onSnapshot(broad, async (snapshot) => {
        const threads = await Promise.all(snapshot.docs.map(async (docSnapshot) => {
          const data = docSnapshot.data();
          const ids: string[] = Array.isArray(data.participantIds)
            ? data.participantIds
            : Array.isArray(data.participants)
              ? (data.participants as any[]).map(p => p?.id || p?.userId || p?.uid).filter(Boolean)
              : [];

          if (!ids.includes(currentUser.id)) return null;

          const unreadCounts = data.unreadCounts || {};
          const typingUsers = data.typingUsers || {};
          const lastReadAt = data.lastReadAt || {};
          const timestampDate = (data.timestamp as Timestamp)?.toDate() || new Date();

          return {
            id: docSnapshot.id,
            ...data,
            chatType: getThreadType(data as Partial<MessageThread>),
            participantIds: ids,
            unreadCounts,
            typingUsers,
            lastReadAt,
            timestamp: timestampDate.toLocaleString(),
            timestampMs: timestampDate.getTime()
          } as MessageThread;
        }));

        const filtered = threads.filter((t): t is MessageThread => t !== null);
        setMessageThreads(filtered);
      }, (err) => {
        // Firestore listeners don't self-heal from an error (permission-denied
        // in particular is treated as terminal, not retried) - seen right after
        // native cold launch when a restored auth session's ID token hasn't
        // fully attached to outgoing requests yet. Without this, message
        // threads silently stay empty for the rest of the app session.
        console.error('Broad threads listener error, retrying shortly', err);
        if (unsub) unsub();
        if (!cancelled) {
          window.setTimeout(() => { if (!cancelled) listenBroad(); }, 3000);
        }
      });
    };

    const start = async () => {
      try {
        // Backfill participantIds so the indexed query works on first load
        const snap = await getDocs(collection(db, 'messageThreads'));
        const updates: Promise<any>[] = [];
        snap.docs.forEach((docSnapshot) => {
          const data = docSnapshot.data();
          const ids = Array.isArray(data.participantIds)
            ? data.participantIds
            : Array.isArray(data.participants)
              ? (data.participants as any[]).map(p => p?.id || p?.userId || p?.uid).filter(Boolean)
              : [];
          if (!ids.includes(currentUser.id)) {
            return;
          }
          if (!data.participantIds || ids.length !== data.participantIds.length) {
            updates.push(updateDoc(doc(db, 'messageThreads', docSnapshot.id), { participantIds: Array.from(new Set(ids)) }));
          }
        });
        await Promise.all(updates);
      } catch (err) {
        console.error('Backfill participantIds failed', err);
      }
      if (cancelled) return;
      listenWithQuery();
    };

    start();

    // Safety-net poll: onSnapshot can silently stall (seen on WKWebView/native),
    // so refetch periodically to guarantee thread previews/unread counts stay live.
    // Only runs while the Messenger tab is actually open — the onSnapshot listener
    // above stays active the whole session so unread badges keep updating elsewhere.
    const pollInterval = window.setInterval(async () => {
      if (activeNavIdRef.current !== 'messenger') return;
      try {
        const snap = await getDocs(query(collection(db, 'messageThreads'), where('participantIds', 'array-contains', currentUser.id)));
        const threads = snap.docs.map((docSnapshot) => {
          const data = docSnapshot.data();
          const ids: string[] = Array.isArray(data.participantIds)
            ? data.participantIds
            : Array.isArray(data.participants)
              ? (data.participants as any[]).map(p => p?.id || p?.userId || p?.uid).filter(Boolean)
              : [];
          const timestampDate = (data.timestamp as Timestamp)?.toDate() || new Date();
          return {
            id: docSnapshot.id,
            ...data,
            chatType: getThreadType(data as Partial<MessageThread>),
            participantIds: ids,
            unreadCounts: data.unreadCounts || {},
            typingUsers: data.typingUsers || {},
            lastReadAt: data.lastReadAt || {},
            timestamp: timestampDate.toLocaleString(),
            timestampMs: timestampDate.getTime()
          } as MessageThread;
        });
        if (!cancelled) setMessageThreads(threads);
      } catch (err) {
        // Ignore — the realtime listener remains the primary source.
      }
    }, 10000);

    return () => {
      cancelled = true;
      if (unsub) unsub();
      window.clearInterval(pollInterval);
    };
  }, [currentUser, getThreadType]);

  // Derive selectedThread from messageThreads to always get latest data (including typing status)
  const selectedThread = useMemo(() => {
    if (!selectedThreadId) return null;
    const thread = messageThreads.find(t => t.id === selectedThreadId) || fallbackThread;
    return thread ? hydrateThreadParticipants(thread) : null;
  }, [selectedThreadId, messageThreads, fallbackThread, hydrateThreadParticipants]);

  // If we don't yet have the thread in state, fetch it once so chat opens immediately
  useEffect(() => {
    if (!selectedThreadId) {
      setFallbackThread(null);
      return;
    }
    const existing = messageThreads.find(t => t.id === selectedThreadId);
    if (existing) {
      setFallbackThread(null);
      return;
    }
    const fetchThread = async () => {
      try {
        const snap = await getDoc(doc(db, 'messageThreads', selectedThreadId));
        if (snap.exists()) {
          const data = snap.data();
          const timestampDate = (data.timestamp as Timestamp)?.toDate?.() || new Date();
          setFallbackThread({
            id: snap.id,
            ...data,
            chatType: getThreadType(data as Partial<MessageThread>),
            timestamp: timestampDate.toLocaleString(),
            timestampMs: timestampDate.getTime(),
            unreadCounts: data.unreadCounts || {},
            typingUsers: data.typingUsers || {},
            lastReadAt: data.lastReadAt || {}
          } as MessageThread);
        }
      } catch (err) {
        console.error('Failed to fetch thread quickly for chat open', err);
      }
    };
    fetchThread();
  }, [selectedThreadId, messageThreads, getThreadType]);

  // Clear stale thread selection if data is missing after loading
  useEffect(() => {
    if (!isLoading && selectedThreadId && !selectedThread) {
      setSelectedThreadId(null);
      setFallbackThread(null);
    }
  }, [isLoading, selectedThreadId, selectedThread]);

  useEffect(() => {
    // Gated on hasVisitedSpotMap (not just currentUser) so the native iOS
    // location-permission prompt fires the first time the user actually
    // opens the map, not the moment they log in - calling
    // appGeolocation.* (the Capacitor plugin on native) is what triggers WebKit's native CoreLocation
    // prompt in a Capacitor WKWebView, so simply not calling it yet is what
    // defers the prompt. The separate live-location-share watcher below
    // covers the case where sharing needs a position without the map ever
    // having been opened.
    if (!currentUser || !hasVisitedSpotMap || !appGeolocation) {
      setDeviceLocation(null);
      return;
    }

    const watchId = appGeolocation.watchPosition(
      (position) => {
        const next = { lat: position.coords.latitude, lng: position.coords.longitude };
        const prev = lastWatchedLocationRef.current;
        // Skip GPS jitter (~a few metres) so we're not churning marker updates
        // and re-renders every time watchPosition fires with a near-identical fix.
        if (prev && Math.abs(prev.lat - next.lat) < 0.00003 && Math.abs(prev.lng - next.lng) < 0.00003) {
          return;
        }
        lastWatchedLocationRef.current = next;
        setDeviceLocation(next);
      },
      (error) => {
        console.warn('Device location watch failed', error);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 15000,
        timeout: 20000,
      }
    );

    return () => appGeolocation.clearWatch(watchId);
  }, [currentUser?.id, hasVisitedSpotMap]);

  useEffect(() => {
    if (!currentUser) {
      setLiveLocationShares([]);
      return;
    }

    let unsub: (() => void) | null = null;
    let stopped = false;

    const listenWithQuery = () => {
      const liveLocationsQuery = query(
        collection(db, 'liveLocationShares'),
        where('participantIds', 'array-contains', currentUser.id),
        where('isActive', '==', true)
      );
      unsub = onSnapshot(liveLocationsQuery, (snapshot) => {
        const shares = snapshot.docs.map((docSnapshot) => {
          const data = docSnapshot.data();
          const updatedAtMs = (data.updatedAt as Timestamp | undefined)?.toMillis?.() || Date.now();
          return {
            id: docSnapshot.id,
            threadId: data.threadId,
            threadName: data.threadName,
            participantIds: Array.isArray(data.participantIds) ? data.participantIds : [],
            ownerId: data.ownerId,
            isGroup: Boolean(data.isGroup),
            isActive: data.isActive !== false,
            location: data.location,
            user: data.user,
            updatedAtMs,
          } as LiveLocationShare;
        }).filter((share) => (
          !!share.threadId &&
          !!share.ownerId &&
          !!share.user?.id &&
          Number.isFinite(share.location?.lat) &&
          Number.isFinite(share.location?.lng)
        ));
        setLiveLocationShares(shares);
      }, (error) => {
        console.error('Live location listener error, falling back to broad collection', error);
        if (unsub) unsub();
        listenBroad();
      });
    };

    const listenBroad = () => {
      unsub = onSnapshot(collection(db, 'liveLocationShares'), (snapshot) => {
        const shares = snapshot.docs.map((docSnapshot) => {
          const data = docSnapshot.data();
          const participantIds = Array.isArray(data.participantIds) ? data.participantIds : [];
          if (!participantIds.includes(currentUser.id) || data.isActive === false) {
            return null;
          }
          const updatedAtMs = (data.updatedAt as Timestamp | undefined)?.toMillis?.() || Date.now();
          return {
            id: docSnapshot.id,
            threadId: data.threadId,
            threadName: data.threadName,
            participantIds,
            ownerId: data.ownerId,
            isGroup: Boolean(data.isGroup),
            isActive: data.isActive !== false,
            location: data.location,
            user: data.user,
            updatedAtMs,
          } as LiveLocationShare;
        }).filter((share): share is LiveLocationShare => (
          !!share &&
          Number.isFinite(share.location?.lat) &&
          Number.isFinite(share.location?.lng)
        ));
        setLiveLocationShares(shares);
      }, (err) => {
        // See matching comment on the messageThreads broad listener above -
        // a denied/errored listener does not auto-retry on its own.
        console.error('Broad live-location listener error, retrying shortly', err);
        if (unsub) unsub();
        if (!stopped) {
          window.setTimeout(() => { if (!stopped) listenBroad(); }, 3000);
        }
      });
    };

    listenWithQuery();
    return () => {
      stopped = true;
      if (unsub) unsub();
    };
  }, [currentUser?.id]);

  const activeOwnedLocationShares = useMemo(
    () => liveLocationShares.filter((share) => share.ownerId === currentUser?.id),
    [currentUser?.id, liveLocationShares]
  );

  const activeOwnedLocationThreadIds = useMemo(
    () => activeOwnedLocationShares.map((share) => share.threadId).sort(),
    [activeOwnedLocationShares]
  );

  useEffect(() => {
    if (!currentUser || !deviceLocation || activeOwnedLocationShares.length === 0) return;

    const syncLocation = async () => {
      const now = Date.now();
      const lastSyncedLocation = lastSyncedLocationRef.current;
      const latChanged = !lastSyncedLocation || Math.abs(lastSyncedLocation.lat - deviceLocation.lat) > 0.00001;
      const lngChanged = !lastSyncedLocation || Math.abs(lastSyncedLocation.lng - deviceLocation.lng) > 0.00001;
      if (!latChanged && !lngChanged && now - locationSyncTimestampRef.current < 15000) return;
      if ((latChanged || lngChanged) && now - locationSyncTimestampRef.current < 2500) return;
      if (!latChanged && !lngChanged && now - locationSyncTimestampRef.current < 2500) return;
      locationSyncTimestampRef.current = now;

      try {
        await Promise.all(activeOwnedLocationShares.map((share) => setDoc(doc(db, 'liveLocationShares', share.id), {
          location: deviceLocation,
          updatedAt: serverTimestamp(),
          user: {
            id: currentUser.id,
            username: currentUser.username,
            avatarUrl: currentUser.avatarUrl,
          },
        }, { merge: true })));
        lastSyncedLocationRef.current = deviceLocation;
      } catch (error) {
        console.error('Failed to sync shared location', error);
      }
    };

    syncLocation();
    const intervalId = window.setInterval(syncLocation, 5000);
    return () => window.clearInterval(intervalId);
  }, [activeOwnedLocationShares, currentUser, deviceLocation]);

  // Foreground-only location for the "Sesh/Event near you" push feature -
  // deliberately lightweight (no new permission prompt, no background
  // tracking): piggybacks on deviceLocation, which is only ever populated
  // while the app is open and the user has already granted location access
  // by visiting the map. Cloud Functions (onNewJamSesh/onNewEvent in
  // functions/index.js) only notify users whose lastLocation.updatedAt is
  // recent, so this being foreground-only just means the notification only
  // reaches someone who's had the app open somewhat recently - not a
  // silent background-location feature.
  useEffect(() => {
    if (!currentUser || !deviceLocation) return;

    const writeLastLocation = () => {
      setDoc(doc(db, 'users', currentUser.id), {
        lastLocation: { lat: deviceLocation.lat, lng: deviceLocation.lng, updatedAt: serverTimestamp() },
      }, { merge: true }).catch((error) => {
        console.error('Failed to update lastLocation', error);
      });
    };

    writeLastLocation();
    const intervalId = window.setInterval(writeLastLocation, 3 * 60 * 1000);
    return () => window.clearInterval(intervalId);
  }, [currentUser?.id, deviceLocation]);

  useEffect(() => {
    if (!currentUser || !appGeolocation || activeOwnedLocationShares.length === 0) {
      return;
    }

    const watchId = appGeolocation.watchPosition(
      (position) => {
        setDeviceLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (error) => {
        console.warn('Shared location background watch failed', error);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 5000,
        timeout: 15000,
      }
    );

    return () => appGeolocation.clearWatch(watchId);
  }, [activeOwnedLocationShares.length, currentUser?.id]);

  useEffect(() => {
    if (activeOwnedLocationShares.length > 0) return;
    lastSyncedLocationRef.current = null;
    locationSyncTimestampRef.current = 0;
  }, [activeOwnedLocationShares.length]);

  // --- HANDLERS ---
  // A banned account's login is disabled, so every way of signing in fails with auth/user-disabled (or, for username login, the
  // server answers permission-denied "banned"). Those all land here and show the banned page instead of an error message.
  const reportIfBanned = (err: any): boolean => {
    const code = String(err?.code || '');
    const banned = code === 'auth/user-disabled' || (code.endsWith('permission-denied') && /banned/i.test(String(err?.message || '')));
    if (banned) {
      setAuthPending(false);
      setShowBanned(true);
    }
    return banned;
  };

  const handleLogin = async (identifier: string, password: string): Promise<void> => {
     let finalEmail = identifier;
    // A username is resolved by a Cloud Function that verifies the password before returning the email (the email is
    // not on the public users doc, and is never handed out without the right password).
    if (!identifier.includes('@')) {
      // A banned account is public information (users/{uid}.isBanned), so say so right away instead of "incorrect password".
      try {
        const found = await getDocs(query(collection(db, 'users'), where('username', '==', identifier.trim().toLowerCase())));
        if (found.docs.some((d) => d.data()?.isBanned === true)) {
          setAuthPending(false);
          setShowBanned(true);
          throw Object.assign(new Error('This account has been banned.'), { code: 'auth/user-disabled' });
        }
      } catch (err: any) {
        if (err?.code === 'auth/user-disabled') throw err;
        // Could not look it up (offline, etc.): carry on with the normal sign-in, which reports its own error.
      }
      try {
        const result = await verifyUsernameLoginCallable({ username: identifier, password });
        finalEmail = result.data.email;
      } catch (err: any) {
        if (reportIfBanned(err)) throw err;
        const code = String(err?.code || '');
        if (code.endsWith('resource-exhausted')) throw Object.assign(new Error('Too many attempts.'), { code: 'auth/too-many-requests' });
        throw Object.assign(new Error('Incorrect username or password.'), { code: 'auth/invalid-credential' });
      }
    }
    try {
      await signInWithEmailAndPassword(auth, finalEmail, password);
    } catch (err: any) {
      reportIfBanned(err);
      throw err;
    }
  };

  const buildDefaultProfileArtwork = useCallback((seedSource: string) => {
    const gradientSeed = parseInt(seedSource.substring(0, 8), 16) || 0;
    const hue1 = (gradientSeed % 60) + 100;
    const hue2 = (gradientSeed % 40) + 140;

    const avatarUrl = `data:image/svg+xml,${encodeURIComponent(`
      <svg width="400" height="400" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:hsl(${hue1}, 70%, 45%);stop-opacity:1" />
            <stop offset="50%" style="stop-color:hsl(${hue2}, 60%, 55%);stop-opacity:1" />
            <stop offset="100%" style="stop-color:hsl(${hue1}, 70%, 35%);stop-opacity:1" />
          </linearGradient>
        </defs>
        <rect width="400" height="400" fill="url(#grad1)" />
      </svg>
    `)}`;

    const coverPhotoUrl = `data:image/svg+xml,${encodeURIComponent(`
      <svg width="1200" height="400" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="grad2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style="stop-color:hsl(${hue2}, 65%, 40%);stop-opacity:1" />
            <stop offset="50%" style="stop-color:hsl(${hue1}, 75%, 50%);stop-opacity:1" />
            <stop offset="100%" style="stop-color:hsl(${hue2}, 65%, 40%);stop-opacity:1" />
          </linearGradient>
        </defs>
        <rect width="1200" height="400" fill="url(#grad2)" />
      </svg>
    `)}`;

    return { avatarUrl, coverPhotoUrl };
  }, []);

  const isUsernameTaken = async (candidate: string) => {
    const snapshot = await getDocs(query(collection(db, 'users'), where('username', '==', candidate)));
    return !snapshot.empty;
  };

  const buildUniqueUsername = useCallback(async (rawBase: string) => {
    const normalizedBase = rawBase
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9._]/g, '')
      .replace(/^[._]+|[._]+$/g, '')
      .slice(0, 20) || 'rider';

    if (!hasInappropriateUsername(normalizedBase) && !(await isUsernameTaken(normalizedBase))) {
      return normalizedBase;
    }

    for (let attempt = 1; attempt < 1000; attempt += 1) {
      const candidate = `${normalizedBase}${attempt}`;
      if (!hasInappropriateUsername(candidate) && !(await isUsernameTaken(candidate))) {
        return candidate;
      }
    }

    return `rider${Date.now().toString().slice(-6)}`;
  }, []);
  
  const handleSignUp = async (username: string, email: string, password: string, sports: Sport[], firstName: string, lastName: string): Promise<void> => {
    email = email.trim();
    firstName = cleanLine(firstName, TEXT_LIMITS.name);
    lastName = cleanLine(lastName, TEXT_LIMITS.name);
    if (!isValidEmail(email)) {
      throw Object.assign(new Error('Please enter a valid email address.'), { code: 'auth/invalid-email' });
    }
    if (password.length < 8 || password.length > 128) {
      throw Object.assign(new Error('password must be 8 or more characters'), { code: 'auth/weak-password' });
    }
    const trimmedUsername = username.trim();
    if (!trimmedUsername) {
      throw new Error('Username cannot be empty.');
    }
    if (!isValidUsernameFormat(trimmedUsername)) {
      throw new Error('Username can only contain letters, numbers, underscores, and periods.');
    }
    if (hasInappropriateUsername(trimmedUsername)) {
      throw new Error('Username contains inappropriate language. Choose a different username.');
    }

    const usernameQuery = query(collection(db, 'users'), where('username', '==', trimmedUsername.toLowerCase()));
    const usernameQuerySnapshot = await getDocs(usernameQuery);

    if (!usernameQuerySnapshot.empty) {
      throw new Error('This name is taken');
    }

    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    const { avatarUrl, coverPhotoUrl } = buildDefaultProfileArtwork(user.uid);

    // Create a user document in Firestore
    const userDocRef = doc(db, "users", user.uid);
    await setDoc(userDocRef, {
        username: trimmedUsername.toLowerCase(),
        // email is intentionally NOT stored here - users/{uid} is publicly
        // readable for guest profile browsing, and Firestore rules can't
        // restrict individual fields on a read. Firebase Auth is the sole
        // source of truth for email; see getEmailForUsername in functions/.
        sports: sports.map(s => ({ id: s.id, name: s.name })), // Store only id and name, not icon function
        firstName,
        lastName,
        avatarUrl,
        coverPhotoUrl,
        bio: 'New Rider!',
        followers: [],
        following: [],
        isOnline: true,
        needsGoogleOnboarding: false,
        needsWelcomeOnboarding: true,
    });
    setJustSignedUpUid(user.uid);
  };

  const handleGoogleSignIn = async (): Promise<void> => {
    const { credential: googleCredential } = await FirebaseAuthentication.signInWithGoogle();
    const oauthCredential = GoogleAuthProvider.credential(googleCredential?.idToken);
    const credential = await signInWithCredential(auth, oauthCredential).catch((err: any) => { reportIfBanned(err); throw err; });
    const googleUser = credential.user;
    const userDocRef = doc(db, 'users', googleUser.uid);
    const existingUserDoc = await getDoc(userDocRef);

    if (existingUserDoc.exists()) {
      return;
    }

    const displayName = googleUser.displayName?.trim() || '';
    const displayParts = displayName.split(/\s+/).filter(Boolean);
    const firstName = displayParts[0] || 'Rider';
    const lastName = displayParts.slice(1).join(' ');
    const emailPrefix = googleUser.email?.split('@')[0] || '';
    const baseUsername = emailPrefix || displayName || `rider${googleUser.uid.slice(0, 6)}`;
    const username = await buildUniqueUsername(baseUsername);
    const { avatarUrl, coverPhotoUrl } = buildDefaultProfileArtwork(googleUser.uid);

    if (!googleUser.displayName && displayName) {
      await updateProfile(googleUser, { displayName });
    }

    const newUserData = {
      // Used by the developer dashboard's signup analytics (accounts made before this have no joinedAt).
      joinedAt: Date.now(),
      username,
      // email intentionally omitted - see handleSignUp above.
      sports: [],
      firstName,
      lastName,
      avatarUrl: googleUser.photoURL || avatarUrl,
      coverPhotoUrl,
      bio: 'New Rider!',
      followers: [],
      following: [],
      isOnline: true,
      needsGoogleOnboarding: true,
    };
    await setDoc(userDocRef, newUserData);
    // The global onAuthStateChanged listener (mounted since app start) reacts
    // to the sign-in itself, which fires the instant signInWithCredential
    // resolves - almost always before this setDoc above lands. It checks
    // Firestore, finds no doc yet for a brand-new user, and sets currentUser
    // to null - and since auth state doesn't change again, nothing re-runs
    // that check afterward. Setting currentUser here directly, instead of
    // waiting on that listener to notice, is what actually moves a new user
    // off the sign-in screen without needing a manual page reload.
    setIsGuest(false);
    setCurrentUser({ id: googleUser.uid, ...newUserData } as User);
    setJustSignedUpUid(googleUser.uid);
  };
  
  const handleAppleSignIn = async (): Promise<void> => {
    // Unlike Google's credential, Apple's is nonce-locked: Firebase rejects a
    // second signInWithCredential using the same idToken/nonce pair with
    // "Duplicate credential received... (auth/missing-or-invalid-nonce)".
    // The global config has skipNativeAuth: false (native iOS Auth.auth()
    // would consume the credential first), so this call overrides it to
    // true just for Apple - only the JS-side signInWithCredential below
    // consumes the credential, avoiding the double-consumption entirely.
    // scopes is ignored on iOS (its native handler always requests
    // .fullName/.email itself) but is required on Web/Android, where Apple
    // otherwise returns just a bare user id - no email, no name to build a
    // profile from.
    //
    // Apple only ever sends the user's name/email on this first-ever
    // authorization, and it comes back on `user` (the plugin's native
    // result, populated directly from ASAuthorizationAppleIDCredential) -
    // NOT on the Firebase JS `signInWithCredential` user below, whose
    // displayName/email stay null for an Apple OAuth credential. Apple's
    // App Store review requires using this data instead of asking the
    // user to type their name/email again (Guideline 4 - Design), so we
    // read it here and skip any follow-up "finish your profile" step that
    // would re-request it.
    const { credential: appleCredential, user: appleNativeUser } = await FirebaseAuthentication.signInWithApple({ scopes: ['email', 'name'], skipNativeAuth: true });
    const provider = new OAuthProvider('apple.com');
    const oauthCredential = provider.credential({
      idToken: appleCredential?.idToken,
      rawNonce: appleCredential?.nonce,
    });
    const credential = await signInWithCredential(auth, oauthCredential).catch((err: any) => { reportIfBanned(err); throw err; });
    const appleUser = credential.user;
    const userDocRef = doc(db, 'users', appleUser.uid);
    const existingUserDoc = await getDoc(userDocRef);

    if (existingUserDoc.exists()) {
      return;
    }

    const displayName = appleNativeUser?.displayName?.trim() || appleUser.displayName?.trim() || '';
    const displayParts = displayName.split(/\s+/).filter(Boolean);
    const firstName = displayParts[0] || 'Rider';
    const lastName = displayParts.slice(1).join(' ');
    const email = appleNativeUser?.email || appleUser.email || '';
    const emailPrefix = email.split('@')[0] || '';
    const baseUsername = emailPrefix || displayName || `rider${appleUser.uid.slice(0, 6)}`;
    const username = await buildUniqueUsername(baseUsername);
    const { avatarUrl, coverPhotoUrl } = buildDefaultProfileArtwork(appleUser.uid);

    if (!appleUser.displayName && displayName) {
      await updateProfile(appleUser, { displayName });
    }

    const newUserData = {
      // Used by the developer dashboard's signup analytics (accounts made before this have no joinedAt).
      joinedAt: Date.now(),
      username,
      // email intentionally omitted - see handleSignUp above.
      sports: [],
      firstName,
      lastName,
      avatarUrl: appleUser.photoURL || avatarUrl,
      coverPhotoUrl,
      bio: 'New Rider!',
      followers: [],
      following: [],
      isOnline: true,
      // Unlike Google sign-in, never route through the required
      // name/username form here - Apple already gave us (or we've
      // defaulted) everything that form would ask for.
      needsWelcomeOnboarding: true,
    };
    await setDoc(userDocRef, newUserData);
    // See matching comment in handleGoogleSignIn - the global
    // onAuthStateChanged listener races this setDoc for a brand-new user and
    // loses, leaving currentUser stuck at null until a manual reload.
    setIsGuest(false);
    setCurrentUser({ id: appleUser.uid, ...newUserData } as User);
    setJustSignedUpUid(appleUser.uid);
  };

  const handleLogout = async () => {
    // No need to set offline - lastActive timestamp will naturally become stale
    setSelectedThreadId(null);
    setFallbackThread(null);
    if (activeOwnedLocationShares.length > 0) {
      try {
        const batch = writeBatch(db);
        activeOwnedLocationShares.forEach((share) => {
          batch.set(doc(db, 'liveLocationShares', share.id), {
            isActive: false,
            updatedAt: serverTimestamp(),
            endedAt: serverTimestamp(),
          }, { merge: true });
        });
        await batch.commit();
      } catch (error) {
        console.error('Failed to stop shared locations during logout', error);
      }
    }
    clearLastUid();
    await signOut(auth);
  };

  const handleDeleteAccount = async () => {
    if (!currentUser) return;
    await deleteAccountCallable();
    // The account no longer exists server-side at this point; sign out to
    // clear the now-invalid local session and drop back to the guest/auth
    // screen the same way handleLogout does.
    setSelectedThreadId(null);
    setFallbackThread(null);
    if (typeof window !== 'undefined' && window.location.pathname === '/delete-account') {
      window.history.replaceState({}, '', '/');
    }
    clearLastUid();
    await signOut(auth);
  };

  const handleBrowseAsGuest = () => {
    setIsGuest(true); 
    setIsLoading(false);
  };
  
  const handlePromptLogin = () => { 
    setIsGuest(false); 
    setCurrentUser(null); 
  };
  
  const handleToggleLike = async (postId: string) => {
      if (!currentUser) { handlePromptLogin(); return; }
      let reverted = false;
      // Optimistic UI update
      setPosts(prev => prev.map(p => {
        if (p.id !== postId) return p;
        const alreadyLiked = (p.likedBy || []).includes(currentUser.id);
        const nextLikedBy = alreadyLiked
          ? (p.likedBy || []).filter(id => id !== currentUser.id)
          : [...(p.likedBy || []), currentUser.id];
        return { ...p, likedBy: nextLikedBy };
      }));
      try {
        const postRef = doc(db, 'posts', postId);
        const postDoc = await getDoc(postRef);
        if(postDoc.exists()) {
            const likedBy = postDoc.data().likedBy || [];
            if (likedBy.includes(currentUser.id)) {
                await updateDoc(postRef, { likedBy: arrayRemove(currentUser.id) });
            } else {
                await updateDoc(postRef, { likedBy: arrayUnion(currentUser.id) });
            }
        } else {
          console.warn('Post not found when toggling like:', postId);
        }
      } catch (err) {
        console.error('Error toggling like:', err);
        reverted = true;
        // revert optimistic update
        setPosts(prev => prev.map(p => {
          if (p.id !== postId) return p;
          const alreadyLiked = (p.likedBy || []).includes(currentUser.id);
          const nextLikedBy = alreadyLiked
            ? (p.likedBy || []).filter(id => id !== currentUser.id)
            : [...(p.likedBy || []), currentUser.id];
          return { ...p, likedBy: nextLikedBy };
        }));
        alert('Could not save like. Please try again.');
      }
  };

  const handleReactToPost = async (postId: string, emoji: string) => {
    if (!currentUser) { handlePromptLogin(); return; }
    const previousReaction = posts.find(p => p.id === postId)?.reactions?.[currentUser.id];
    const isRemoving = previousReaction === emoji;
    setPosts(prev => prev.map(p => {
      if (p.id !== postId) return p;
      const nextReactions = { ...(p.reactions || {}) };
      if (isRemoving) delete nextReactions[currentUser.id];
      else nextReactions[currentUser.id] = emoji;
      return { ...p, reactions: nextReactions };
    }));
    try {
      await updateDoc(doc(db, 'posts', postId), {
        [`reactions.${currentUser.id}`]: isRemoving ? deleteField() : emoji,
      });
    } catch (err) {
      console.error('Error reacting to post:', err);
      setPosts(prev => prev.map(p => {
        if (p.id !== postId) return p;
        const nextReactions = { ...(p.reactions || {}) };
        if (previousReaction) nextReactions[currentUser.id] = previousReaction;
        else delete nextReactions[currentUser.id];
        return { ...p, reactions: nextReactions };
      }));
    }
  };

  const handleToggleSave = async (postId: string) => {
      if (!currentUser) { handlePromptLogin(); return; }
      // Optimistic update
      setPosts(prev => prev.map(p => {
        if (p.id !== postId) return p;
        const alreadySaved = (p.savedBy || []).includes(currentUser.id);
        const nextSavedBy = alreadySaved
          ? (p.savedBy || []).filter(id => id !== currentUser.id)
          : [...(p.savedBy || []), currentUser.id];
        return { ...p, savedBy: nextSavedBy };
      }));
      try {
        const postRef = doc(db, 'posts', postId);
        const postDoc = await getDoc(postRef);
        if(postDoc.exists()) {
            const savedBy = postDoc.data().savedBy || [];
            if (savedBy.includes(currentUser.id)) {
                await updateDoc(postRef, { savedBy: arrayRemove(currentUser.id) });
            } else {
                await updateDoc(postRef, { savedBy: arrayUnion(currentUser.id) });
            }
        } else {
          console.warn('Post not found when toggling save:', postId);
        }
      } catch (err) {
        console.error('Error toggling save:', err);
        // revert optimistic update
        setPosts(prev => prev.map(p => {
          if (p.id !== postId) return p;
          const alreadySaved = (p.savedBy || []).includes(currentUser.id);
          const nextSavedBy = alreadySaved
            ? (p.savedBy || []).filter(id => id !== currentUser.id)
            : [...(p.savedBy || []), currentUser.id];
          return { ...p, savedBy: nextSavedBy };
        }));
        alert('Could not save post. Please try again.');
      }
  };

  const handleDeletePost = useCallback(async (postId: string) => {
    if (!currentUser) { handlePromptLogin(); return; }
    const target = posts.find(p => p.id === postId);
    if (!target || (target.user.id !== currentUser.id && !isRidazModerator)) {
      alert('You can only delete your own posts.');
      return;
    }
    setPosts(prev => prev.filter(p => p.id !== postId));
    // Its photos also live in the tagged spot's map gallery - drop them there right away (the server removes them
    // for good via onPostDeletedRemoveFromMap).
    if (target.locationTag?.spotId) {
      const mediaUrls = new Set([target.imageUrl, target.videoUrlCompressed, ...((target.mediaItems || []).map((m: any) => m?.url))].filter(Boolean));
      setSpots(prev => prev.map(s => s.id === target.locationTag!.spotId ? { ...s, photos: (s.photos || []).filter((ph: any) => !mediaUrls.has(ph?.url)) } : s));
    }
    try {
      const resolveStorageRef = (url: string) => {
        if (!url) return null;
        if (url.startsWith('gs://')) return ref(storage, url);
        if (url.startsWith('http')) {
          const match = url.match(/\/o\/([^?]+)/);
          if (match && match[1]) {
            const decodedPath = decodeURIComponent(match[1]);
            return ref(storage, decodedPath);
          }
        }
        return null;
      };
      await deleteDoc(doc(db, 'posts', postId));
      if (target.imageUrl) {
        try {
          const storageRef = resolveStorageRef(target.imageUrl);
          if (storageRef) {
            await deleteObject(storageRef);
          }
        } catch (err) {
          console.warn('Failed to delete post media from storage, continuing', err);
        }
      }
    } catch (err) {
      console.error('Error deleting post:', err);
      // rollback UI removal on failure
      setPosts(prev => [...prev, target].sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || '')));
      alert('Failed to delete post. Please try again.');
    }
  }, [currentUser, posts, handlePromptLogin, isRidazModerator]);

  const handleToggleZipLike = async (zipId: string) => {
      if (!currentUser) { handlePromptLogin(); return; }
      setZips(prev => prev.map((zip) => {
        if (zip.id !== zipId) return zip;
        const alreadyLiked = (zip.likedBy || []).includes(currentUser.id);
        return {
          ...zip,
          likedBy: alreadyLiked
            ? (zip.likedBy || []).filter((id) => id !== currentUser.id)
            : [...(zip.likedBy || []), currentUser.id],
        };
      }));
      try {
        const zipRef = doc(db, 'zips', zipId);
        const zipDoc = await getDoc(zipRef);
        if (!zipDoc.exists()) return;
        const likedBy = zipDoc.data().likedBy || [];
        if (likedBy.includes(currentUser.id)) {
          await updateDoc(zipRef, { likedBy: arrayRemove(currentUser.id) });
        } else {
          await updateDoc(zipRef, { likedBy: arrayUnion(currentUser.id) });
        }
      } catch (err) {
        console.error('Error toggling zip like:', err);
        setZips(prev => prev.map((zip) => {
          if (zip.id !== zipId) return zip;
          const alreadyLiked = (zip.likedBy || []).includes(currentUser.id);
          return {
            ...zip,
            likedBy: alreadyLiked
              ? (zip.likedBy || []).filter((id) => id !== currentUser.id)
              : [...(zip.likedBy || []), currentUser.id],
          };
        }));
        alert('Could not save Zip like. Please try again.');
      }
  };

  const handleReactToZip = async (zipId: string, emoji: string) => {
    if (!currentUser) { handlePromptLogin(); return; }
    const previousReaction = zips.find(z => z.id === zipId)?.reactions?.[currentUser.id];
    const isRemoving = previousReaction === emoji;
    setZips(prev => prev.map((zip) => {
      if (zip.id !== zipId) return zip;
      const nextReactions = { ...(zip.reactions || {}) };
      if (isRemoving) delete nextReactions[currentUser.id];
      else nextReactions[currentUser.id] = emoji;
      return { ...zip, reactions: nextReactions };
    }));
    try {
      await updateDoc(doc(db, 'zips', zipId), {
        [`reactions.${currentUser.id}`]: isRemoving ? deleteField() : emoji,
      });
    } catch (err) {
      console.error('Error reacting to zip:', err);
      setZips(prev => prev.map((zip) => {
        if (zip.id !== zipId) return zip;
        const nextReactions = { ...(zip.reactions || {}) };
        if (previousReaction) nextReactions[currentUser.id] = previousReaction;
        else delete nextReactions[currentUser.id];
        return { ...zip, reactions: nextReactions };
      }));
      }
  };

  const handleAddComment = async (postId: string, text: string, parentId?: string | null) => {
    if (!currentUser) { handlePromptLogin(); return; }
    if (!allowAction('comment', 8, 20000)) { window.alert(TOO_FAST_MESSAGE); return; }

    try {
      const moderatedText = censorInappropriateText(cleanText(text, TEXT_LIMITS.comment));
      const mentionUsernames = Array.from(moderatedText.matchAll(/@([A-Za-z0-9_]+)/g)).map(m => m[1].toLowerCase());
      const uniqueMentions = Array.from(new Set(mentionUsernames));
      const mentionIds = users
        .filter(u => uniqueMentions.includes(u.username.toLowerCase()))
        .map(u => u.id);

      const commentsRef = collection(db, 'posts', postId, 'comments');
      const commentDoc = await addDoc(commentsRef, {
          user: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
          text: moderatedText,
          timestamp: serverTimestamp(),
          parentId: parentId || null,
      });

      console.log('[Comment Debug] Comment added successfully:', commentDoc.id);

      if (mentionIds.length > 0 || uniqueMentions.length > 0) {
        const postRef = doc(db, 'posts', postId);
        await updateDoc(postRef, {
          tags: arrayUnion(...mentionIds),
          mentionUsernames: arrayUnion(...uniqueMentions),
        }).catch(err => console.warn('Failed to append mention metadata', err));
        setPosts(prev => prev.map(p => {
          if (p.id !== postId) return p;
          const nextTags = Array.from(new Set([...(p.tags || []), ...mentionIds]));
          const nextMentions = Array.from(new Set([...(p as any).mentionUsernames || [], ...uniqueMentions]));
          return { ...p, tags: nextTags, mentionUsernames: nextMentions };
        }));
      }
    } catch (error) {
      console.error('[Comment Debug] Failed to add comment:', error);
      // You could add user-facing error notification here
      throw error;
    }
  };

  const handleToggleCommentLike = async (postId: string, commentId: string) => {
    if (!currentUser) { handlePromptLogin(); return; }
    try {
      const commentRef = doc(db, 'posts', postId, 'comments', commentId);
      const commentDoc = await getDoc(commentRef);
      if (!commentDoc.exists()) return;
      const likedBy = commentDoc.data().likedBy || [];
      if (likedBy.includes(currentUser.id)) {
        await updateDoc(commentRef, { likedBy: arrayRemove(currentUser.id) });
      } else {
        await updateDoc(commentRef, { likedBy: arrayUnion(currentUser.id) });
      }
    } catch (err) {
      console.error('Error toggling comment like:', err);
      alert('Could not save like. Please try again.');
    }
  };

  const handleAddSpotComment = async (spotId: string, text: string, parentId?: string | null) => {
    if (!currentUser) { handlePromptLogin(); return; }
    if (!allowAction('comment', 8, 20000)) { window.alert(TOO_FAST_MESSAGE); return; }
    const moderatedText = censorInappropriateText(cleanText(text, TEXT_LIMITS.comment));
    const commentsRef = collection(db, 'spots', spotId, 'comments');
    await addDoc(commentsRef, {
        user: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
        text: moderatedText,
        timestamp: serverTimestamp(),
        parentId: parentId || null,
    });
  };

  // Jam sesh / event comments are temporary - each carries an `expiresAt`
  // matching the sesh/event's own endTime, which the client filters against
  // immediately (see SpotMap.tsx) and a scheduled Cloud Function later
  // sweeps for real deletion (see functions/index.js cleanupExpiredSeshEventComments).
  const handleAddJamSeshComment = async (jamSeshId: string, text: string) => {
    if (!currentUser) { handlePromptLogin(); return; }
    const sesh = jamSeshes.find(s => s.id === jamSeshId);
    if (!sesh) return;
    const moderatedText = censorInappropriateText(cleanText(text, TEXT_LIMITS.comment));
    await addDoc(collection(db, 'jam_seshes', jamSeshId, 'comments'), {
      user: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
      text: moderatedText,
      timestamp: serverTimestamp(),
      parentId: null,
      expiresAt: Timestamp.fromDate(new Date(sesh.endTime)),
    });
  };

  const handleAddEventComment = async (eventId: string, text: string) => {
    if (!currentUser) { handlePromptLogin(); return; }
    const evt = events.find(e => e.id === eventId);
    if (!evt) return;
    const moderatedText = censorInappropriateText(cleanText(text, TEXT_LIMITS.comment));
    await addDoc(collection(db, 'events', eventId, 'comments'), {
      user: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
      text: moderatedText,
      timestamp: serverTimestamp(),
      parentId: null,
      expiresAt: Timestamp.fromDate(new Date(evt.endTime)),
    });
  };

  // Pins whose photos are still uploading in the background (id -> 0..1). SpotMap draws a progress bar on each pin.
  const [pinUploadProgress, setPinUploadProgress] = useState<Record<string, number>>({});
  const setPinProgress = (id: string, fraction: number | null) => {
    setPinUploadProgress(prev => {
      if (fraction === null) {
        if (!(id in prev)) return prev;
        const { [id]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: Math.max(0, Math.min(1, fraction)) };
    });
  };
  // Uploads many files at once and reports one combined progress fraction. Returns the download URLs in order.
  const uploadFilesWithPinProgress = async (pinId: string, uploads: { path: string; file: File }[]): Promise<string[]> => {
    const totals = uploads.map(u => u.file.size || 1);
    const done = uploads.map(() => 0);
    const grand = totals.reduce((a, b) => a + b, 0) || 1;
    const report = () => setPinProgress(pinId, done.reduce((a, b) => a + b, 0) / grand);
    report();
    return Promise.all(uploads.map(async (u, i) => {
      const storageRef = ref(storage, u.path);
      await uploadFileWithTimeout(storageRef, u.file, { contentType: u.file.type || 'image/jpeg', customMetadata: { uploadedBy: currentUser?.id || '' } }, 60000, (fraction: number) => {
        done[i] = totals[i] * fraction;
        report();
      });
      done[i] = totals[i];
      report();
      return getDownloadURL(storageRef);
    }));
  };

  // The "Submit Shop" flow (SpotMap.tsx) is only shown to @ridaz and already
  // enforces the required-fields/3-photo-minimum validation before calling
  // this - firestore.rules mirrors the @ridaz-only restriction server-side.
  const handleAddShop = async (
    shopData: { name: string; description: string; location: { lat: number; lng: number }; sports: Sport[]; businessHours: string },
    photoFiles: File[]
  ) => {
    if (!currentUser) { handlePromptLogin(); return; }
    const adder = { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl };
    let newShopRef: any = null;
    try {
      // Create the pin straight away; photos upload in the background while the pin shows a progress bar.
      newShopRef = await addDoc(collection(db, 'shops'), {
        ...shopData,
        name: censorInappropriateText(cleanLine(shopData.name, TEXT_LIMITS.title)),
        description: censorInappropriateText(cleanText(shopData.description, TEXT_LIMITS.description)),
        businessHours: cleanLine(shopData.businessHours, 120),
        sports: shopData.sports.map(s => ({ id: s.id, name: s.name })),
        addedBy: adder,
        photos: [],
        overallRatings: {},
        // Submissions from the @ridaz account are approved on the spot; everyone else's wait in the dashboard queue.
        reviewed: isRidazModerator,
        createdAt: serverTimestamp(),
      });
      if (photoFiles.length > 0) {
        const shopId = newShopRef.id;
        setPinProgress(shopId, 0);
        const compressedFiles = await Promise.all(photoFiles.map(file => compressAndDownscaleImage(file)));
        const urls = await uploadFilesWithPinProgress(shopId, compressedFiles.map(file => ({
          path: `shop_photos/${shopId}/${uuidv4()}.${file.name.split('.').pop() || 'jpg'}`,
          file,
        })));
        const uploadedPhotos = urls.map(url => ({ id: uuidv4(), url, type: 'image' as const, addedBy: adder }));
        await updateDoc(newShopRef, { photos: uploadedPhotos });
        setShops(prev => prev.map(shop => (shop.id === shopId ? { ...shop, photos: uploadedPhotos } : shop)));
        setPinProgress(shopId, null);
      }
    } catch (err: any) {
      console.error('Failed to add shop:', err);
      if (newShopRef) {
        setPinProgress(newShopRef.id, null);
        // A shop with no photos is not a usable listing - remove the half-created pin.
        try { await deleteDoc(newShopRef); } catch { /* ignore */ }
        setShops(prev => prev.filter(shop => shop.id !== newShopRef.id));
      }
      alert(`Failed to add shop: ${err?.message || 'photos could not upload'}. Please try again.`);
    }
  };

  // @ridaz only (Firestore rules enforce it too): edit an existing shop's details.
  const handleEditShop = async (shopId: string, edits: { name: string; description: string; businessHours: string; sports: { id: string; name: string }[] }) => {
    if (!isRidazModerator) return;
    await updateDoc(doc(db, 'shops', shopId), {
      name: edits.name,
      description: edits.description,
      businessHours: edits.businessHours,
      sports: edits.sports,
    });
    setShops(prev => prev.map(shop => (shop.id === shopId ? { ...shop, ...edits, sports: edits.sports as any } : shop)));
  };

  const handleRateShop = async (shopId: string, value: number) => {
    if (!currentUser) { handlePromptLogin(); return; }
    const normalizedValue = Math.min(5, Math.max(1, Math.round(value)));
    setShops(prev => prev.map(shop => {
      if (shop.id !== shopId) return shop;
      return { ...shop, overallRatings: { ...(shop.overallRatings || {}), [currentUser.id]: normalizedValue } };
    }));
    try {
      await updateDoc(doc(db, 'shops', shopId), { [`overallRatings.${currentUser.id}`]: normalizedValue });
    } catch (err) {
      console.error('Failed to rate shop:', err);
    }
  };

  const handleAddShopPhoto = async (shopId: string, imageFile: File) => {
    if (!currentUser) { handlePromptLogin(); return; }
    try {
      const processedFile = await compressAndDownscaleImage(imageFile);
      const fileExtension = processedFile.name.split('.').pop() || 'jpg';
      const storageRef = ref(storage, `shop_photos/${shopId}/${uuidv4()}.${fileExtension}`);
      await uploadBytes(storageRef, processedFile, { contentType: processedFile.type });
      const url = await getDownloadURL(storageRef);
      await updateDoc(doc(db, 'shops', shopId), {
        photos: arrayUnion({ id: uuidv4(), url, type: 'image', addedBy: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl } })
      });
    } catch (err) {
      console.error('Failed to add shop photo:', err);
      alert('Failed to add photo. Check console for details.');
    }
  };

  const handleAddShopComment = async (shopId: string, text: string) => {
    if (!currentUser) { handlePromptLogin(); return; }
    if (!allowAction('comment', 8, 20000)) { window.alert(TOO_FAST_MESSAGE); return; }
    const moderatedText = censorInappropriateText(cleanText(text, TEXT_LIMITS.comment));
    await addDoc(collection(db, 'shops', shopId, 'comments'), {
      user: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
      text: moderatedText,
      timestamp: serverTimestamp(),
      parentId: null,
    });
  };

  const handleToggleSpotMediaLike = async (spotId: string, mediaId: string) => {
    if (!currentUser) { handlePromptLogin(); return; }

    const currentSpot = spots.find(s => s.id === spotId);
    const currentLikes = currentSpot?.mediaLikes?.[mediaId] || [];
    const hasLiked = currentLikes.includes(currentUser.id);
    const nextLikedBy = hasLiked
      ? currentLikes.filter((id: string) => id !== currentUser.id)
      : [...currentLikes, currentUser.id];

    setSpots((prev) => prev.map((spot) => {
      if (spot.id !== spotId) return spot;
      return {
        ...spot,
        mediaLikes: {
          ...(spot.mediaLikes || {}),
          [mediaId]: nextLikedBy,
        },
      };
    }));

    await updateDoc(doc(db, 'spots', spotId), {
      [`mediaLikes.${mediaId}`]: nextLikedBy,
    });
  };

  const handleAddSpotMediaComment = async (spotId: string, mediaId: string, text: string) => {
    if (!currentUser) { handlePromptLogin(); return; }
    if (!allowAction('comment', 8, 20000)) { window.alert(TOO_FAST_MESSAGE); return; }
    const moderatedText = censorInappropriateText(cleanText(text, TEXT_LIMITS.comment));
    const commentsRef = collection(db, 'spots', spotId, 'mediaComments');
    await addDoc(commentsRef, {
      mediaId,
      user: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
      text: moderatedText,
      timestamp: serverTimestamp(),
      parentId: null,
    });
  };

  const handleRateSpot = async (spotId: string, ratingType: 'overall' | 'difficulty' | 'security', value: number) => {
    if (!currentUser) { handlePromptLogin(); return; }

    const normalizedValue = Math.min(5, Math.max(1, Math.round(value)));
    const fieldName =
      ratingType === 'overall'
        ? 'overallRatings'
        : ratingType === 'difficulty'
          ? 'difficultyRatings'
          : 'securityRatings';

    setSpots((prev) => prev.map((spot) => {
      if (spot.id !== spotId) return spot;
      const existingRatings =
        (fieldName === 'overallRatings'
          ? spot.overallRatings
          : fieldName === 'difficultyRatings'
            ? spot.difficultyRatings
            : spot.securityRatings) || {};
      const nextRatings: SpotRatingMap = {
        ...existingRatings,
        [currentUser.id]: normalizedValue,
      };
      return {
        ...spot,
        [fieldName]: nextRatings,
      };
    }));

    await updateDoc(doc(db, 'spots', spotId), {
      [`${fieldName}.${currentUser.id}`]: normalizedValue,
    });
  };

  // --- Marketplace (buy & sell) ---

  const handleSaveListing = async (listingId: string | null, input: {
    title: string; description: string; price: number; obo: boolean; willingToTrade: boolean; category: string;
    condition: Listing['condition']; locationLabel: string; location: { lat: number; lng: number } | null; photos: (string | File)[];
  }, onProgress?: (fraction: number) => void) => {
    if (!currentUser) { handlePromptLogin(); throw new Error('Please log in to sell.'); }
    if (!allowAction('listing', 8, 10 * 60000)) throw new Error(TOO_FAST_MESSAGE);
    // Keep existing photo URLs, upload new files, preserve the user's order.
    // Progress: photos are downscaled first, then uploaded; the last 5% is saving the listing itself.
    const items = input.photos.slice(0, 8);
    const prepared: (string | File)[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      prepared.push(typeof item === 'string' ? item : await compressAndDownscaleImage(item, 1280, 1280, 0.82));
      onProgress?.(0.1 * ((i + 1) / items.length));
    }
    const totalBytes = prepared.reduce((sum, p) => sum + (typeof p === 'string' ? 0 : p.size), 0) || 1;
    let doneBytes = 0;
    const photoUrls: string[] = [];
    for (const item of prepared) {
      if (typeof item === 'string') { photoUrls.push(item); continue; }
      const ext = item.name.split('.').pop() || 'jpg';
      const storageRef = ref(storage, `listing_photos/${currentUser.id}/${uuidv4()}.${ext}`);
      const task = uploadBytesResumable(storageRef, item, { contentType: item.type, customMetadata: { uploadedBy: currentUser.id } });
      await new Promise<void>((resolve, reject) => {
        task.on('state_changed',
          (snap) => onProgress?.(0.1 + 0.85 * ((doneBytes + snap.bytesTransferred) / totalBytes)),
          reject,
          () => resolve());
      });
      doneBytes += item.size;
      photoUrls.push(await getDownloadURL(storageRef));
    }
    onProgress?.(0.95);
    const fields = {
      title: censorInappropriateText(cleanLine(input.title, TEXT_LIMITS.title)),
      description: censorInappropriateText(cleanText(input.description, TEXT_LIMITS.description)),
      price: cleanPrice(input.price),
      obo: Boolean(input.obo),
      willingToTrade: Boolean(input.willingToTrade),
      category: /^[a-z0-9-]{1,30}$/.test(String(input.category)) ? input.category : 'other',
      condition: (['new', 'like-new', 'good', 'fair'] as const).includes(input.condition) ? input.condition : 'good',
      photos: photoUrls,
      locationLabel: censorInappropriateText(cleanLine(input.locationLabel, TEXT_LIMITS.place)),
      location: input.location && Number.isFinite(input.location.lat) && Number.isFinite(input.location.lng)
        && Math.abs(input.location.lat) <= 90 && Math.abs(input.location.lng) <= 180 ? input.location : null,
    };
    if (!fields.title) throw new Error('Please add a title.');
    if (listingId) {
      await updateDoc(doc(db, 'listings', listingId), fields);
      onProgress?.(1);
      return;
    }
    await addDoc(collection(db, 'listings'), {
      ...fields,
      seller: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
      status: 'active',
      savedBy: [],
      createdAt: serverTimestamp(),
    });
    onProgress?.(1);
  };

  // Seller taps "Yes, still available" on a stale-listing reminder.
  const handleBumpListing = async (listingId: string) => {
    if (!currentUser) return;
    try {
      await updateDoc(doc(db, 'listings', listingId), { bumpedAt: serverTimestamp() });
    } catch (err) {
      console.error('Failed to bump listing', err);
      alert('Could not refresh the listing. Please try again.');
    }
  };

  const handleMakeOffer = async (listing: Listing, amount: number, message: string) => {
    if (!currentUser) { handlePromptLogin(); throw new Error('Please log in to make an offer.'); }
    if (listing.seller.id === currentUser.id) throw new Error("You can't make an offer on your own listing.");
    const existing = listingOffers.find((o) => o.listingId === listing.id && o.buyerId === currentUser.id && o.status === 'pending');
    if (existing) throw new Error('You already have a pending offer on this item.');
    await addDoc(collection(db, 'offers'), {
      listingId: listing.id,
      listingTitle: listing.title,
      listingPhoto: listing.photos[0] || '',
      buyerId: currentUser.id,
      buyerUsername: currentUser.username,
      buyerAvatarUrl: currentUser.avatarUrl,
      sellerId: listing.seller.id,
      amount,
      message: censorInappropriateText(cleanText(message, TEXT_LIMITS.offer)),
      status: 'pending',
      createdAt: serverTimestamp(),
    });
  };

  const handleRespondToOffer = async (offerId: string, status: 'accepted' | 'declined' | 'withdrawn') => {
    if (!currentUser) return;
    try {
      await updateDoc(doc(db, 'offers', offerId), { status });
    } catch (err) {
      console.error('Failed to update offer', err);
      alert('Could not update the offer. Please try again.');
    }
  };

  // Anonymous review after a confirmed sale - role is derived from who the
  // current user is on the listing; firestore.rules re-verifies it.
  const handleSubmitReview = async (listing: Listing, stars: number, comment: string) => {
    if (!currentUser) { handlePromptLogin(); throw new Error('Please log in to leave a review.'); }
    const isSeller = listing.seller.id === currentUser.id;
    const isBuyer = listing.soldTo?.id === currentUser.id;
    if (listing.status !== 'sold' || (!isSeller && !isBuyer)) throw new Error('Only the buyer and seller can review a completed sale.');
    const role = isSeller ? 'seller' : 'buyer';
    const subjectId = isSeller ? listing.soldTo?.id : listing.seller.id;
    if (!subjectId) throw new Error('This sale has no buyer to review.');
    await setDoc(doc(db, 'listingReviews', `${listing.id}_${role}`), {
      listingId: listing.id,
      reviewerRole: role,
      subjectId,
      stars: Math.min(5, Math.max(1, Math.round(stars))),
      comment: censorInappropriateText(cleanText(comment, TEXT_LIMITS.review)),
      createdAt: serverTimestamp(),
    });
  };

  const handleToggleSaveListing = async (listingId: string) => {
    if (!currentUser) { handlePromptLogin(); return; }
    const listing = listings.find((l) => l.id === listingId);
    if (!listing) return;
    const isSaved = (listing.savedBy || []).includes(currentUser.id);
    setListings((prev) => prev.map((l) => l.id !== listingId ? l : {
      ...l,
      savedBy: isSaved ? (l.savedBy || []).filter((id) => id !== currentUser.id) : [...(l.savedBy || []), currentUser.id],
    }));
    try {
      await updateDoc(doc(db, 'listings', listingId), { savedBy: isSaved ? arrayRemove(currentUser.id) : arrayUnion(currentUser.id) });
    } catch (err) {
      console.error('Failed to toggle saved listing', err);
    }
  };

  // Seller confirms a sale on the "Sold to" page: marks the listing sold, records
  // who bought it (null = sold outside RIDAZ, so no reviews), and optionally
  // leaves an anonymous review of the buyer.
  const handleConfirmSale = async (
    listing: Listing,
    soldTo: User | null,
    review: { stars: number; comment: string } | null
  ) => {
    if (!currentUser || listing.seller.id !== currentUser.id) return;
    await updateDoc(doc(db, 'listings', listing.id), {
      status: 'sold',
      soldTo: soldTo ? { id: soldTo.id, username: soldTo.username, avatarUrl: soldTo.avatarUrl } : null,
    });
    if (soldTo && review && review.stars >= 1) {
      await setDoc(doc(db, 'listingReviews', `${listing.id}_seller`), {
        listingId: listing.id,
        reviewerRole: 'seller',
        subjectId: soldTo.id,
        stars: Math.min(5, Math.max(1, Math.round(review.stars))),
        comment: censorInappropriateText(cleanText(review.comment, TEXT_LIMITS.review)),
        createdAt: serverTimestamp(),
      });
    }
  };

  const handleRelistListing = async (listingId: string) => {
    if (!currentUser) return;
    try {
      await updateDoc(doc(db, 'listings', listingId), { status: 'active', soldTo: null, bumpedAt: serverTimestamp() });
    } catch (err) {
      console.error('Failed to relist', err);
      alert('Could not relist the item. Please try again.');
    }
  };

  const handleDeleteListing = async (listingId: string) => {
    const listing = listings.find((l) => l.id === listingId);
    const isOwner = !!currentUser && listing?.seller.id === currentUser.id;
    if (!isRidazModerator && !isOwner) return;
    if (!window.confirm('Delete this listing?')) return;
    try {
      await deleteDoc(doc(db, 'listings', listingId));
      setListings((prev) => prev.filter((l) => l.id !== listingId));
    } catch (err) {
      console.error('Failed to delete listing', err);
      alert('Could not delete the listing. Please try again.');
    }
  };

  // "Check in here" on a spot: a small record riders nearby are notified about (functions/index.js onSpotCheckIn).
  const handleCheckInSpot = async (spot: Spot) => {
    if (!currentUser) { handlePromptLogin(); return; }
    await addDoc(collection(db, 'spots', spot.id, 'checkIns'), {
      user: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl || '' },
      at: serverTimestamp(),
    });
  };

  const handleToggleFavoriteSpot = async (spotId: string) => {
    if (!currentUser) { handlePromptLogin(); return; }
    const spot = spots.find((sp) => sp.id === spotId);
    if (!spot) return;
    const isFavorited = (spot.favoritedBy || []).includes(currentUser.id);
    setSpots((prev) => prev.map((sp) => sp.id !== spotId ? sp : {
      ...sp,
      favoritedBy: isFavorited
        ? (sp.favoritedBy || []).filter((id) => id !== currentUser.id)
        : [...(sp.favoritedBy || []), currentUser.id],
    }));
    try {
      await updateDoc(doc(db, 'spots', spotId), {
        favoritedBy: isFavorited ? arrayRemove(currentUser.id) : arrayUnion(currentUser.id),
      });
    } catch (err) {
      console.error('Failed to toggle favourite spot', err);
      alert('Could not update your favourite. Please try again.');
    }
  };

  // "Correct information" submissions for spots/parks/shops, reviewed in the
  // @ridaz Developer dashboard (corrections collection, see firestore.rules).
  const handleSubmitCorrection = async (
    targetType: 'spot' | 'skatepark' | 'shop',
    targetId: string,
    targetName: string,
    text: string
  ) => {
    if (!currentUser) { handlePromptLogin(); throw new Error('Not signed in'); }
    await addDoc(collection(db, 'corrections'), {
      targetType,
      targetId,
      targetName,
      text: censorInappropriateText(cleanText(text, 500)),
      submittedBy: { id: currentUser.id, username: currentUser.username },
      // A correction from the @ridaz account needs no review.
      reviewed: isRidazModerator,
      createdAt: serverTimestamp(),
    });
  };

  const handleAddZipComment = async (zipId: string, text: string, parentId?: string | null) => {
    if (!currentUser) { handlePromptLogin(); return; }
    if (!allowAction('comment', 8, 20000)) { window.alert(TOO_FAST_MESSAGE); return; }
    const moderatedText = censorInappropriateText(cleanText(text, TEXT_LIMITS.comment));
    const commentsRef = collection(db, 'zips', zipId, 'comments');
    await addDoc(commentsRef, {
      user: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
      text: moderatedText,
      timestamp: serverTimestamp(),
      parentId: parentId || null,
    });
  };
  
  const handleSharePost = async (postId: string, recipientId: string) => {
      if (!currentUser) { handlePromptLogin(); return; }
      console.log(`Sharing post ${postId} with user ${recipientId}.`);
      // This is a complex feature. For now, we'll simulate it.
      // A real implementation would involve finding or creating a chat thread
      // and sending a special message type.
  };

  const handleShareZip = async (zipId: string, recipientId: string) => {
      if (!currentUser) { handlePromptLogin(); return; }
      console.log(`Sharing zip ${zipId} with user ${recipientId}.`);
  };

  const handleShareLocation = async (threadIds: string[]) => {
    const runWithTimeout = async <T,>(operation: Promise<T>, timeoutMs: number, errorMessage: string): Promise<T> => {
      let timeoutId: ReturnType<typeof window.setTimeout> | null = null;

      try {
        return await Promise.race([
          operation,
          new Promise<T>((_, reject) => {
            timeoutId = window.setTimeout(() => reject(new Error(errorMessage)), timeoutMs);
          }),
        ]);
      } finally {
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }
      }
    };

    if (!currentUser) {
      handlePromptLogin();
      return false;
    }
    if (!appGeolocation) {
      alert('Location sharing is unavailable on this device.');
      return false;
    }

    const selectedThreads = messageThreads.filter((thread) => threadIds.includes(thread.id));
    if (!selectedThreads.length) {
      alert('Choose at least one chat to share with.');
      return false;
    }

    const currentPosition = deviceLocation || await runWithTimeout(
      new Promise<{ lat: number; lng: number }>((resolve, reject) => {
        appGeolocation.getCurrentPosition(
          (position) => resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }),
          (error) => reject(error),
          {
            enableHighAccuracy: true,
            timeout: 12000,
            maximumAge: 10000,
          }
        );
      }),
      15000,
      'Getting your location took too long.'
    ).catch((error) => {
      console.error('Failed to get current location for share', error);
      alert('Could not get your current location. Check location permissions and try again.');
      return null;
    });

    if (!currentPosition) return false;

    setDeviceLocation(currentPosition);

    try {
      const batch = writeBatch(db);
      activeOwnedLocationShares
        .filter((share) => !threadIds.includes(share.threadId))
        .forEach((share) => {
          batch.set(doc(db, 'liveLocationShares', share.id), {
            isActive: false,
            updatedAt: serverTimestamp(),
            endedAt: serverTimestamp(),
          }, { merge: true });
        });
      await runWithTimeout(batch.commit(), 10000, 'Updating your location shares took too long.');

      await runWithTimeout(Promise.all(selectedThreads.map(async (thread) => {
        const participantIds = Array.isArray(thread.participantIds) && thread.participantIds.length
          ? thread.participantIds
          : (thread.participants || []).map((participant) => participant.id);
        const otherUser = (thread.participants || []).find((participant) => participant.id !== currentUser.id);
        const threadName = thread.isGroup
          ? (thread.groupName || 'Group chat')
          : (otherUser ? `@${otherUser.username}`.toUpperCase() : 'Direct chat');
        const shareId = `${thread.id}_${currentUser.id}`;
        const nowTimestamp = Timestamp.now();
        const existingShare = liveLocationShares.find((share) => share.id === shareId);
        const isFirstShareForThread = !existingShare;

        await setDoc(doc(db, 'liveLocationShares', shareId), {
          threadId: thread.id,
          threadName,
          participantIds: Array.from(new Set(participantIds)),
          ownerId: currentUser.id,
          isGroup: thread.isGroup,
          chatType: getThreadType(thread),
          isActive: true,
          location: currentPosition,
          user: {
            id: currentUser.id,
            username: currentUser.username,
            avatarUrl: currentUser.avatarUrl,
          },
          updatedAt: serverTimestamp(),
          ...(isFirstShareForThread ? { startedAt: serverTimestamp() } : {}),
        }, { merge: true });

        if (isFirstShareForThread) {
          await addDoc(collection(db, 'messageThreads', thread.id, 'messages'), {
            sender: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
            text: 'Shared a live location on the map.',
            locationPayload: {
              shareId,
              ownerId: currentUser.id,
              username: currentUser.username,
              lat: currentPosition.lat,
              lng: currentPosition.lng,
              isLive: true,
              threadName,
            },
            timestamp: nowTimestamp,
          });

          await updateDoc(doc(db, 'messageThreads', thread.id), {
            lastMessage: 'Shared a live location on the map.',
            timestamp: nowTimestamp,
          });
        }
      })), 15000, 'Starting live location sharing took too long.');

      return true;
    } catch (error: any) {
      console.error('Failed to share live location', error);
      alert(`Could not start live location sharing. ${error?.message || 'Unknown error'}`);
      return false;
    }
  };

  const handleStopSharingLocation = async () => {
    if (!currentUser || activeOwnedLocationShares.length === 0) return;

    try {
      const batch = writeBatch(db);
      activeOwnedLocationShares.forEach((share) => {
        batch.set(doc(db, 'liveLocationShares', share.id), {
          isActive: false,
          updatedAt: serverTimestamp(),
          endedAt: serverTimestamp(),
        }, { merge: true });
      });
      await batch.commit();
    } catch (error: any) {
      console.error('Failed to stop shared locations', error);
      alert(`Could not stop location sharing. ${error?.message || 'Unknown error'}`);
    }
  };
  
  const handleAddPost = async ({ mediaFile, mediaFiles, caption, sport, tricks, textOverlay, locationTag, overlays }: { mediaFile?: File; mediaFiles?: File[]; caption: string; sport: Sport; tricks?: string[]; textOverlay?: MediaTextOverlay | null; locationTag?: LocationTag | null; overlays?: (MediaOverlays | null)[] }) => {
    if (!currentUser) { handlePromptLogin(); return; }
    if (!allowAction('upload', 6, 600000)) { window.alert(TOO_FAST_MESSAGE); return; }
    const uploadRateLimit = getUploadRateLimitStatus();
    if (!uploadRateLimit.allowed) {
      const minutes = Math.max(1, Math.ceil(((uploadRateLimit.unblockAtMs || Date.now()) - Date.now()) / 60000));
      alert(`You've hit the upload limit for now. Try again in about ${minutes} minute${minutes === 1 ? '' : 's'}.`);
      return;
    }

    const moderatedCaption = censorInappropriateText(cleanText(caption, TEXT_LIMITS.caption));
    const sanitizedTricks = Array.from(
      new Set(
        (tricks || [])
          .map((trick) => censorInappropriateText(cleanLine(trick, TEXT_LIMITS.trick)))
          .filter(Boolean)
      )
    ).slice(0, 12);

    setPostUploadProgress(0);
    setPostUploadStage('preparing');
    setIsPostUploading(true);
    try {
      const files = (mediaFiles && mediaFiles.length ? mediaFiles : mediaFile ? [mediaFile] : []).slice(0, 10);
      if (!files.length) throw new Error('No media selected.');
      const uploaded: { type: 'image' | 'video'; url: string }[] = [];
      for (const [fileIndex, file] of files.entries()) {
        const fileIsVideo = file.type.startsWith('video/');
        setPostUploadStage('preparing');
        const processedFile = fileIsVideo
          ? await compressAndDownscaleVideo(file)
          : await compressAndDownscaleImage(file);
        const fileExtension = processedFile.name.split('.').pop() || (fileIsVideo ? 'mp4' : 'jpg');
        const storageRef = ref(storage, `posts/${currentUser.id}/${uuidv4()}.${fileExtension}`);
        const metadata = {
          contentType: processedFile.type,
          customMetadata: { uploadedBy: currentUser.id, originalSize: file.size.toString(), compressedSize: processedFile.size.toString() }
        };
        setPostUploadStage('uploading');
        await uploadFileWithTimeout(storageRef, processedFile, metadata, 60000, (fraction) => {
          setPostUploadProgress((fileIndex + fraction) / files.length);
        });
        uploaded.push({ type: fileIsVideo ? 'video' : 'image', url: await getDownloadURL(storageRef) });
      }
      setPostUploadStage('finishing');
      setPostUploadProgress(1);
      const url = uploaded[0].url;
      const isVideo = uploaded[0].type === 'video';

      const mentionUsernames = Array.from(moderatedCaption.matchAll(/@([A-Za-z0-9_]+)/g)).map(m => m[1].toLowerCase());
      const uniqueMentions = Array.from(new Set(mentionUsernames));
      const mentionIds = users
        .filter(u => uniqueMentions.includes(u.username.toLowerCase()))
        .map(u => u.id);

      await addDoc(collection(db, 'posts'), {
        user: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
        imageUrl: url,
        mediaType: isVideo ? 'video' : 'image',
        ...(uploaded.length > 1 ? { mediaItems: uploaded } : {}),
        caption: moderatedCaption,
        tricks: sanitizedTricks,
        sport: { id: sport.id, name: sport.name },
        likedBy: [],
        timestamp: serverTimestamp(),
        tags: mentionIds,
        mentionUsernames: uniqueMentions,
        textOverlay: textOverlay || null,
        ...(overlays && overlays.some(Boolean) ? { overlays: files.map((_, i) => overlays[i] || null) } : {}),
        locationTag: locationTag || null,
      });

      // A post tagged with a spot also lands in that spot's gallery on the map (same already-uploaded media, no
      // re-upload). One write per item: the spots rule only lets non-owners append a single photo at a time.
      if (locationTag?.spotId) {
        for (const item of uploaded.slice(0, 10)) {
          try {
            await updateDoc(doc(db, 'spots', locationTag.spotId), {
              photos: arrayUnion({
                id: uuidv4(),
                url: item.url,
                type: item.type,
                addedBy: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
              }),
            });
          } catch (spotErr) {
            console.warn('Could not add post media to the tagged spot', spotErr);
          }
        }
      }
    } catch (error: any) {
      console.error('Error creating post:', error);
      alert(`Failed to upload post:\n\n${error?.message || 'Unknown error'}\n\nPlease try again.`);
    } finally {
      setIsPostUploading(false);
    }
  };

  const handleAddZip = async ({ videoFile, caption, sport, tricks, locationTag, overlays }: { videoFile: File; caption: string; sport: Sport; tricks?: string[]; locationTag?: LocationTag | null; overlays?: MediaOverlays | null }) => {
    if (!currentUser) { handlePromptLogin(); return; }
    if (!allowAction('upload', 6, 600000)) { window.alert(TOO_FAST_MESSAGE); return; }
    const uploadRateLimit = getUploadRateLimitStatus();
    if (!uploadRateLimit.allowed) {
      const minutes = Math.max(1, Math.ceil(((uploadRateLimit.unblockAtMs || Date.now()) - Date.now()) / 60000));
      throw new Error(`You've hit the upload limit for now. Try again in about ${minutes} minute${minutes === 1 ? '' : 's'}.`);
    }
    if (!videoFile.type.startsWith('video/')) {
      throw new Error('Zips only support video uploads.');
    }

    const { duration: durationSeconds } = await readVideoMetadata(videoFile);

    if (durationSeconds > 180) {
      throw new Error('Zips are limited to 3 minutes.');
    }

    const moderatedCaption = censorInappropriateText(cleanText(caption, TEXT_LIMITS.caption));
    const sanitizedTricks = Array.from(
      new Set(
        (tricks || [])
          .map((trick) => censorInappropriateText(cleanLine(trick, TEXT_LIMITS.trick)))
          .filter(Boolean)
      )
    ).slice(0, 12);

    try {
      const processedFile = await runWithTimeout(
        compressAndDownscaleVideo(videoFile, 1080, 1920, 180),
        20000,
        'Preparing your Zip took too long. Try a smaller video or try again.'
      );
      const fileExtension = processedFile.name.split('.').pop() || 'mp4';
      const storageRef = ref(storage, `zips/${currentUser.id}/${uuidv4()}.${fileExtension}`);
      const uploadMetadata = {
        contentType: processedFile.type,
        customMetadata: {
          uploadedBy: currentUser.id,
          originalSize: videoFile.size.toString(),
          processedSize: processedFile.size.toString(),
          durationSeconds: durationSeconds.toFixed(2),
        }
      };

      await uploadFileWithTimeout(storageRef, processedFile, uploadMetadata, 20000);

      const videoUrl = await runWithTimeout(
        getDownloadURL(storageRef),
        15000,
        'Finishing your Zip upload took too long. Try again.'
      );

      await runWithTimeout(
        addDoc(collection(db, 'zips'), {
          user: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
          videoUrl,
          caption: moderatedCaption,
          tricks: sanitizedTricks,
          sport: { id: sport.id, name: sport.name },
          likedBy: [],
          durationSeconds,
          timestamp: serverTimestamp(),
          locationTag: locationTag || null,
          ...(overlays ? { overlays } : {}),
        }),
        15000,
        'Saving your Zip post took too long. Try again.'
      );
    } catch (error) {
      console.error('Error creating zip:', error);
      if ((error as any)?.code === 'storage/canceled') {
        throw new Error('Uploading your Zip stalled and was canceled. Try again on a stronger connection.');
      }
      throw error;
    }
  };
  
  const handleAddStory = async (item: StoryItem) => {
    if (!currentUser) { handlePromptLogin(); return; }
    try {
      // Upload media to storage if provided
      let finalUrl = item.url;
      if (item.file) {
        const isVideo = item.type === 'video';
        const processedFile = isVideo ? await compressAndDownscaleVideo(item.file) : await convertImageToJpeg(item.file);
        const extFromName = processedFile.name.split('.').pop();
        const extFromType = processedFile.type.split('/')[1];
        const ext = (extFromName && extFromName.length <= 5 ? extFromName : extFromType) || (isVideo ? 'mp4' : 'jpg');
        const storagePath = `stories_media/${currentUser.id}/${item.id}.${ext}`;
        const storageRef = ref(storage, storagePath);
        const metadata = { contentType: processedFile.type, customMetadata: { uploadedBy: currentUser.id } };
        await uploadBytes(storageRef, processedFile, metadata);
        finalUrl = await getDownloadURL(storageRef);
      }

      const sanitizedItem: StoryItem = {
        id: item.id,
        type: item.type,
        url: finalUrl,
        duration: item.duration,
        timestamp: item.timestamp,
        ...(item.overlays ? { overlays: item.overlays } : {})
      };

      // Write to top-level stories collection with 24h expiry
      const storyDocRef = doc(db, 'stories', currentUser.id);
      const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000);
      await setDoc(storyDocRef, {
        user: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
        items: arrayUnion(sanitizedItem),
        expiresAt: Timestamp.fromDate(expiresAt)
      }, { merge: true });

      // Optimistically reflect the new news item locally so it shows up immediately
      setStories(prev => {
        const idx = prev.findIndex(s => s.user.id === currentUser.id);
        const baseUser = idx >= 0 ? prev[idx].user : currentUser;
        const updatedItems = idx >= 0 ? [...prev[idx].items, sanitizedItem] : [sanitizedItem];
        const updatedStory: UserStory = { user: baseUser, items: updatedItems };
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = updatedStory;
          return next;
        }
        return [updatedStory, ...prev];
      });
    } catch (err: any) {
      console.error('Failed to add story/news', err);
      // Re-throw so the News page can show the error inline and keep the user's selection
      // (it used to alert here and then close as if the upload had worked).
      throw err;
    }
  };
  
  const handleDeleteStoryItem = async (ownerId: string, itemId: string) => {
    if (!currentUser || currentUser.id !== ownerId) throw new Error('Not allowed');
    const storyDocRef = doc(db, 'stories', ownerId);
    const snap = await getDoc(storyDocRef);
    const existing: StoryItem[] = snap.exists() ? ((snap.data() as any).items || []) : [];
    const target = existing.find(i => i.id === itemId);
    const remaining = existing.filter(i => i.id !== itemId);
    if (remaining.length === 0) await deleteDoc(storyDocRef);
    else await updateDoc(storyDocRef, { items: remaining });
    if (target?.url) {
      try { await deleteObject(ref(storage, target.url)); } catch (e) { console.warn('Could not delete news media', e); }
    }
    setStories(prev => prev
      .map(s => s.user.id === ownerId ? { ...s, items: s.items.filter(i => i.id !== itemId) } : s)
      .filter(s => s.items.length > 0 || s.user.id !== ownerId));
    // Land on the home page once the viewer closes.
    newsDeletedGoHomeRef.current = true;
    const feedNav = NAV_ITEMS.find(item => item.id === 'feed');
    if (feedNav) handleNavChange(feedNav);
  };

  const handleViewProfile = (user: User) => {
    if (isGuest) {
      handlePromptLogin();
      return;
    }
    const fullUser = users.find(u => u.id === user.id) || user;
    setViewedUser(fullUser);
    // Already on the profile tab (e.g. viewing a follower from your own
    // followers list) - no nav change needed, and calling handleNavChange
    // here would trigger its own "re-tapping profile resets to your own
    // profile" cleanup, wiping the viewedUser we just set above.
    if (activeNav.id !== 'profile') {
      const profileNav = NAV_ITEMS.find((item) => item.id === 'profile');
      if (profileNav) {
        handleNavChange(profileNav);
      }
    }
  };

  const handleViewProfileOverlay = (user: User) => {
    if (isGuest) { handlePromptLogin(); return; }
    const fullUser = users.find(u => u.id === user.id) || user;
    setProfileOverlay(fullUser);
  };

  const handleBlockUser = async (userId: string) => {
    if (!currentUser) return;
    await updateDoc(doc(db, 'users', currentUser.id), { blocked: arrayUnion(userId) });
  };

  const handleUnblockUser = async (userId: string) => {
    if (!currentUser) return;
    await updateDoc(doc(db, 'users', currentUser.id), { blocked: arrayRemove(userId) });
  };

  const handleReportUser = async (userId: string) => {
    if (!currentUser) { handlePromptLogin(); return; }
    await addDoc(collection(db, 'reports'), {
      reporterId: currentUser.id,
      reportedUserId: userId,
      timestamp: serverTimestamp(),
    });
  };

  // General-purpose report from Settings, not tied to a specific user/post -
  // e.g. objectionable content the reporter doesn't have a more specific
  // flag flow for yet. Feeds the same `reports` collection so moderation
  // review happens in one place.
  const handleReportProblem = async (description: string) => {
    if (!currentUser) { handlePromptLogin(); return; }
    if (!allowAction('report', 3, 60000)) { window.alert(TOO_FAST_MESSAGE); return; }
    description = cleanText(description, TEXT_LIMITS.report);
    if (!description) return;
    await addDoc(collection(db, 'reports'), {
      reporterId: currentUser.id,
      type: 'general',
      description,
      timestamp: serverTimestamp(),
    });
  };

  // Structured report against a specific piece of content, feeding the
  // Developer dashboard's Posts & Zips / Comments / Spots categories. For a
  // comment, parentType/parentId locate it (posts/zips/spots + that
  // document's id), since comments live in a subcollection under their post.
  const handleReportContent = async (
    type: 'post' | 'zip' | 'comment' | 'spot' | 'sesh' | 'event' | 'listing',
    targetId: string,
    extra?: { parentType?: 'posts' | 'zips' | 'spots'; parentId?: string }
  ) => {
    if (!currentUser) { handlePromptLogin(); return; }
    if (!allowAction('report', 3, 60000)) { window.alert(TOO_FAST_MESSAGE); return; }
    await addDoc(collection(db, 'reports'), {
      reporterId: currentUser.id,
      type,
      targetId,
      ...(extra || {}),
      timestamp: serverTimestamp(),
    });
  };

  // --- Moderation (the @ridaz account only - see isRidazModerator/isModerator) ---

  const handleBanUser = async (userId: string) => {
    if (!isRidazModerator) return;
    if (!window.confirm('Permanently ban this user? They will be immediately signed out and unable to use RIDAZ again.')) return;
    try {
      await updateDoc(doc(db, 'users', userId), { isBanned: true, bannedAt: serverTimestamp() });
    } catch (err) {
      console.error('Error banning user:', err);
      alert('Failed to ban user. Please try again.');
    }
  };

  // Profile > ... menu > Ban User & Erase (the @ridaz account only). Unlike handleBanUser (a reversible flag), this bans for good,
  // disables the login and erases everything the person posted. Returns true when it finished.
  const handleBanAndEraseUser = async (userId: string): Promise<boolean> => {
    if (!isRidazModerator) return false;
    const target = users.find((u) => u.id === userId);
    const name = target?.username ? `@${target.username}` : 'this user';
    if (!window.confirm(`Ban ${name} and ERASE ALL of their content?\n\nThis removes their posts, Zips, news, map pins, comments, likes, messages, listings, photos and profile, and they can never sign in again.\n\nThis cannot be undone.`)) return false;
    if (!window.confirm(`Last check: permanently ban ${name} and erase everything they ever posted?`)) return false;
    try {
      await banUserAndEraseCallable({ userId });
      alert(`${name} was banned and all of their content was erased.`);
      return true;
    } catch (err: any) {
      console.error('Error banning and erasing user:', err);
      alert(`Could not finish banning ${name}:\n\n${err?.message || 'Unknown error'}\n\nThey may already be banned. Try again to finish erasing.`);
      return false;
    }
  };

  const handleUnbanUser = async (userId: string) => {
    if (!isRidazModerator) return;
    if (!window.confirm('Unban this user? They will be able to sign in again.')) return;
    try {
      await updateDoc(doc(db, 'users', userId), { isBanned: false, bannedAt: null });
    } catch (err) {
      console.error('Error unbanning user:', err);
      alert('Failed to unban user. Please try again.');
    }
  };

  const handleModDeleteComment = async (parentCollection: 'posts' | 'zips' | 'spots', parentId: string, commentId: string) => {
    if (!isRidazModerator) return;
    if (!window.confirm('Delete this comment?')) return;
    try {
      await deleteDoc(doc(db, parentCollection, parentId, 'comments', commentId));
    } catch (err) {
      console.error('Error deleting comment:', err);
      alert('Failed to delete comment. Please try again.');
    }
  };

  const handleModDeleteZip = async (zipId: string) => {
    if (!isRidazModerator) return;
    if (!window.confirm('Take down this Zip?')) return;
    try {
      await deleteDoc(doc(db, 'zips', zipId));
      setZips(prev => prev.filter(z => z.id !== zipId));
    } catch (err) {
      console.error('Error deleting zip:', err);
      alert('Failed to delete Zip. Please try again.');
    }
  };

  // A rider deleting their own zip (the firestore rules already allow the owner to delete it).
  const handleDeleteOwnZip = async (zipId: string) => {
    const target = zips.find((z) => z.id === zipId);
    if (!currentUser || !target || target.user.id !== currentUser.id) return;
    try {
      await deleteDoc(doc(db, 'zips', zipId));
      setZips((prev) => prev.filter((z) => z.id !== zipId));
    } catch (err) {
      console.error('Error deleting zip:', err);
      alert('Failed to delete your Zip. Please try again.');
    }
  };

  const handleModDeleteSpot = async (spotId: string) => {
    const spot = spots.find(s => s.id === spotId);
    const isOwner = !!currentUser && spot?.addedBy?.id === currentUser.id;
    if (!isRidazModerator && !isOwner) return;
    if (!window.confirm('Delete this spot?')) return;
    try {
      await deleteDoc(doc(db, 'spots', spotId));
      setSpots(prev => prev.filter(s => s.id !== spotId));
    } catch (err) {
      console.error('Error deleting spot:', err);
      alert('Failed to delete spot. Please try again.');
    }
  };

  const handleModDeleteEvent = async (eventId: string) => {
    const event = events.find(e => e.id === eventId);
    const isOwner = !!currentUser && event?.createdBy?.id === currentUser.id;
    if (!isRidazModerator && !isOwner) return;
    if (!window.confirm('Delete this event?')) return;
    try {
      await deleteDoc(doc(db, 'events', eventId));
      setEvents(prev => prev.filter(e => e.id !== eventId));
    } catch (err) {
      console.error('Error deleting event:', err);
      alert('Failed to delete event. Please try again.');
    }
  };

  const handleModDeleteJamSesh = async (seshId: string) => {
    const jamSesh = jamSeshes.find(j => j.id === seshId);
    const isOwner = !!currentUser && jamSesh?.createdBy?.id === currentUser.id;
    if (!isRidazModerator && !isOwner) return;
    if (!window.confirm('Delete this sesh?')) return;
    try {
      await deleteDoc(doc(db, 'jam_seshes', seshId));
      setJamSeshes(prev => prev.filter(s => s.id !== seshId));
    } catch (err) {
      console.error('Error deleting sesh:', err);
      alert('Failed to delete sesh. Please try again.');
    }
  };

  const handleModDeleteShop = async (shopId: string) => {
    const shop = shops.find(s => s.id === shopId);
    const isOwner = !!currentUser && shop?.addedBy?.id === currentUser.id;
    if (!isRidazModerator && !isOwner) return;
    if (!window.confirm('Delete this shop?')) return;
    try {
      await deleteDoc(doc(db, 'shops', shopId));
      setShops(prev => prev.filter(s => s.id !== shopId));
    } catch (err) {
      console.error('Error deleting shop:', err);
      alert('Failed to delete shop. Please try again.');
    }
  };

  // Marks a Shop/Park submission as reviewed in the Developer Dashboard's
  // "Shop Submits"/"Park Submits" queues - the submission is already live
  // on the map either way, this just clears it from @ridaz's review list.
  const handleAcceptShopSubmission = async (shopId: string) => {
    if (!isRidazModerator) return;
    try {
      await updateDoc(doc(db, 'shops', shopId), { reviewed: true });
      setShops(prev => prev.map(s => (s.id === shopId ? { ...s, reviewed: true } : s)));
    } catch (err) {
      console.error('Error accepting shop submission:', err);
    }
  };

  const handleAcceptParkSubmission = async (spotId: string) => {
    if (!isRidazModerator) return;
    try {
      await updateDoc(doc(db, 'spots', spotId), { reviewed: true });
      setSpots(prev => prev.map(s => (s.id === spotId ? { ...s, reviewed: true } : s)));
    } catch (err) {
      console.error('Error accepting park submission:', err);
    }
  };

  const handleToggleFollow = async (userIdToFollow: string) => {
    if (!currentUser) { handlePromptLogin(); return; }
    try {
      console.log('Toggling follow for user:', userIdToFollow);
      const currentUserRef = doc(db, 'users', currentUser.id);
      const userToFollowRef = doc(db, 'users', userIdToFollow);

      const isFollowing = currentUser.following?.includes(userIdToFollow);
      console.log('Currently following:', isFollowing);

      const batch = writeBatch(db);
      if(isFollowing) {
          batch.update(currentUserRef, { following: arrayRemove(userIdToFollow) });
          batch.update(userToFollowRef, { followers: arrayRemove(currentUser.id) });
      } else {
          batch.update(currentUserRef, { following: arrayUnion(userIdToFollow) });
          batch.update(userToFollowRef, { followers: arrayUnion(currentUser.id) });
      }
      await batch.commit();
      console.log('Follow toggle successful');
    } catch (error) {
      console.error('Error toggling follow:', error);
      alert('Failed to update follow status. Check console for details.');
    }
  };
  
  const handleUpdateUser = async (updatedData: Partial<User>, avatarFile?: File, coverFile?: File) => {
    if (!currentUser) { handlePromptLogin(); return; }

    try {
      const sanitizedUserData: Partial<User> = { ...updatedData };
      if (typeof sanitizedUserData.firstName === 'string') sanitizedUserData.firstName = cleanLine(sanitizedUserData.firstName, TEXT_LIMITS.name);
      if (typeof sanitizedUserData.lastName === 'string') sanitizedUserData.lastName = cleanLine(sanitizedUserData.lastName, TEXT_LIMITS.name);
      if (typeof sanitizedUserData.username === 'string') {
        const trimmedUsername = sanitizedUserData.username.trim();
        if (!trimmedUsername) {
          throw new Error('Username cannot be empty.');
        }
        if (!isValidUsernameFormat(trimmedUsername)) {
          throw new Error('Username can only contain letters, numbers, underscores, and periods.');
        }
        if (hasInappropriateUsername(trimmedUsername)) {
          throw new Error('Username contains inappropriate language. Choose a different username.');
        }
        const usernameChanged = trimmedUsername.toLowerCase() !== currentUser.username.toLowerCase();
        if (usernameChanged) {
          const usersSnapshot = await getDocs(collection(db, 'users'));
          const isTaken = usersSnapshot.docs.some((docSnapshot) => (
            docSnapshot.id !== currentUser.id &&
            String(docSnapshot.data().username || '').trim().toLowerCase() === trimmedUsername.toLowerCase()
          ));
          if (isTaken) {
            throw new Error('This name is taken');
          }
        }
        // Usernames are stored lowercase so display and lookups (login,
        // mentions, uniqueness) are inherently case-insensitive everywhere,
        // rather than lowercasing at every read/comparison site.
        sanitizedUserData.username = trimmedUsername.toLowerCase();
      }
      if (typeof sanitizedUserData.bio === 'string') {
        sanitizedUserData.bio = censorInappropriateText(cleanText(sanitizedUserData.bio, TEXT_LIMITS.bio));
      }

      const userRef = doc(db, 'users', currentUser.id);
      const getExt = (file?: File) => {
        if (!file) return 'jpg';
        const fromName = file.name.split('.').pop();
        if (fromName && fromName.length <= 5) return fromName.toLowerCase();
        const fromType = file.type.split('/')[1];
        return fromType || 'jpg';
      };

      if (avatarFile) {
        try {
          console.log('Uploading avatar image...');
          const convertedAvatar = await convertImageToJpeg(avatarFile);
          const avatarExt = getExt(convertedAvatar);
          const avatarRef = ref(storage, `avatars/${currentUser.id}.${avatarExt}`);
          const metadata = {
            contentType: convertedAvatar.type,
            customMetadata: {
              uploadedBy: currentUser.id
            }
          };
          await uploadBytes(avatarRef, convertedAvatar, metadata);
          sanitizedUserData.avatarUrl = await getDownloadURL(avatarRef);
          console.log('Avatar uploaded successfully:', sanitizedUserData.avatarUrl);
        } catch (error: any) {
          console.error('Avatar upload failed:', error);
          throw new Error(`Avatar upload failed: ${error?.message || 'Unknown error'}`);
        }
      }
      if (coverFile) {
        try {
          console.log('Uploading cover image from device...', {
            fileName: coverFile.name,
            fileSize: coverFile.size,
            fileType: coverFile.type
          });
          // Videos go up as-is (the editor already capped their length and size); images are normalised to JPEG.
          const convertedCover = coverFile.type.startsWith('video/') ? coverFile : await convertImageToJpeg(coverFile);
          const coverExt = coverFile.type.startsWith('video/') ? (getExt(convertedCover) || 'mp4') : getExt(convertedCover);
          const coverRef = ref(storage, `covers/${currentUser.id}.${coverExt}`);
          const coverSettings = sanitizedUserData.coverVideoSettings;
          const metadata = {
            contentType: convertedCover.type,
            customMetadata: {
              uploadedBy: currentUser.id,
              // A cover video is uploaded untouched; the server (functions/index.js) trims, crops and shrinks it to
              // exactly what the cover editor showed, so profiles load a small clip instead of the full original.
              ...(coverFile.type.startsWith('video/') && coverSettings
                ? {
                    coverTrimStart: String(coverSettings.start),
                    coverTrimEnd: String(coverSettings.end),
                    coverZoom: String(coverSettings.zoom),
                    coverTx: String(coverSettings.tx),
                    coverTy: String(coverSettings.ty),
                    coverAspect: String(coverSettings.aspect ?? ''),
                    coverMode: coverSettings.mode || 'loop',
                  }
                : {}),
            }
          };
          console.log('Starting Firebase Storage upload...');
          const uploadResult = await uploadBytes(coverRef, convertedCover, metadata);
          console.log('Upload result:', uploadResult);

          if (coverFile.type.startsWith('video/')) {
            // A cover video is trimmed, framed and shrunk by the server (it can take a little while). Until that is done the
            // profile keeps showing the current cover with an "Updating cover" note; the server then swaps in the finished
            // clip - only the part you kept - so nobody ever plays the raw, untrimmed upload.
            delete (sanitizedUserData as any).coverIsVideo;
            delete (sanitizedUserData as any).coverVideoSettings;
            (sanitizedUserData as any).coverProcessing = Date.now();
            console.log('Cover video uploaded; waiting for the server to process it.');
          } else {
            console.log('Getting download URL...');
            sanitizedUserData.coverPhotoUrl = await getDownloadURL(coverRef);
            console.log('Cover uploaded successfully! URL:', sanitizedUserData.coverPhotoUrl);
          }
        } catch (error: any) {
          console.error('Cover photo upload failed:', error);
          throw new Error(`Cover photo upload failed: ${error?.message || 'Unknown error'}`);
        }
      }

      // Prepare data for Firestore (remove icon functions from sports)
      const firestoreData = { ...sanitizedUserData };
      if (firestoreData.sports) {
          firestoreData.sports = firestoreData.sports.map(s => ({ id: s.id, name: s.name })) as any;
      }

      console.log('Updating Firestore with:', firestoreData);
      await updateDoc(userRef, firestoreData);
      console.log('Profile updated successfully in Firestore');

      // Verify the update by reading back from Firestore
      const updatedDoc = await getDoc(userRef);
      const verifiedData = updatedDoc.data();
      console.log('Verified Firestore data:', verifiedData);

      if (coverFile && !coverFile.type.startsWith('video/') && !verifiedData?.coverPhotoUrl) {
        console.error('ERROR: Cover photo URL was NOT saved to Firestore!');
        alert('Cover photo upload failed - URL not saved to database');
        return;
      }

      // Optimistically update local state with full Sport objects (with icons)
      const updatedUser = {...currentUser, ...sanitizedUserData};
      setCurrentUser(updatedUser);

      // If viewing own profile, also update viewedUser
      if (viewedUser && viewedUser.id === currentUser.id) {
        setViewedUser(updatedUser);
      }

      // Update avatar across message threads participants so everyone sees the new icon
      if (sanitizedUserData.avatarUrl) {
        try {
          const threadsSnap = await getDocs(query(collection(db, 'messageThreads'), where('participantIds', 'array-contains', currentUser.id)));
          let batch = writeBatch(db);
          let ops = 0;
          const commits: Promise<any>[] = [];

          threadsSnap.docs.forEach((docSnap, idx) => {
            const data = docSnap.data();
            const participants = (data.participants || []).map((p: any) => p?.id === currentUser.id ? { ...p, avatarUrl: sanitizedUserData.avatarUrl, username: updatedUser.username } : p);
            batch.update(docSnap.ref, { participants });
            ops += 1;
            if (ops === 400) { // safety to avoid oversize batch
              commits.push(batch.commit());
              batch = writeBatch(db);
              ops = 0;
            }
          });
          if (ops > 0) commits.push(batch.commit());
          await Promise.all(commits);
        } catch (err) {
          console.error('Failed to propagate avatar to threads', err);
        }

        // Also update avatar in posts authored by the user so Feed reflects the new icon
        try {
          const postsSnap = await getDocs(query(collection(db, 'posts'), where('user.id', '==', currentUser.id)));
          let batch = writeBatch(db);
          let ops = 0;
          const commits: Promise<any>[] = [];

          postsSnap.docs.forEach((docSnap) => {
            const data = docSnap.data();
            const userField = data.user || {};
            batch.update(docSnap.ref, { user: { ...userField, avatarUrl: sanitizedUserData.avatarUrl, username: updatedUser.username } });
            ops += 1;
            if (ops === 400) {
              commits.push(batch.commit());
              batch = writeBatch(db);
              ops = 0;
            }
          });
          if (ops > 0) commits.push(batch.commit());
          await Promise.all(commits);
        } catch (err) {
          console.error('Failed to propagate avatar to posts', err);
        }

        // Optionally update stories owner avatar
        try {
          const storiesSnap = await getDocs(query(collection(db, 'stories'), where('user.id', '==', currentUser.id)));
          let batch = writeBatch(db);
          let ops = 0;
          const commits: Promise<any>[] = [];
          storiesSnap.docs.forEach((docSnap) => {
            const data = docSnap.data();
            batch.update(docSnap.ref, { user: { ...(data.user || {}), avatarUrl: sanitizedUserData.avatarUrl, username: updatedUser.username } });
            ops += 1;
            if (ops === 400) {
              commits.push(batch.commit());
              batch = writeBatch(db);
              ops = 0;
            }
          });
          if (ops > 0) commits.push(batch.commit());
          await Promise.all(commits);
        } catch (err) {
          console.error('Failed to propagate avatar to stories', err);
        }
      }
    } catch (error: any) {
      console.error('Error updating user profile:', error);
      const errorMessage = error?.message || 'Unknown error occurred';
      alert(`Failed to update profile:\n\n${errorMessage}\n\nPlease try again or use a different file.`);
    }
  };

  const handleCompleteGoogleOnboarding = async ({
    firstName,
    lastName,
    username,
    avatarFile,
    coverFile,
  }: {
    firstName: string;
    lastName: string;
    username: string;
    avatarFile?: File;
    coverFile?: File;
  }) => {
    if (!currentUser) return;
    await handleUpdateUser({
      firstName,
      lastName,
      username,
      needsGoogleOnboarding: false,
    }, avatarFile, coverFile);
    setViewedUser(null);
    handleNavChange(profileNav);
  };

  const handleCompleteWelcomeOnboarding = async ({
    bio,
    avatarFile,
    coverFile,
  }: {
    bio: string;
    avatarFile?: File;
    coverFile?: File;
  }) => {
    if (!currentUser) return;
    await handleUpdateUser({
      bio: bio || currentUser.bio,
      needsWelcomeOnboarding: false,
    }, avatarFile, coverFile);
  };

  const handleTyping = async (threadId: string) => {
    if (!currentUser) return;
    try {
      await updateDoc(doc(db, 'messageThreads', threadId), {
        [`typingUsers.${currentUser.id}`]: Date.now()
      });
    } catch (error) {
      console.error('Error updating typing status:', error);
    }
  };

  const handleStopTyping = async (threadId: string) => {
    if (!currentUser) return;
    try {
      await updateDoc(doc(db, 'messageThreads', threadId), {
        [`typingUsers.${currentUser.id}`]: deleteField()
      });
    } catch (error) {
      console.error('Error clearing typing status:', error);
    }
  };

  const handleAddMessage = async (threadId: string, text: string, file?: File, clientId?: string, postId?: string, replyTo?: MessageReplyPreview, gifUrl?: string) => {
    if (!currentUser) { handlePromptLogin(); return; }
    if (!threadId) { alert('No conversation selected.'); return; }
    try {
      const moderatedText = censorInappropriateText(cleanText(text, TEXT_LIMITS.message));
      const threadRef = doc(db, 'messageThreads', threadId);
      const threadDoc = await getDoc(threadRef);
      let threadData = threadDoc.data();

      // If the thread doc somehow got removed, recreate a minimal shell so sends don't fail
      if (!threadDoc.exists()) {
        await setDoc(threadRef, {
          participantIds: [currentUser.id],
          participants: [{ id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl }],
          isGroup: false,
          chatType: 'direct',
          lastMessage: '',
          timestamp: serverTimestamp(),
          unreadCounts: { [currentUser.id]: 0 }
        }, { merge: true });
        threadData = { participantIds: [currentUser.id], unreadCounts: { [currentUser.id]: 0 }, typingUsers: {} };
      }

      const derivedParticipantIds = Array.isArray(threadData?.participantIds)
        ? threadData.participantIds
        : Array.isArray(threadData?.participants)
          ? threadData.participants.map((participant: any) => participant?.id || participant?.userId || participant?.uid).filter(Boolean)
          : [];
      const participantIds = Array.from(new Set([currentUser.id, ...derivedParticipantIds]));
      const canonicalParticipants = Array.isArray(threadData?.participants)
        ? threadData.participants
        : (selectedThread?.id === threadId ? selectedThread.participants : fallbackThread?.id === threadId ? fallbackThread.participants : []) || [];
      const normalizedUnreadCounts = { ...(threadData?.unreadCounts || {}) };
      participantIds.forEach((participantId: string) => {
        if (typeof normalizedUnreadCounts[participantId] !== 'number') {
          normalizedUnreadCounts[participantId] = 0;
        }
      });

      await setDoc(threadRef, {
        participantIds,
        participants: canonicalParticipants,
        unreadCounts: normalizedUnreadCounts,
      }, { merge: true });

      // Increment unread count for all participants except the sender
      const unreadCounts: { [key: string]: number } = {};
      participantIds.forEach((participantId: string) => {
        if (participantId !== currentUser.id) {
          const currentCount = normalizedUnreadCounts[participantId] || 0;
          unreadCounts[`unreadCounts.${participantId}`] = currentCount + 1;
        }
      });

      // Clear typing status when sending message
      const typingUsers = threadData?.typingUsers || {};
      delete typingUsers[currentUser.id];

      const nowTimestamp = Timestamp.now();

      const messagesRef = collection(db, 'messageThreads', threadId, 'messages');
      const messageData: any = {
        sender: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
        timestamp: nowTimestamp,
      };
      if (clientId) {
        messageData.clientId = clientId;
      }
      if (replyTo) {
        messageData.replyTo = replyTo;
      }
      let lastMessagePreview = moderatedText;

      if (moderatedText) {
        messageData.text = moderatedText;
      }

      if (gifUrl && !file) {
        // GIFs come straight from GIPHY's own CDN URL - no Storage upload
        // needed, so this bypasses the file-processing branch below entirely.
        messageData.imageUrl = gifUrl;
        if (!lastMessagePreview) {
          lastMessagePreview = 'Sent a GIF';
        }
      }

      if (file) {
        const isVideo = file.type.startsWith('video/');
        const isAudio = file.type.startsWith('audio/');
        // Voice messages are sent as-is (no re-encoding/compression pass -
        // compressAndDownscaleVideo/convertImageToJpeg are built for the
        // other two media kinds and would either fail or mangle audio).
        const processedFile = isAudio ? file : isVideo ? await compressAndDownscaleVideo(file) : await convertImageToJpeg(file);
        const originalExtension = processedFile.name.split('.').pop() || (isVideo ? 'mp4' : isAudio ? 'webm' : 'jpg');
        const fileExtension = originalExtension.toLowerCase();
        const storageRef = ref(storage, `chat_media/${threadId}/${uuidv4()}.${fileExtension}`);
        await uploadBytes(storageRef, processedFile, { contentType: processedFile.type });
        const mediaUrl = await getDownloadURL(storageRef);

        if (isVideo) {
          messageData.videoUrl = mediaUrl;
        } else if (isAudio) {
          messageData.audioUrl = mediaUrl;
        } else {
          messageData.imageUrl = mediaUrl;
        }

        if (!lastMessagePreview) {
          lastMessagePreview = isVideo ? 'Sent a video' : isAudio ? 'Sent a voice message' : 'Sent a photo';
        }
      }

      await addDoc(messagesRef, messageData);

      await updateDoc(threadRef, {
          lastMessage: lastMessagePreview,
          timestamp: nowTimestamp,
          typingUsers,
          ...unreadCounts
      });

      // Optimistically update local thread list so Messenger reflects the new message immediately
      setMessageThreads(prev => {
        const existingIdx = prev.findIndex(t => t.id === threadId);
        const baseThread = existingIdx !== -1 ? prev[existingIdx] : {
          id: threadId,
          participantIds,
          participants: canonicalParticipants,
          isGroup: threadData?.isGroup || false,
          chatType: getThreadType(threadData as Partial<MessageThread>),
          unreadCounts: normalizedUnreadCounts,
          typingUsers: threadData?.typingUsers || {},
          timestamp: nowTimestamp.toDate().toLocaleString(),
          timestampMs: nowTimestamp.toDate().getTime(),
          lastMessage: ''
        } as MessageThread;

        const updated: MessageThread = {
          ...baseThread,
          unreadCounts: { ...baseThread.unreadCounts, ...unreadCounts },
          typingUsers,
          lastMessage: lastMessagePreview,
          timestamp: nowTimestamp.toDate().toLocaleString(),
          timestampMs: nowTimestamp.toDate().getTime()
        };

        if (existingIdx !== -1) {
          const clone = [...prev];
          clone[existingIdx] = updated;
          return clone;
        }
        return [updated, ...prev];
      });
    } catch (err: any) {
      console.error('Failed to send message', err);
      alert(`Message failed to send. ${err?.message || 'Unknown error'}`);
    }
  };

  const handleReactToMessage = async (threadId: string, messageId: string, emoji: string) => {
    if (!currentUser) { handlePromptLogin(); return; }
    try {
      const messageRef = doc(db, 'messageThreads', threadId, 'messages', messageId);
      const messageSnap = await getDoc(messageRef);
      const existingReactions = messageSnap.data()?.reactions || {};
      // Tapping your own already-picked emoji again clears it; otherwise it
      // replaces whatever you'd previously picked - one reaction per user.
      const isRemoving = existingReactions[currentUser.id] === emoji;
      await updateDoc(messageRef, {
        [`reactions.${currentUser.id}`]: isRemoving ? deleteField() : emoji,
      });
    } catch (err) {
      console.error('Failed to react to message:', err);
    }
  };

  // Shop "Contact seller": one chat per listing + buyer. A new chat opens with an
  // "I'm interested" message carrying a preview card of the listing; an existing
  // one is simply reopened.
  const handleContactSeller = async (listing: Listing) => {
    if (!currentUser) { handlePromptLogin(); return; }
    if (listing.seller.id === currentUser.id) return;
    const sellerUser = users.find((u) => u.id === listing.seller.id);
    if ((currentUser.blocked || []).includes(listing.seller.id) || (sellerUser?.blocked || []).includes(currentUser.id)) {
      alert("You can't contact this seller.");
      return;
    }
    const participantIds = [currentUser.id, listing.seller.id].sort();
    const canonicalParticipants = [
      { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
      { id: listing.seller.id, username: listing.seller.username, avatarUrl: listing.seller.avatarUrl },
    ];
    const listingMeta = { listingId: listing.id, listingTitle: listing.title, listingPhoto: listing.photos[0] || '' };
    const existing = messageThreads.find((t) => t.listingId === listing.id && !t.isGroup && participantIds.every((id) => t.participantIds?.includes(id)));
    if (existing) { openThread(existing.id); setShopChatsOpen(true); return; }

    const tempThreadId = `pending_${uuidv4()}`;
    setFallbackThread({
      id: tempThreadId, participants: canonicalParticipants, participantIds, isGroup: false, chatType: 'direct',
      ...listingMeta, lastMessage: '', timestamp: new Date().toLocaleString(), timestampMs: Date.now(),
      unreadCount: 0, unreadCounts: { [currentUser.id]: 0, [listing.seller.id]: 0 }, typingUsers: {},
    } as MessageThread);
    openThread(tempThreadId);
    setShopChatsOpen(true);

    try {
      // Re-check Firestore in case the local thread list hasn't loaded yet.
      const snap = await getDocs(query(collection(db, 'messageThreads'), where('participantIds', 'array-contains', currentUser.id)));
      const found = snap.docs.find((d) => (d.data() as any).listingId === listing.id && (d.data() as any).participantIds?.includes(listing.seller.id));
      if (found) {
        setSelectedThreadId(found.id);
        setFallbackThread({ id: found.id, ...found.data(), timestamp: new Date().toLocaleString(), timestampMs: Date.now() } as MessageThread);
        return;
      }
      const text = "I'm interested";
      const nowTimestamp = Timestamp.now();
      const threadRef = await addDoc(collection(db, 'messageThreads'), {
        participantIds,
        participants: canonicalParticipants,
        isGroup: false,
        chatType: 'direct',
        ...listingMeta,
        lastMessage: text,
        timestamp: nowTimestamp,
        unreadCount: 0,
        unreadCounts: { [currentUser.id]: 0, [listing.seller.id]: 1 },
        typingUsers: {},
      });
      await addDoc(collection(db, 'messageThreads', threadRef.id, 'messages'), {
        sender: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
        text,
        listingPayload: {
          listingId: listing.id,
          title: listing.title,
          price: listing.price,
          photo: listing.photos[0] || '',
          sellerId: listing.seller.id,
        },
        timestamp: nowTimestamp,
      });
      setSelectedThreadId(threadRef.id);
      setFallbackThread({
        id: threadRef.id, participants: canonicalParticipants, participantIds, isGroup: false, chatType: 'direct',
        ...listingMeta, lastMessage: text, timestamp: nowTimestamp.toDate().toLocaleString(), timestampMs: nowTimestamp.toDate().getTime(),
        unreadCount: 0, unreadCounts: { [currentUser.id]: 0, [listing.seller.id]: 1 }, typingUsers: {},
      } as MessageThread);
    } catch (err: any) {
      console.error('Failed to contact seller', err);
      setSelectedThreadId((prev: string | null) => (prev === tempThreadId ? null : prev));
      setFallbackThread((prev: MessageThread | null) => (prev?.id === tempThreadId ? null : prev));
      alert(`Could not contact the seller. ${err?.message || 'Unknown error'}`);
    }
  };

  // Reply to someone's news (swipe up in the news viewer): a direct message to whoever posted it.
  const handleReplyToNews = async (ownerId: string, item: StoryItem, text: string) => {
    if (!currentUser) { handlePromptLogin(); throw new Error('Not signed in'); }
    const owner = users.find((u) => u.id === ownerId);
    if (!owner) throw new Error('Could not find that user.');
    if ((currentUser.blocked || []).includes(ownerId) || (owner.blocked || []).includes(currentUser.id)) throw new Error('You cannot message this user.');
    const moderated = censorInappropriateText(cleanText(text, TEXT_LIMITS.message));
    if (!moderated) return;
    const participantIds = [currentUser.id, ownerId].sort();
    const canonicalParticipants = [
      { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
      { id: owner.id, username: owner.username, avatarUrl: owner.avatarUrl },
    ];
    const snap = await getDocs(query(collection(db, 'messageThreads'), where('participantIds', 'array-contains', currentUser.id)));
    const existing = snap.docs.find((d) => {
      const data: any = d.data();
      if (data.isGroup || data.listingId) return false;
      const ids = Array.isArray(data.participantIds) ? [...data.participantIds].sort() : [];
      return ids.length === 2 && ids[0] === participantIds[0] && ids[1] === participantIds[1];
    });
    const nowTimestamp = Timestamp.now();
    let threadId = existing?.id;
    if (!threadId) {
      const created = await addDoc(collection(db, 'messageThreads'), {
        participantIds,
        participants: canonicalParticipants,
        isGroup: false,
        chatType: 'direct',
        lastMessage: moderated,
        timestamp: nowTimestamp,
        unreadCount: 0,
        unreadCounts: { [currentUser.id]: 0, [ownerId]: 1 },
        typingUsers: {},
      });
      threadId = created.id;
    }
    await addDoc(collection(db, 'messageThreads', threadId, 'messages'), {
      sender: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
      text: moderated,
      newsReplyPayload: { mediaUrl: item.url, mediaType: item.type, ownerId },
      timestamp: nowTimestamp,
    });
    if (existing) {
      const prevUnread = ((existing.data() as any).unreadCounts || {})[ownerId] || 0;
      await updateDoc(doc(db, 'messageThreads', threadId), {
        lastMessage: moderated,
        timestamp: nowTimestamp,
        [`unreadCounts.${ownerId}`]: prevUnread + 1,
      });
    }
  };

  // "Message" from someone's profile: remember where the chat was opened from, so Back returns to that profile instead
  // of dropping the person on the Messages list.
  const [chatReturnProfile, setChatReturnProfile] = useState<{ user: User; overlay: boolean } | null>(null);
  useEffect(() => { if (!selectedThreadId) setChatReturnProfile(null); }, [selectedThreadId]);
  const handleMessageFromProfile = async (u: User, overlay = false) => {
    setChatReturnProfile({ user: u, overlay });
    await handleStartChat(u);
  };
  const handleChatBack = () => {
    const back = chatReturnProfile;
    setSelectedThreadId(null);
    setFallbackThread(null);
    if (!back) return;
    setChatReturnProfile(null);
    if (back.overlay) setProfileOverlay(back.user); else handleViewProfile(back.user);
  };

  const handleStartChat = async (userToChatWith: User) => {
    if (!currentUser) { handlePromptLogin(); return; }
    const iBlocked = (currentUser.blocked || []).includes(userToChatWith.id);
    const theyBlocked = (users.find(u => u.id === userToChatWith.id)?.blocked || []).includes(currentUser.id);
    if (iBlocked || theyBlocked) return;
    const participantIds = [currentUser.id, userToChatWith.id].sort();
    const canonicalParticipants = [
      { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
      { id: userToChatWith.id, username: userToChatWith.username, avatarUrl: userToChatWith.avatarUrl }
    ];
    const canonicalUnreadCounts = {
      [currentUser.id]: 0,
      [userToChatWith.id]: 0
    };

    // Navigate immediately with a temporary local thread so the chat page opens
    // right away, regardless of how long the Firestore round-trip takes.
    const tempThreadId = `pending_${uuidv4()}`;
    setFallbackThread({
      id: tempThreadId,
      participants: canonicalParticipants,
      participantIds,
      isGroup: false,
      chatType: 'direct',
      lastMessage: '',
      timestamp: new Date().toLocaleString(),
      timestampMs: Date.now(),
      unreadCount: 0,
      unreadCounts: canonicalUnreadCounts,
      typingUsers: {}
    } as MessageThread);
    openThread(tempThreadId);

    try {
    const existingThreadQuery = query(collection(db, 'messageThreads'), where('participantIds', 'array-contains', currentUser.id));
    const querySnapshot = await getDocs(existingThreadQuery);
    const existingDoc = querySnapshot.docs.find((docSnapshot) => {
      const data = docSnapshot.data();
      if ((data as any).isGroup || (data as any).listingId) return false;
      const ids = Array.isArray((data as any).participantIds) ? [...(data as any).participantIds].sort() : [];
      return ids.length === participantIds.length && ids.every((id, index) => id === participantIds[index]);
    });

    if (existingDoc) {
        const docSnapshot = existingDoc;
        const data = docSnapshot.data();
        await setDoc(doc(db, 'messageThreads', docSnapshot.id), {
          participantIds,
          participants: Array.isArray((data as any).participants) && (data as any).participants.length > 0
            ? (data as any).participants
            : canonicalParticipants,
          chatType: 'direct',
          unreadCounts: {
            ...canonicalUnreadCounts,
            ...((data as any).unreadCounts || {})
          },
          typingUsers: (data as any).typingUsers || {}
        }, { merge: true });
        setFallbackThread({
          id: docSnapshot.id,
          ...data,
          chatType: 'direct',
          participants: Array.isArray((data as any).participants) && (data as any).participants.length > 0 ? (data as any).participants : canonicalParticipants,
          participantIds: (data as any).participantIds || participantIds,
          timestamp: (data as any).timestamp ? ((data as any).timestamp as Timestamp)?.toDate?.()?.toLocaleString?.() || (data as any).timestamp : new Date().toLocaleString(),
          timestampMs: (data as any).timestamp ? ((data as any).timestamp as Timestamp)?.toDate?.()?.getTime?.() || Date.now() : Date.now(),
          unreadCounts: {
            ...canonicalUnreadCounts,
            ...((data as any).unreadCounts || {})
          },
          typingUsers: (data as any).typingUsers || {}
        } as MessageThread);
        openThread(docSnapshot.id);
    } else {
        const newThreadRef = await addDoc(collection(db, 'messageThreads'), {
            participantIds,
            participants: canonicalParticipants,
            isGroup: false,
            chatType: 'direct',
            lastMessage: "Chat started.",
            timestamp: serverTimestamp(),
            unreadCount: 0,
            unreadCounts: canonicalUnreadCounts,
            typingUsers: {}
        });
        setFallbackThread({
          id: newThreadRef.id,
          participants: canonicalParticipants,
          participantIds,
          isGroup: false,
          chatType: 'direct',
          lastMessage: "Chat started.",
          timestamp: new Date().toLocaleString(),
          timestampMs: Date.now(),
          unreadCount: 0,
          unreadCounts: canonicalUnreadCounts,
          typingUsers: {}
        } as MessageThread);
        openThread(newThreadRef.id);
    }
    setCreateGroupOpen(false);
    } catch (err: any) {
      console.error('Failed to start chat', err);
      // Don't leave the user stuck on a disabled, never-resolving pending chat.
      setSelectedThreadId((prev: string | null) => (prev === tempThreadId ? null : prev));
      setFallbackThread((prev: MessageThread | null) => (prev?.id === tempThreadId ? null : prev));
      alert(`Could not start chat. ${err?.message || 'Unknown error'}`);
    }
  };

  const handleCreateGroup = async (chatType: 'group', groupName: string, participants: User[], groupAvatarFile?: File): Promise<string> => {
    console.log('[CREATE GROUP] Starting. Type:', chatType, 'Group name:', groupName, 'Participants:', participants.length);
    if (!currentUser) { handlePromptLogin(); return; }
    try {
      // No auto-generated identicon - leave blank so the UI shows a plain white
      // placeholder until the user picks a photo.
      let groupAvatarUrl = '';

      if (groupAvatarFile) {
        console.log('[CREATE GROUP] Uploading custom avatar...');
        const converted = await convertImageToJpeg(groupAvatarFile);
        const ext = (converted.name.split('.').pop() || converted.type.split('/')[1] || 'jpg').toLowerCase();
        const storageRef = ref(storage, `groupAvatars/${uuidv4()}.${ext}`);
        await uploadBytes(storageRef, converted, { contentType: converted.type });
        groupAvatarUrl = await getDownloadURL(storageRef);
        console.log('[CREATE GROUP] Avatar uploaded');
      }

      const allParticipants = [currentUser, ...participants];
      const unreadCounts: { [key: string]: number } = {};
      allParticipants.forEach(p => { unreadCounts[p.id] = 0; });

      console.log('[CREATE GROUP] Saving to Firestore...');
      const docRef = await addDoc(collection(db, 'messageThreads'), {
          groupName,
          groupAvatarUrl,
          isGroup: true,
          chatType,
          ownerId: currentUser.id,
          moderatorIds: [],
          participants: allParticipants.map(p => ({id: p.id, username: p.username, avatarUrl: p.avatarUrl})),
          participantIds: allParticipants.map(p => p.id),
          lastMessage: `${currentUser.username} created the group chat.`,
          timestamp: serverTimestamp(),
          unreadCounts
      });

      console.log('[CREATE GROUP] Success! Doc ID:', docRef.id);
      setFallbackThread({
        id: docRef.id,
        groupName,
        groupAvatarUrl,
        isGroup: true,
        chatType,
        participants: allParticipants.map(p => ({id: p.id, username: p.username, avatarUrl: p.avatarUrl})),
        participantIds: allParticipants.map(p => p.id),
        lastMessage: `${currentUser.username} created the group chat.`,
        timestamp: new Date().toLocaleString(),
        timestampMs: Date.now(),
        unreadCount: 0,
        unreadCounts
      } as MessageThread);
      openThread(docRef.id);
      setCreateGroupOpen(false);
      return docRef.id;
    } catch (error: any) {
      console.error('[CREATE GROUP] Error:', error);
      const errorMessage = error?.message || 'Unknown error occurred';
      alert(`Failed to create group: ${errorMessage}`);
      throw error;
    }
  };

  const handleUpdateGroupAvatar = async (threadId: string, file: File) => {
    if (!currentUser) { handlePromptLogin(); return; }
    try {
      const converted = await convertImageToJpeg(file);
      const ext = (converted.name.split('.').pop() || converted.type.split('/')[1] || 'jpg').toLowerCase();
      const storageRef = ref(storage, `groupAvatars/${uuidv4()}.${ext}`);
      await uploadBytes(storageRef, converted, { contentType: converted.type });
      const url = await getDownloadURL(storageRef);
      await updateDoc(doc(db, 'messageThreads', threadId), {
        groupAvatarUrl: url,
        lastMessage: `${currentUser.username} updated the group photo.`,
        timestamp: serverTimestamp()
      });
    } catch (err) {
      console.error('Failed to update group avatar', err);
      alert('Could not update group photo. Please try again.');
    }
  };

  const handleCreateCrew = async (crewName: string, crewColor: string, crewLogoFile?: File, initialMembers?: User[]): Promise<string> => {
    if (!currentUser) { handlePromptLogin(); return ''; }
    try {
      // No auto-generated identicon - leave blank so the UI shows a plain white
      // placeholder until the user picks a logo.
      let groupAvatarUrl = '';

      if (crewLogoFile) {
        const converted = await convertImageToJpeg(crewLogoFile);
        const ext = (converted.name.split('.').pop() || converted.type.split('/')[1] || 'jpg').toLowerCase();
        const storageRef = ref(storage, `groupAvatars/${uuidv4()}.${ext}`);
        await uploadBytes(storageRef, converted, { contentType: converted.type });
        groupAvatarUrl = await getDownloadURL(storageRef);
      }

      const participants = initialMembers || [];
      const allParticipants = [currentUser, ...participants];
      const unreadCounts: { [key: string]: number } = {};
      allParticipants.forEach(p => { unreadCounts[p.id] = 0; });

      const docRef = await addDoc(collection(db, 'messageThreads'), {
          groupName: crewName,
          groupAvatarUrl,
          isGroup: true,
          chatType: 'crew',
          crewColor,
          ownerId: currentUser.id,
          moderatorIds: [],
          participants: allParticipants.map(p => ({id: p.id, username: p.username, avatarUrl: p.avatarUrl})),
          participantIds: allParticipants.map(p => p.id),
          lastMessage: `${currentUser.username} created the crew.`,
          timestamp: serverTimestamp(),
          unreadCounts
      });

      setFallbackThread({
        id: docRef.id,
        groupName: crewName,
        groupAvatarUrl,
        isGroup: true,
        chatType: 'crew',
        crewColor,
        ownerId: currentUser.id,
        moderatorIds: [],
        participants: allParticipants.map(p => ({id: p.id, username: p.username, avatarUrl: p.avatarUrl})),
        participantIds: allParticipants.map(p => p.id),
        lastMessage: `${currentUser.username} created the crew.`,
        timestamp: new Date().toLocaleString(),
        timestampMs: Date.now(),
        unreadCount: 0,
        unreadCounts
      } as MessageThread);
      openThread(docRef.id);
      setCreateGroupOpen(false);
      return docRef.id;
    } catch (error: any) {
      console.error('[CREATE CREW] Error:', error);
      const errorMessage = error?.message || 'Unknown error occurred';
      alert(`Failed to create crew: ${errorMessage}`);
      throw error;
    }
  };

  const handleAddSpot = async (spotData: Omit<Spot, 'id' | 'addedBy' | 'comments' | 'photos'>, imageFile?: File, videoFile?: File, extraImageFiles?: File[]) => {
      if (!currentUser) { handlePromptLogin(); return; }
      if (!getPinRateLimitStatus().allowed) return;
      const adder = { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl };
      const isPark = spotData.category === 'skatepark';
      const newSpotRef = doc(collection(db, 'spots'));
      const spotId = newSpotRef.id;
      let created = false;
      try {
          // The pin goes on the map straight away; photos/video upload in the background
          // while the pin shows a progress bar, then get attached to the spot.
          await setDoc(newSpotRef, {
              ...spotData,
              name: censorInappropriateText(cleanLine(spotData.name, TEXT_LIMITS.title)),
              description: censorInappropriateText(cleanText(spotData.description, TEXT_LIMITS.description)),
              sport: { id: spotData.sport.id, name: spotData.sport.name }, // Store only id and name, not icon function
              imageUrl: '',
              videoUrl: '',
              photos: [],
              mediaLikes: {},
              overallRatings: {},
              difficultyRatings: {},
              securityRatings: {},
              addedBy: adder,
              reviewed: isPark ? isRidazModerator : true,
              createdAt: serverTimestamp(),
          });
          created = true;
          const hasMedia = !!imageFile || !!videoFile || (extraImageFiles?.length || 0) > 0;
          setSpots(prev => [
            {
              id: spotId,
              ...spotData,
              sport: spotData.sport,
              imageUrl: '',
              videoUrl: '',
              mediaLikes: {},
              overallRatings: {},
              difficultyRatings: {},
              securityRatings: {},
              addedBy: adder,
              comments: [],
              photos: [],
              reviewed: isPark ? isRidazModerator : true,
              createdAt: Date.now(),
            },
            ...prev.filter(spot => spot.id !== spotId)
          ]);

          // Visual success feedback
          const feedback = document.createElement('div');
          feedback.className = 'fixed top-20 left-1/2 transform -translate-x-1/2 bg-lime-500 text-black px-4 py-2 rounded-lg font-semibold shadow-lg z-50';
          feedback.textContent = hasMedia ? 'Pin added - uploading photos...' : 'Spot added successfully!';
          document.body.appendChild(feedback);
          setTimeout(() => { feedback.remove(); }, 3000);

          if (!hasMedia) return;
          setPinProgress(spotId, 0);

          const extraCompressed = await Promise.all((extraImageFiles || []).map(file => compressAndDownscaleImage(file)));
          const primaryCompressed = imageFile ? await compressAndDownscaleImage(imageFile) : null;
          const processedVideo = videoFile ? await compressAndDownscaleVideo(videoFile) : null;

          const uploads: { path: string; file: File }[] = [
            ...extraCompressed.map(file => ({ path: `spot_photos/${uuidv4()}.${file.name.split('.').pop() || 'jpg'}`, file })),
            ...(primaryCompressed ? [{ path: `spot_photos/${uuidv4()}.${primaryCompressed.name.split('.').pop() || 'jpg'}`, file: primaryCompressed }] : []),
            ...(processedVideo ? [{ path: `spot_videos/${uuidv4()}.${processedVideo.name.split('.').pop() || 'mp4'}`, file: processedVideo }] : []),
          ];
          const urls = await uploadFilesWithPinProgress(spotId, uploads);
          const extraPhotos = extraCompressed.map((_, i) => ({ id: uuidv4(), url: urls[i], type: 'image' as const, addedBy: adder }));
          const imageUrl = primaryCompressed ? urls[extraCompressed.length] : '';
          const videoUrl = processedVideo ? urls[urls.length - 1] : '';

          await updateDoc(newSpotRef, { imageUrl, videoUrl, photos: extraPhotos });
          setSpots(prev => prev.map(spot => (spot.id === spotId ? { ...spot, imageUrl, videoUrl, photos: extraPhotos } : spot)));
          setPinProgress(spotId, null);
      } catch (error: any) {
          console.error('Error adding spot:', error);
          setPinProgress(spotId, null);
          if (created) {
              // A pin whose media never uploaded is incomplete - take it back off the map.
              try { await deleteDoc(newSpotRef); } catch { /* ignore */ }
              setSpots(prev => prev.filter(spot => spot.id !== spotId));
          }
          const errorMessage = error?.message || 'Unknown error occurred';
          alert(`Failed to add spot:\n\n${errorMessage}\n\nPlease try again or use a different file.`);
      }
  };

  const handleAddSpotPhoto = async (spotId: string, imageFile: File) => {
      if (!currentUser) { handlePromptLogin(); return; }
      let tempId: string | null = null;
      try {
          const isVideo = imageFile.type.startsWith('video/');

          // Compress/process media before upload
          let processedFile: File;
          if (isVideo) {
            console.log('Processing spot video...');
            processedFile = await compressAndDownscaleVideo(imageFile);
          } else {
            console.log('Compressing spot photo...');
            processedFile = await compressAndDownscaleImage(imageFile);
          }

          // Optimistic local update so photo appears immediately
          tempId = uuidv4();
          const tempUrl = URL.createObjectURL(processedFile);
          setSpots(prev => prev.map(s => s.id === spotId ? {
            ...s,
            photos: [
              ...(s.photos || []),
              { id: tempId, url: tempUrl, type: isVideo ? 'video' : 'image', addedBy: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl } }
            ]
          } : s));

          console.log(`Uploading spot ${isVideo ? 'video' : 'photo'}...`);

          const fileExtension = processedFile.name.split('.').pop() || (isVideo ? 'mp4' : 'jpg');
          const folder = isVideo ? 'spot_videos' : 'spot_photos';
          const storageRef = ref(storage, `${folder}/${spotId}/${uuidv4()}.${fileExtension}`);
          const metadata = {
            contentType: processedFile.type,
            customMetadata: {
              uploadedBy: currentUser.id,
              spotId: spotId,
              originalSize: imageFile.size.toString(),
              compressedSize: processedFile.size.toString()
            }
          };
          await uploadBytes(storageRef, processedFile, metadata);
          const mediaUrl = await getDownloadURL(storageRef);
          console.log(`Spot ${isVideo ? 'video' : 'photo'} uploaded successfully:`, mediaUrl);

          // Add media to spot's photos array
          const spotRef = doc(db, 'spots', spotId);
          await updateDoc(spotRef, {
              photos: arrayUnion({
                  id: uuidv4(),
                  url: mediaUrl,
                  type: isVideo ? 'video' : 'image',
                  addedBy: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl }
              })
          });
          console.log(`${isVideo ? 'Video' : 'Photo'} added to spot!`);

          // Replace optimistic temp entry with real URL
          setSpots(prev => prev.map(s => s.id === spotId ? {
            ...s,
            photos: (s.photos || []).map(p => p.id === tempId ? { ...p, id: uuidv4(), url: mediaUrl } : p)
          } : s));
      } catch (error) {
          console.error('Error adding spot media:', error);
          alert('Failed to add media. Check console for details.');
          // Rollback optimistic entry
          if (tempId) {
            setSpots(prev => prev.map(s => s.id === spotId ? {
              ...s,
              photos: (s.photos || []).filter(p => p.id !== tempId)
            } : s));
          }
      }
  };

  const handleAddJamSesh = async (seshData: Omit<JamSesh, 'id' | 'createdBy'>) => {
      if (!currentUser) { handlePromptLogin(); return; }
      if (!getPinRateLimitStatus().allowed) return;
      try {
          const newSeshRef = await addDoc(collection(db, 'jam_seshes'), {
              ...seshData,
              sport: { id: seshData.sport.id, name: seshData.sport.name }, // Store only id and name, not icon function
              createdBy: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
              createdAt: serverTimestamp(),
          });
          // Optimistic local add so pin shows instantly
          setJamSeshes(prev => [
            {
              id: newSeshRef.id,
              ...seshData,
              sport: seshData.sport,
              createdBy: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
              createdAt: Date.now(),
            },
            ...prev
          ]);
          console.log('Jam sesh added successfully!');
      } catch (error) {
          console.error('Error adding jam sesh:', error);
          alert('Failed to add jam sesh. Check console for details.');
      }
  };

  const handleAddEvent = async (eventData: Omit<Event, 'id' | 'createdBy' | 'interestedUserIds' | 'createdAt'>) => {
      if (!currentUser) { handlePromptLogin(); return; }
      if (!getPinRateLimitStatus().allowed) return;
      try {
          const newEventRef = await addDoc(collection(db, 'events'), {
              ...eventData,
              sport: { id: eventData.sport.id, name: eventData.sport.name },
              createdBy: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
              interestedUserIds: [],
              createdAt: serverTimestamp(),
          });
          // Optimistic local add so pin shows instantly
          setEvents(prev => [
            {
              id: newEventRef.id,
              ...eventData,
              sport: eventData.sport,
              createdBy: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
              interestedUserIds: [],
              createdAt: Date.now(),
            },
            ...prev
          ]);
      } catch (error) {
          console.error('Error adding event:', error);
          alert('Failed to add event. Check console for details.');
      }
  };

  // Adds a user as a message-thread participant for an event chat. Participants are only ever
  // added, never removed (even if a user later un-marks Interested) — this keeps the chat history
  // visible to everyone who was ever part of the conversation and avoids the extra bookkeeping /
  // race conditions of trying to keep participantIds in perfect lockstep with interestedUserIds.
  const addParticipantToEventThread = async (threadId: string, user: User) => {
    const threadRef = doc(db, 'messageThreads', threadId);
    const threadSnap = await getDoc(threadRef);
    if (!threadSnap.exists()) return;
    const threadData = threadSnap.data();
    const existingParticipantIds: string[] = threadData?.participantIds || [];
    if (existingParticipantIds.includes(user.id)) return;
    const existingParticipants = Array.isArray(threadData?.participants) ? threadData.participants : [];
    await updateDoc(threadRef, {
      participantIds: arrayUnion(user.id),
      participants: [...existingParticipants, { id: user.id, username: user.username, avatarUrl: user.avatarUrl }],
    });
  };

  const getOrCreateEventThread = async (event: Event, joiningUserId?: string): Promise<string> => {
    const existingQuery = query(collection(db, 'messageThreads'), where('eventId', '==', event.id));
    const existingSnap = await getDocs(existingQuery);
    if (!existingSnap.empty) return existingSnap.docs[0].id;

    const hostParticipant = { id: event.createdBy.id, username: event.createdBy.username, avatarUrl: event.createdBy.avatarUrl };
    // Include the user actually creating this doc (from a possibly-stale local
    // `event` snapshot whose interestedUserIds may not yet reflect them) so the
    // Firestore create rule's "creator must be in participantIds" check holds.
    const participantIds = Array.from(new Set([
      event.createdBy.id,
      ...(event.interestedUserIds || []),
      ...(joiningUserId ? [joiningUserId] : []),
    ]));
    const participants = participantIds.map((id) => {
      if (id === event.createdBy.id) return hostParticipant;
      const user = users.find((u) => u.id === id);
      return user ? { id: user.id, username: user.username, avatarUrl: user.avatarUrl } : { id, username: 'unknown', avatarUrl: '/logo.png' };
    });

    const newThreadRef = await addDoc(collection(db, 'messageThreads'), {
      eventId: event.id,
      isGroup: true,
      chatType: 'group',
      groupName: event.name,
      // Show a zoomed-in map snapshot of the event's location instead of a
      // generic avatar, in the chat list preview and the chat header.
      groupAvatarUrl: getStaticMapTileUrl(event.location.lat, event.location.lng, 17),
      participantIds,
      participants,
      lastMessage: 'Event chat created.',
      timestamp: serverTimestamp(),
      unreadCounts: Object.fromEntries(participantIds.map((id) => [id, 0])),
      typingUsers: {},
    });
    return newThreadRef.id;
  };

  const handleToggleEventInterest = async (eventId: string) => {
    if (!currentUser) { handlePromptLogin(); return; }
    const event = events.find((e) => e.id === eventId);
    if (!event) return;
    const currentInterestedIds = event.interestedUserIds || [];
    const isInterested = currentInterestedIds.includes(currentUser.id);
    const nextInterestedUserIds = isInterested
      ? currentInterestedIds.filter((id) => id !== currentUser.id)
      : [...currentInterestedIds, currentUser.id];

    setEvents(prev => prev.map((e) => (e.id === eventId ? { ...e, interestedUserIds: nextInterestedUserIds } : e)));

    try {
      await updateDoc(doc(db, 'events', eventId), {
        interestedUserIds: isInterested ? arrayRemove(currentUser.id) : arrayUnion(currentUser.id),
      });

      if (!isInterested) {
        const threadId = await getOrCreateEventThread(event, currentUser.id);
        await addParticipantToEventThread(threadId, currentUser);
      }
    } catch (error) {
      console.error('Error toggling event interest:', error);
      setEvents(prev => prev.map((e) => (e.id === eventId ? event : e)));
      alert('Could not update your interest. Please try again.');
    }
  };

  const handleOpenEventChat = async (event: Event) => {
    if (!currentUser) { handlePromptLogin(); return; }
    try {
      const threadId = await getOrCreateEventThread(event, currentUser.id);
      await addParticipantToEventThread(threadId, currentUser);
      openThread(threadId);
    } catch (error) {
      console.error('Error opening event chat:', error);
      alert('Could not open the event chat. Please try again.');
    }
  };

  const getOrCreateDirectThreadForShare = async (recipient: User): Promise<string> => {
    if (!currentUser) throw new Error('Not signed in');
    const participantIds = [currentUser.id, recipient.id].sort();
    const existingThreadQuery = query(collection(db, 'messageThreads'), where('participantIds', 'array-contains', currentUser.id));
    const querySnapshot = await getDocs(existingThreadQuery);
    const existingDoc = querySnapshot.docs.find((docSnapshot) => {
      const data = docSnapshot.data();
      if ((data as any).isGroup || (data as any).listingId) return false;
      const ids = Array.isArray((data as any).participantIds) ? [...(data as any).participantIds].sort() : [];
      return ids.length === participantIds.length && ids.every((id, index) => id === participantIds[index]);
    });
    if (existingDoc) return existingDoc.id;

    const canonicalParticipants = [
      { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
      { id: recipient.id, username: recipient.username, avatarUrl: recipient.avatarUrl },
    ];
    const newThreadRef = await addDoc(collection(db, 'messageThreads'), {
      participantIds,
      participants: canonicalParticipants,
      isGroup: false,
      chatType: 'direct',
      lastMessage: '',
      timestamp: serverTimestamp(),
      unreadCount: 0,
      unreadCounts: { [currentUser.id]: 0, [recipient.id]: 0 },
      typingUsers: {},
    });
    return newThreadRef.id;
  };

  const handleShareSpot = async (spotId: string, recipientId: string) => {
    if (!currentUser) { handlePromptLogin(); return; }
    const spot = spots.find((s) => s.id === spotId) || shareSpotPage?.fallbackSpot;
    const recipient = users.find((u) => u.id === recipientId);
    if (!spot || !recipient) return;
    try {
      const threadId = await getOrCreateDirectThreadForShare(recipient);
      const nowTimestamp = Timestamp.now();
      const text = `Shared a spot: ${spot.name}`;
      await addDoc(collection(db, 'messageThreads', threadId, 'messages'), {
        sender: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
        text,
        spotPayload: {
          spotId: spot.id,
          name: spot.name,
          description: spot.description,
          sportName: spot.sport?.name,
          imageUrl: spot.imageUrl || spot.photos?.[0]?.url || '',
          lat: spot.location.lat,
          lng: spot.location.lng,
        },
        timestamp: nowTimestamp,
      });
      await updateDoc(doc(db, 'messageThreads', threadId), {
        lastMessage: text,
        timestamp: nowTimestamp,
      });
    } catch (error) {
      console.error('Error sharing spot:', error);
      alert('Could not share this spot. Please try again.');
    }
  };

  const handleShareJamSesh = async (jamSeshId: string, recipientId: string) => {
    if (!currentUser) { handlePromptLogin(); return; }
    const jamSesh = jamSeshes.find((s) => s.id === jamSeshId) || shareJamSeshPage?.fallbackJamSesh;
    const recipient = users.find((u) => u.id === recipientId);
    if (!jamSesh || !recipient) return;
    try {
      const threadId = await getOrCreateDirectThreadForShare(recipient);
      const nowTimestamp = Timestamp.now();
      const text = `Shared a Jam Sesh: ${jamSesh.name}`;
      await addDoc(collection(db, 'messageThreads', threadId, 'messages'), {
        sender: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
        text,
        jamSeshPayload: {
          jamSeshId: jamSesh.id,
          name: jamSesh.name,
          description: jamSesh.description || '',
          sportName: jamSesh.sport?.name,
          startTime: jamSesh.startTime,
          endTime: jamSesh.endTime,
          lat: jamSesh.location.lat,
          lng: jamSesh.location.lng,
        },
        timestamp: nowTimestamp,
      });
      await updateDoc(doc(db, 'messageThreads', threadId), {
        lastMessage: text,
        timestamp: nowTimestamp,
      });
    } catch (error) {
      console.error('Error sharing jam sesh:', error);
      alert('Could not share this jam sesh. Please try again.');
    }
  };

  const handleShareEvent = async (eventId: string, recipientId: string) => {
    if (!currentUser) { handlePromptLogin(); return; }
    const event = events.find((e) => e.id === eventId) || shareEventPage?.fallbackEvent;
    const recipient = users.find((u) => u.id === recipientId);
    if (!event || !recipient) return;
    try {
      const threadId = await getOrCreateDirectThreadForShare(recipient);
      const nowTimestamp = Timestamp.now();
      const text = `Shared an event: ${event.name}`;
      await addDoc(collection(db, 'messageThreads', threadId, 'messages'), {
        sender: { id: currentUser.id, username: currentUser.username, avatarUrl: currentUser.avatarUrl },
        text,
        eventPayload: {
          eventId: event.id,
          name: event.name,
          description: event.description,
          sportName: event.sport?.name,
          startTime: event.startTime,
          endTime: event.endTime,
          lat: event.location.lat,
          lng: event.location.lng,
        },
        timestamp: nowTimestamp,
      });
      await updateDoc(doc(db, 'messageThreads', threadId), {
        lastMessage: text,
        timestamp: nowTimestamp,
      });
    } catch (error) {
      console.error('Error sharing event:', error);
      alert('Could not share this event. Please try again.');
    }
  };

  const handleLeaveGroup = async (threadId: string) => {
    if (!currentUser) return;
    try {
      const threadRef = doc(db, 'messageThreads', threadId);
      const threadDoc = await getDoc(threadRef);

      if (threadDoc.exists()) {
        const threadData = threadDoc.data();
        const updatedParticipantIds = (threadData.participantIds || []).filter((id: string) => id !== currentUser.id);

        // Update the thread to remove the user
        await updateDoc(threadRef, {
          participantIds: updatedParticipantIds
        });

        // Navigate back to messenger list
        setSelectedThreadId(null);
        setFallbackThread(null);
      }
    } catch (error) {
      console.error('Error leaving group:', error);
      alert('Failed to leave group. Please try again.');
    }
  };

  const handleAddCrewMembers = async (threadId: string, newMembers: User[]) => {
    if (!currentUser || !newMembers.length) return;
    try {
      const threadRef = doc(db, 'messageThreads', threadId);
      const threadDoc = await getDoc(threadRef);
      if (!threadDoc.exists()) return;

      const threadData = threadDoc.data();
      const existingParticipants: { id: string; username: string; avatarUrl: string }[] = Array.isArray(threadData.participants) ? threadData.participants : [];
      const existingIds: string[] = Array.isArray(threadData.participantIds) ? threadData.participantIds : existingParticipants.map(p => p.id);

      const membersToAdd = newMembers.filter(m => !existingIds.includes(m.id));
      if (!membersToAdd.length) return;

      const updatedParticipants = [
        ...existingParticipants,
        ...membersToAdd.map(m => ({ id: m.id, username: m.username, avatarUrl: m.avatarUrl }))
      ];
      const updatedParticipantIds = [...existingIds, ...membersToAdd.map(m => m.id)];

      // Only touch the two fields that changed, to avoid clobbering concurrent writes.
      await updateDoc(threadRef, {
        participants: updatedParticipants,
        participantIds: updatedParticipantIds
      });
    } catch (error) {
      console.error('Error adding crew members:', error);
      alert('Failed to add members. Please try again.');
    }
  };

  // A moderation action ("made @x a moderator", "kicked @x") is remembered on the chat and shown as small text under the
  // newest message. Kept on the thread (not as a message) so it never triggers a push notification.
  const modLogEntry = (text: string) => ({ id: uuidv4(), text, at: Date.now() });

  const handlePromoteModerator = async (threadId: string, userId: string) => {
    if (!currentUser) return;
    try {
      const target = messageThreads.find((t) => t.id === threadId)?.participants?.find((p) => p.id === userId);
      await updateDoc(doc(db, 'messageThreads', threadId), {
        moderatorIds: arrayUnion(userId),
        modLog: arrayUnion(modLogEntry(`@${currentUser.username} made @${target?.username || 'a member'} a moderator`)),
      });
    } catch (error) {
      console.error('Error promoting moderator:', error);
      alert('Failed to promote moderator. Please try again.');
    }
  };

  const handleDemoteModerator = async (threadId: string, userId: string) => {
    if (!currentUser) return;
    try {
      const target = messageThreads.find((t) => t.id === threadId)?.participants?.find((p) => p.id === userId);
      await updateDoc(doc(db, 'messageThreads', threadId), {
        moderatorIds: arrayRemove(userId),
        modLog: arrayUnion(modLogEntry(`@${currentUser.username} removed @${target?.username || 'a member'} as moderator`)),
      });
    } catch (error) {
      console.error('Error removing moderator:', error);
      alert('Failed to remove moderator. Please try again.');
    }
  };

  const handleKickMember = async (threadId: string, user: User) => {
    if (!currentUser) return;
    try {
      const threadRef = doc(db, 'messageThreads', threadId);
      const threadDoc = await getDoc(threadRef);
      if (!threadDoc.exists()) return;
      const data = threadDoc.data();
      const participants: { id: string }[] = Array.isArray(data.participants) ? data.participants : [];
      const ids: string[] = Array.isArray(data.participantIds) ? data.participantIds : participants.map((p) => p.id);
      await updateDoc(threadRef, {
        participants: participants.filter((p) => p.id !== user.id),
        participantIds: ids.filter((id) => id !== user.id),
        moderatorIds: arrayRemove(user.id),
        modLog: arrayUnion(modLogEntry(`@${currentUser.username} kicked @${user.username}`)),
      });
    } catch (error) {
      console.error('Error kicking member:', error);
      alert('Failed to kick that user. Please try again.');
    }
  };

  // Events planned inside a crew/group chat (shown on its calendar). Stored on the thread so every member sees them live.
  const handleSaveChatEvent = async (threadId: string, event: ChatEvent) => {
    if (!currentUser) return;
    try {
      await updateDoc(doc(db, 'messageThreads', threadId), { chatEvents: arrayUnion(event) });
    } catch (error) {
      console.error('Error saving chat event:', error);
      alert('Could not save the event. Please try again.');
    }
  };

  // "Going" / "Checking in" on a chat event: toggles the rider's id on the event and drops a small notice in the chat
  // ("@x is going to the Skateboard event on Wed, Oct 14"). Done in a transaction so two people tapping at once both count.
  const handleRespondToChatEvent = async (threadId: string, event: ChatEvent, kind: 'going' | 'checkedIn') => {
    if (!currentUser) return;
    try {
      const threadRef = doc(db, 'messageThreads', threadId);
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(threadRef);
        if (!snap.exists()) return;
        const data = snap.data();
        const list: ChatEvent[] = Array.isArray(data.chatEvents) ? data.chatEvents : [];
        const index = list.findIndex((e) => e.id === event.id);
        if (index < 0) return;
        const current = list[index];
        const ids = new Set<string>(current[kind] || []);
        const wasIn = ids.has(currentUser.id);
        if (wasIn) ids.delete(currentUser.id); else ids.add(currentUser.id);
        const next = [...list];
        next[index] = { ...current, [kind]: [...ids] };
        const sport = (current.sports?.length ? current.sports : [current.sport]).map((id) => SPORTS.find((sp) => sp.id === id)?.name || id).join('/') || 'chat';
        const when = new Date(current.startMs).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
        const who = `@${currentUser.username}`;
        const what = current.title ? `“${current.title}”` : `the ${sport} event`;
        const text = kind === 'going'
          ? (wasIn ? `${who} is no longer going to ${what} on ${when}` : `${who} is going to ${what} on ${when}`)
          : (wasIn ? `${who} took back their check-in for ${what} on ${when}` : `${who} is checking in to ${what} on ${when}`);
        const log = [...(Array.isArray(data.modLog) ? data.modLog : []), modLogEntry(text)].slice(-30);
        tx.update(threadRef, { chatEvents: next, modLog: log });
      });
    } catch (error) {
      console.error('Error updating chat event response:', error);
      alert('Could not update that. Please try again.');
    }
  };

  const handleDeleteChatEvent = async (threadId: string, event: ChatEvent) => {
    if (!currentUser) return;
    try {
      await updateDoc(doc(db, 'messageThreads', threadId), { chatEvents: arrayRemove(event) });
    } catch (error) {
      console.error('Error deleting chat event:', error);
      alert('Could not delete the event. Please try again.');
    }
  };

  const totalUnreadCount = useMemo(() => {
    if (!currentUser) return 0;
    return messageThreads.reduce((sum, t) => sum + (t.unreadCounts?.[currentUser.id] || 0), 0);
  }, [messageThreads, currentUser]);

  // Calculate separate notification counts for likes, comments, and tags
  const notificationCounts = useMemo(() => {
    if (!currentUser) return { total: 0, likes: 0, comments: 0, tags: 0 };

    // Track which notifications have been seen
    const seenLikes = new Set(currentUser.seenLikes || []);
    const seenTags = new Set(currentUser.seenTags || []);

    // Count unseen likes
    let likes = 0;
    for (const p of posts) {
      if (p.user.id === currentUser.id) {
        for (const userId of (p.likedBy || [])) {
          const likeId = `${p.id}-${userId}`;
          if (!seenLikes.has(likeId)) {
            likes++;
          }
        }
      }
    }

    // Count unseen comments using fetched comment counts
    let comments = 0;
    const seenComments = new Set(currentUser.seenComments || []);

    // Sum up unseen comments from live counts
    const hasSeenAllComments = seenComments.size > 0;
    for (const [_postId, data] of commentCounts) {
      if (hasSeenAllComments) {
        comments += Math.max(data?.newSinceSeen || 0, 0);
      } else {
        comments += data?.total || 0;
      }
    }

    // Count unseen tags (includes post tags, caption mentions, and comment mentions)
    let tagged = 0;
    const mentionPattern = `@${currentUser.username.toLowerCase()}`;
    for (const p of posts) {
      const tagIds = (p.tags || []).map((t: any) => typeof t === 'string' ? t : t?.id).filter(Boolean);
      const viaTags = tagIds.includes(currentUser.id);
      const viaCaption = p.caption && p.caption.toLowerCase().includes(mentionPattern);
      const tagMatch = viaTags || viaCaption || commentMentions.has(p.id);
      if (tagMatch && !seenTags.has(p.id)) {
        tagged++;
      }
    }

    return {
      total: likes + comments + tagged,
      likes,
      comments,
      tags: tagged
    };
  }, [posts, currentUser, commentCounts, commentMentions]);

  const notificationsCount = notificationCounts.total;

  // Mark all notifications as seen when user views the notifications page
  const handleMarkNotificationsAsSeen = useCallback(async () => {
    if (!currentUser) return;

    try {
      // Collect all current notification IDs
      const seenLikes: string[] = [];
      const seenSaves: string[] = [];
      const seenTags: string[] = [];

      for (const p of posts) {
        // Collect likes on user's posts
        if (p.user.id === currentUser.id) {
          for (const userId of (p.likedBy || [])) {
            seenLikes.push(`${p.id}-${userId}`);
          }
          for (const userId of (p.savedBy || [])) {
            seenSaves.push(`${p.id}-${userId}`);
          }
        }

        // Collect tags
        const tagIds = (p.tags || []).map((t: any) => typeof t === 'string' ? t : t?.id).filter(Boolean);
        const viaTags = tagIds.includes(currentUser.id);
        const mentionPattern = `@${currentUser.username.toLowerCase()}`;
        const viaCaption = p.caption && p.caption.toLowerCase().includes(mentionPattern);
        const viaComments = commentMentions.has(p.id);

        if (viaTags || viaCaption || viaComments) {
          seenTags.push(p.id);
        }
      }

      // Update user document in Firestore
      const userRef = doc(db, 'users', currentUser.id);
      await updateDoc(userRef, {
        seenLikes: Array.from(new Set([...(currentUser.seenLikes || []), ...seenLikes])),
        seenSaves: Array.from(new Set([...(currentUser.seenSaves || []), ...seenSaves])),
        seenTags: Array.from(new Set([...(currentUser.seenTags || []), ...seenTags])),
        lastSeenNotifications: serverTimestamp()
      });

      console.log('Notifications marked as seen');
    } catch (error) {
      console.error('Error marking notifications as seen:', error);
    }
  }, [currentUser, posts]);

  // Mark only likes as seen
  const handleMarkLikesAsSeen = useCallback(async () => {
    if (!currentUser) return;

    try {
      const seenLikes: string[] = [];

      for (const p of posts) {
        if (p.user.id === currentUser.id) {
          for (const userId of (p.likedBy || [])) {
            seenLikes.push(`${p.id}-${userId}`);
          }
        }
      }

      const userRef = doc(db, 'users', currentUser.id);
      const nextSeenLikes = Array.from(new Set([...(currentUser.seenLikes || []), ...seenLikes]));
      setCurrentUser(prev => prev ? { ...prev, seenLikes: nextSeenLikes } as User : prev);
      await updateDoc(userRef, { seenLikes: nextSeenLikes });

      console.log('Likes marked as seen');
    } catch (error) {
      console.error('Error marking likes as seen:', error);
    }
  }, [currentUser, posts]);

  // Mark only comments as seen
  const handleMarkCommentsAsSeen = useCallback(async () => {
    if (!currentUser) return;
    try {
      const marker = `all-${Date.now()}`;
      const lastSeenAt = Date.now();
      const userRef = doc(db, 'users', currentUser.id);
      await updateDoc(userRef, { seenComments: [marker], lastSeenCommentsAt: lastSeenAt });

      // Update local user state optimistically
      setCurrentUser(prev => prev ? { ...prev, seenComments: [marker], lastSeenCommentsAt: lastSeenAt } as User : prev);
    } catch (error) {
      console.error('Error marking comments as seen:', error);
    }
  }, [currentUser]);

  // Mark only tags as seen
  const handleMarkTagsAsSeen = useCallback(async () => {
    if (!currentUser) return;

    try {
      const seenTags: string[] = [];

      for (const p of posts) {
        const tagIds = (p.tags || []).map((t: any) => typeof t === 'string' ? t : t?.id).filter(Boolean);
        const viaTags = tagIds.includes(currentUser.id);
        const mentionPattern = `@${currentUser.username.toLowerCase()}`;
        const viaCaption = p.caption && p.caption.toLowerCase().includes(mentionPattern);
        const viaComments = commentMentions.has(p.id);

        if (viaTags || viaCaption || viaComments) {
          seenTags.push(p.id);
        }
      }

      const userRef = doc(db, 'users', currentUser.id);
      const nextSeenTags = Array.from(new Set([...(currentUser.seenTags || []), ...seenTags]));
      setCurrentUser(prev => prev ? { ...prev, seenTags: nextSeenTags } as User : prev);
      await updateDoc(userRef, { seenTags: nextSeenTags });

      console.log('Tags marked as seen');
    } catch (error) {
      console.error('Error marking tags as seen:', error);
    }
  }, [currentUser, posts, commentMentions]);

  useEffect(() => {
    if (!currentUser) {
      setUnseenNotifications(0);
      return;
    }

    // Update the badge count based on notificationsCount
    setUnseenNotifications(notificationsCount);
  }, [notificationsCount, currentUser]);

  const profileToShow = viewedUser || currentUser;

  const renderContent = () => {
    const threadForChat = selectedThread
      ? hydrateThreadParticipants({ ...selectedThread, participants: selectedThread.participants || [] } as MessageThread)
      : null;
    if (threadForChat) {
      const otherParticipant = !threadForChat.isGroup
        ? threadForChat.participants?.find((p: User) => p.id !== currentUser?.id)
        : null;
      const chatIsBlocked = otherParticipant
        ? (currentUser?.blocked || []).includes(otherParticipant.id) ||
          (users.find((u: User) => u.id === otherParticipant.id)?.blocked || []).includes(currentUser?.id ?? '')
        : false;
      return (
        <Suspense fallback={<ChatSkeleton group={Boolean(threadForChat?.isGroup)} />}>
          <ChatDetail
            thread={threadForChat}
            currentUser={currentUser!}
            allUsers={users}
            ratingReviews={listingReviews}
            onOpenRatingProfile={(u) => setRatingProfileUser(u)}
            onBack={handleChatBack}
            onViewProfile={handleViewProfileOverlay}
            onAddMessage={handleAddMessage}
            onReactToMessage={handleReactToMessage}
            onTyping={handleTyping}
            onStopTyping={handleStopTyping}
            onLeaveGroup={handleLeaveGroup}
            onUpdateGroupAvatar={handleUpdateGroupAvatar}
            onAddCrewMembers={handleAddCrewMembers}
            onPromoteModerator={handlePromoteModerator}
            onDemoteModerator={handleDemoteModerator}
            onKickMember={handleKickMember}
            onSaveChatEvent={handleSaveChatEvent}
            onDeleteChatEvent={handleDeleteChatEvent}
            onRespondToChatEvent={handleRespondToChatEvent}
            mapPins={{ spots, shops, jamSeshes, events }}
            deviceLocation={deviceLocation}
            sharedLocations={liveLocationShares}
            onOpenLocationInMap={handleOpenLocationInMap}
            onOpenSpotInMap={handleOpenSpotInMap}
            onOpenListing={handleOpenListing}
            onStartCall={(type) => { if (!callSession) setCallSession({ thread: threadForChat, type }); }}
            onJoinCall={() => { const c = threadForChat.activeCall; if (!callSession && c) setCallSession({ thread: threadForChat, type: c.type }); }}
            onOpenJamSeshInMap={handleOpenJamSeshInMap}
            onOpenEventInMap={handleOpenEventInMap}
            isBlocked={chatIsBlocked}
          />
        </Suspense>
      );
    }
    if (isCreateGroupOpen) {
      return (
        <Suspense fallback={<ListSkeleton />}>
          <CreateGroupChat currentUser={currentUser!} allUsers={users} onClose={() => setCreateGroupOpen(false)} onCreate={handleCreateGroup} onCreateCrew={handleCreateCrew} onStartDirectChat={async (u) => { await handleStartChat(u); }} />
        </Suspense>
      );
    }
    switch (activeNav.id) {
      case 'feed':
        return (
          <Suspense fallback={<FeedSkeleton />}>
            <Feed
              posts={posts}
              stories={stories}
              zips={feedZips}
              currentUser={currentUser}
              allUsers={users}
              onPromptLogin={handlePromptLogin}
              setActiveNav={handleNavChange}
              onViewProfile={handleViewProfile}
              onToggleLike={handleToggleLike}
              onReactToPost={handleReactToPost}
              onAddComment={handleAddComment}
              onOpenComments={openPostComments}
              onAddPost={handleAddPost}
              onAddStory={handleAddStory}
              onSharePost={handleSharePost}
              onOpenSharePage={openSharePostPage}
              onToggleSave={handleToggleSave}
              onDeletePost={handleDeletePost} onBanUser={handleBanUser} onReportContent={handleReportContent}
              onOpenUploadPage={openUploadPage}
              onOpenUploadNewsPage={openUploadNewsPage} onOpenUploadZipPage={openUploadZipPage}
              onOpenZip={openZip}
              onOpenLocation={handleOpenPostLocation}
              onOpenNewsViewer={(storiesList, idx) => openNewsViewer(storiesList, idx)}
              notificationsCount={notificationsCount}
              newLikesCount={notificationCounts.likes}
              newCommentsCount={notificationCounts.comments}
              newTagsCount={notificationCounts.tags}
              onMarkLikesAsSeen={handleMarkLikesAsSeen}
              onMarkCommentsAsSeen={handleMarkCommentsAsSeen}
              onMarkTagsAsSeen={handleMarkTagsAsSeen}
              setNotificationFilter={setNotificationFilterType}
              onOpenNotifications={() => { setNotificationFilterType('all'); setIsNotificationsOpen(true); }}
              isPostUploading={isPostUploading}
              postUploadProgress={postUploadProgress}
              postUploadStage={postUploadStage}
            />
          </Suspense>
        );
      case 'zips':
        return (
          <Suspense fallback={<ZipsSkeleton />}>
            <Zips
              zips={zips}
              currentUser={currentUser}
              onPromptLogin={handlePromptLogin}
              onViewProfile={handleViewProfile}
              onToggleZipLike={handleToggleZipLike}
              onReactToZip={handleReactToZip}
              onOpenUploadZipPage={openUploadZipPage}
              onOpenZipComments={openZipComments}
              onOpenShareZipPage={openShareZipPage}
              focusZipId={focusedZipId}
              onOpenLocation={handleOpenZipLocation}
              onDeleteZip={handleModDeleteZip}
              onDeleteOwnZip={handleDeleteOwnZip}
              onBanUser={handleBanUser}
              onReportContent={handleReportContent}
            />
          </Suspense>
        );
      case 'discover':
        return (
          <Suspense fallback={<DiscoverSkeleton />}>
            <Discover posts={posts} deviceLocation={deviceLocation} currentUser={currentUser} allUsers={users} onPromptLogin={handlePromptLogin} onViewProfile={handleViewProfileOverlay} onToggleFollow={handleToggleFollow} onToggleLike={handleToggleLike} onReactToPost={handleReactToPost} onAddComment={handleAddComment} onOpenComments={openPostComments} onSharePost={handleSharePost} onOpenSharePage={openSharePostPage} onToggleSave={handleToggleSave} onDeletePost={handleDeletePost} onBanUser={handleBanUser} onReportContent={handleReportContent} onOpenPostViewer={(list, idx) => openPostViewer(list, idx)} zips={zips} onOpenZip={openZip} onOpenLocation={handleOpenPostLocation} />
          </Suspense>
        );
      case 'marketplace':
        return (
          <Suspense fallback={<MarketplaceSkeleton />}>
            <Marketplace
              listings={listings}
              onOpenMyRating={() => { if (currentUser) setRatingProfileUser(currentUser); }}
              resetSignal={marketplaceResetSignal}
              currentUser={currentUser}
              allUsers={users}
              deviceLocation={deviceLocation}
              canModerate={isRidazModerator}
              onPromptLogin={handlePromptLogin}
              offers={listingOffers}
              reviews={listingReviews}
              listingContacts={listingContacts}
              onContactSeller={handleContactSeller}
              focusListingId={focusedListingId}
              onFocusListingHandled={() => setFocusedListingId(null)}
              onSaveListing={handleSaveListing}
              onBumpListing={handleBumpListing}
              onMakeOffer={handleMakeOffer}
              onRespondToOffer={handleRespondToOffer}
              onSubmitReview={handleSubmitReview}
              onToggleSave={handleToggleSaveListing}
              onConfirmSale={handleConfirmSale}
              onRelistListing={handleRelistListing}
              onDeleteListing={handleDeleteListing}
              onReportListing={(listingId) => { handleReportContent('listing', listingId); alert('Thanks, we\'ll review this listing.'); }}
              onMessageSeller={handleStartChat}
              onViewProfile={handleViewProfileOverlay}
            />
          </Suspense>
        );
      case 'spot-map':
        // Rendered persistently outside renderContent() so the Leaflet map
        // instance doesn't get destroyed/recreated on every tab switch.
        return null;
      case 'messenger':
        return (
          <Suspense fallback={<MessengerSkeleton />}>
            <Messenger onStartChat={handleStartChat} threads={messageThreads} currentUser={currentUser} allUsers={users} onSelectThread={(thread) => { openThread(thread.id); setShopChatsOpen(Boolean(thread.listingId)); }} showShopChats={shopChatsOpen} onToggleShopChats={setShopChatsOpen} onStartGroupChat={() => setCreateGroupOpen(true)} onPromptLogin={handlePromptLogin} onViewProfile={handleViewProfile} stories={stories} onOpenNewsViewer={(list, idx) => openNewsViewer(list, idx)}/>
          </Suspense>
        );
      case 'notifications':
        return (
          <Suspense fallback={<NotificationsSkeleton />}>
            <Notifications currentUser={currentUser} posts={posts} allUsers={users} onViewProfile={handleViewProfile} onBack={() => handleNavChange(NAV_ITEMS[0])} onMarkAsSeen={handleMarkNotificationsAsSeen} onMarkCommentsAsSeen={handleMarkCommentsAsSeen} onMarkLikesAsSeen={handleMarkLikesAsSeen} onMarkTagsAsSeen={handleMarkTagsAsSeen} newCounts={{ comments: notificationCounts.comments, tags: notificationCounts.tags, likes: notificationCounts.likes }} filterType={notificationFilterType} />
          </Suspense>
        );
      case 'profile':
        console.log('Profile tab - profileToShow:', profileToShow, 'currentUser:', currentUser);
        if (!currentUser) {
          return (
            <Suspense fallback={<div className="flex items-center justify-center h-full"><div className="text-gray-400">Loading...</div></div>}>
              {/* Fixed height (h-full) so this scrolls: the profile tab's <main> is overflow-hidden, so a
                  min-h-full box just grew past the screen and got clipped with no way to scroll. */}
              <div className="bg-app-gradient h-full overflow-y-auto overflow-x-hidden" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
                <div className="w-full px-4 pt-10" style={{ paddingBottom: 'calc(var(--sab-native, env(safe-area-inset-bottom)) + 8rem)' }}>
                  <h2 className="text-3xl font-black mb-4 text-center">Sign in to view profiles</h2>
                  <Auth onLogin={handleLogin} onSignUp={handleSignUp} onGoogleSignIn={handleGoogleSignIn} onAppleSignIn={handleAppleSignIn} onBrowseAsGuest={handleBrowseAsGuest} onOpenPrivacyPolicy={() => openLegalPage('privacy')} onOpenTerms={() => openLegalPage('terms')} onOpenEula={() => openLegalPage('eula')} embedded />
                </div>
              </div>
            </Suspense>
          );
        }
        if (!profileToShow) return <div className="p-4 text-center">Please log in to see your profile.</div>;
        return (
          <Suspense fallback={<ProfileSkeleton />}>
            <Profile user={profileToShow} currentUser={currentUser!} onBanAndEraseUser={handleBanAndEraseUser} posts={posts} spots={spots} stories={stories} allUsers={users} onLogout={handleLogout} onDeleteAccount={handleDeleteAccount} onUpdateUser={handleUpdateUser} onStartChat={(u: User) => handleMessageFromProfile(u)} onToggleFollow={handleToggleFollow} onViewProfile={handleViewProfile} onToggleLike={handleToggleLike} onReactToPost={handleReactToPost} onAddComment={handleAddComment} onOpenComments={openPostComments} onSharePost={handleSharePost} onOpenSharePage={openSharePostPage} onPromptLogin={handlePromptLogin} onToggleSave={handleToggleSave} onDeletePost={handleDeletePost} onBanUser={handleBanUser} onReportContent={handleReportContent} onOpenPostViewer={(list, idx) => openPostViewer(list, idx)} onOpenNewsViewer={openNewsViewer} onOpenSpot={handleOpenSpotInMap} messageThreads={messageThreads} onOpenPrivacyPolicy={() => openLegalPage('privacy', { returnTarget: 'main-profile-settings' })} onOpenCustomerSupport={() => openLegalPage('support', { returnTarget: 'main-profile-settings' })} onOpenTerms={() => openLegalPage('terms', { returnTarget: 'main-profile-settings' })} onOpenMarketing={() => openLegalPage('marketing', { returnTarget: 'main-profile-settings' })} startInSettingsMode={legalReturnTarget === 'main-profile-settings'} onSettingsModeChange={(open) => { if (!open && legalReturnTarget === 'main-profile-settings') setLegalReturnTarget(null); }} onBlock={handleBlockUser} onUnblock={handleUnblockUser} onReportUser={handleReportUser} onReportProblem={handleReportProblem} onOpenDeveloperDashboard={() => setIsDeveloperDashboardOpen(true)} isDeleteAccountPageOpen={isDeleteAccountPageOpen} onOpenDeleteAccountPage={openDeleteAccountPage} onCloseDeleteAccountPage={closeDeleteAccountPage} onOpenLocation={handleOpenPostLocation} />
          </Suspense>
        );
      default:
        return <Feed posts={posts} deviceLocation={deviceLocation} stories={stories} zips={feedZips} currentUser={currentUser} allUsers={users} onPromptLogin={handlePromptLogin} setActiveNav={handleNavChange} onViewProfile={handleViewProfile} onToggleLike={handleToggleLike} onReactToPost={handleReactToPost} onAddComment={handleAddComment} onOpenComments={openPostComments} onAddPost={handleAddPost} onAddStory={handleAddStory} onSharePost={handleSharePost} onOpenSharePage={openSharePostPage} onToggleSave={handleToggleSave} onDeletePost={handleDeletePost} onBanUser={handleBanUser} onReportContent={handleReportContent} onOpenUploadPage={openUploadPage} onOpenUploadNewsPage={openUploadNewsPage} onOpenUploadZipPage={openUploadZipPage} onOpenZip={openZip} onOpenLocation={handleOpenPostLocation} isPostUploading={isPostUploading} postUploadProgress={postUploadProgress} postUploadStage={postUploadStage} />;
    }
  };

  // Global iOS-style edge-swipe-to-go-back gesture. Only active while a
  // sub-page/menu is actually open (never on the base feed/map/zips views),
  // so it can't interfere with those surfaces' own gestures (post media
  // carousels, Leaflet map pan, Zips vertical scroll-snap, etc). Priority
  // order below mirrors the app's real visual stacking (topmost surface
  // first), so the swipe always closes whatever the user is currently
  // looking at, then reveals whatever was underneath.
  const isAnySwipeBackTargetOpen = !!(
    legalPage || isUploadPageOpen || isUploadNewsPageOpen || isUploadZipPageOpen || newsViewer ||
    profileOverlay || zipCommentsPage || postCommentsPage || shareLocationPageOpen ||
    shareEventPage || shareJamSeshPage || shareSpotPage || shareZipPage || sharePostPage ||
    postViewer || selectedThread || isCreateGroupOpen
  );

  const handleGlobalSwipeBack = useCallback(() => {
    if (legalPage) { closeLegalPage(); return; }
    if (isUploadPageOpen) { handleCloseUploadPost(); return; }
    if (isUploadNewsPageOpen) { closeUploadNewsPage(); return; }
    if (isUploadZipPageOpen) { handleCloseUploadZip(); return; }
    if (newsViewer) { closeNewsViewer(); return; }
    if (profileOverlay) { setProfileOverlay(null); return; }
    if (zipCommentsPage) { closeZipComments(); return; }
    if (postCommentsPage) { closePostComments(); return; }
    if (shareLocationPageOpen) { closeShareLocationPage(); return; }
    if (shareEventPage) { closeShareEventPage(); return; }
    if (shareJamSeshPage) { closeShareJamSeshPage(); return; }
    if (shareSpotPage) { closeShareSpotPage(); return; }
    if (shareZipPage) { closeShareZipPage(); return; }
    if (sharePostPage) { closeSharePostPage(); return; }
    if (postViewer) { closePostViewer(); return; }
    if (selectedThread) { setSelectedThreadId(null); setFallbackThread(null); return; }
    if (isCreateGroupOpen) { setCreateGroupOpen(false); return; }
  }, [
    legalPage, closeLegalPage, isUploadPageOpen, handleCloseUploadPost, isUploadNewsPageOpen, closeUploadNewsPage,
    isUploadZipPageOpen, handleCloseUploadZip, newsViewer, closeNewsViewer, profileOverlay, zipCommentsPage,
    closeZipComments, postCommentsPage, closePostComments, shareLocationPageOpen, closeShareLocationPage,
    shareEventPage, closeShareEventPage, shareJamSeshPage, closeShareJamSeshPage, shareSpotPage, closeShareSpotPage,
    shareZipPage, closeShareZipPage, sharePostPage, closeSharePostPage, postViewer, closePostViewer,
    selectedThread, isCreateGroupOpen,
  ]);

  useSwipeBack({ enabled: isAnySwipeBackTargetOpen, onSwipeBack: handleGlobalSwipeBack });

  if (showBanned) {
    return <BannedPage onClose={() => setShowBanned(false)} />;
  }

  if ((isLoading || !isAuthResolved) && !isGuest) {
    // Plain, unanimated fallback - the actual loading experience is
    // index.html's #boot-loading-screen (see the effect above), which now
    // correctly stays up for this entire window. This only ever paints if
    // that boot screen is somehow already gone (shouldn't happen in normal
    // use), so it deliberately matches its dark backdrop color rather than
    // having its own competing animation.
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[#05060a] text-white">
        <img src={APP_LOGO_SRC} alt="RIDAZ logo" className="h-24 w-24 rounded-2xl object-contain drop-shadow-[0_8px_24px_rgba(0,0,0,0.6)]" />
      </div>
    );
  }

  if (legalPage) {
    return <LegalDocumentPage kind={legalPage.type} onClose={() => closeLegalPage()} />;
  }


  if (!currentUser && !isGuest) {
    return <Auth onLogin={handleLogin} onSignUp={handleSignUp} onGoogleSignIn={handleGoogleSignIn} onAppleSignIn={handleAppleSignIn} onBrowseAsGuest={handleBrowseAsGuest} onOpenPrivacyPolicy={() => openLegalPage('privacy')} onOpenTerms={() => openLegalPage('terms')} onOpenEula={() => openLegalPage('eula')} />;
  }

  if (currentUser?.needsGoogleOnboarding) {
    return (
      <Suspense fallback={<div className="flex items-center justify-center h-screen bg-app-backdrop text-gray-400">Loading...</div>}>
        <GoogleOnboarding currentUser={currentUser} onComplete={handleCompleteGoogleOnboarding} />
      </Suspense>
    );
  }

  if (currentUser?.needsWelcomeOnboarding) {
    return (
      <Suspense fallback={<div className="flex items-center justify-center h-screen bg-app-backdrop text-gray-400">Loading...</div>}>
        <WelcomeOnboarding currentUser={currentUser} onComplete={handleCompleteWelcomeOnboarding} />
      </Suspense>
    );
  }

  if (isUploadPageOpen && !postDraft?.mediaItems?.length) {
    return <CreateMediaPage onClose={handleCloseUploadPost} onNext={handleCreateNext} />;
  }

  if (isUploadPageOpen) {
    return (
      <UploadPostPage
        currentUser={currentUser}
        allUsers={users}
        onPromptLogin={handlePromptLogin}
        onAddPost={handleAddPost}
        onClose={handleCloseUploadPost}
        initialDraft={postDraft}
        onRequestLocationPick={handleRequestPostLocationPick}
      />
    );
  }

  if (isUploadNewsPageOpen) {
    return (
      <UploadNewsPage
        currentUser={currentUser}
        onPromptLogin={handlePromptLogin}
        onAddStory={handleAddStory}
        onClose={() => { setNewsInitialFile(null); closeUploadNewsPage(); }}
        initialFile={newsInitialFile}
      />
    );
  }

  if (isUploadZipPageOpen) {
    return (
      <UploadZipPage
        currentUser={currentUser}
        onPromptLogin={handlePromptLogin}
        onAddZip={handleAddZip}
        onClose={handleCloseUploadZip}
        initialDraft={zipDraft}
        onRequestLocationPick={handleRequestZipLocationPick}
      />
    );
  }

  // Dedicated full-page news view
  if (newsViewer && newsViewer.stories && newsViewer.stories.length > 0) {
    return (
      <NewsViewerPage
        stories={newsViewer.stories}
        startIndex={newsViewer.startIndex}
        onClose={() => closeNewsViewer(true)}
        onViewProfile={handleViewProfile}
        currentUserId={currentUser?.id}
        onDeleteItem={handleDeleteStoryItem}
        onReplyToNews={handleReplyToNews}
      />
    );
  }

  const activeCommentsPost = postCommentsPage
    ? posts.find((post) => post.id === postCommentsPage.postId) || postCommentsPage.fallbackPost
    : null;
  const activeCommentsZip = zipCommentsPage
    ? zips.find((zip) => zip.id === zipCommentsPage.zipId) || zipCommentsPage.fallbackZip
    : null;

  const mainScrollClasses = activeNav.id === 'spot-map' || activeNav.id === 'zips'
    ? 'overflow-hidden relative'
    : activeNav.id === 'profile'
      ? 'overflow-hidden'
    : selectedThread || isCreateGroupOpen
      ? 'overflow-hidden'
      : 'overflow-y-auto pb-48';

  return (
    <div
      data-ridaz-section={activeNav.id} className="ridaz-app bg-app-backdrop text-white flex w-full max-w-[820px] flex-col overflow-hidden border-x border-white/5 mx-auto shadow-2xl relative md:my-3 md:rounded-[2rem] md:border md:border-gray-700"
      style={{ height: '100dvh' }}
    >
      <main id="app-scroll" className={`flex-grow min-h-0 ${mainScrollClasses}`}>
        <div
          key={selectedThread ? `${activeNav.id}-thread` : activeNav.id}
          className={`${activeNav.id === 'profile' || activeNav.id === 'zips' || selectedThread || isCreateGroupOpen ? 'h-full' : 'min-h-full'} swoosh-view`}
        >
          {renderContent()}
        </div>
        {hasVisitedSpotMap && (
          <div className={activeNav.id === 'spot-map' ? 'absolute inset-0' : 'hidden'}>
            <Suspense fallback={<MapSkeleton />}>
              <SpotMap
                onOpenPostViewer={(list, idx) => openPostViewer(list, idx)}
                onOpenZip={openZip}
                onCheckInSpot={handleCheckInSpot}
                spots={spots}
                shops={shops}
                jamSeshes={jamSeshes}
                events={events}
                currentUser={currentUser}
                allUsers={users}
                threads={messageThreads}
                deviceLocation={deviceLocation}
                onDeviceLocationUpdate={setDeviceLocation}
                sharedLocations={liveLocationShares}
                hasActiveLocationSharing={activeOwnedLocationShares.length > 0}
                focusLocation={mapFocusLocation}
                focusSpotId={focusedSpotId}
                onFocusSpotHandled={() => setFocusedSpotId(null)}
                focusJamSeshId={focusedJamSeshId}
                onFocusJamSeshHandled={() => setFocusedJamSeshId(null)}
                focusEventId={focusedEventId}
                onFocusEventHandled={() => setFocusedEventId(null)}
                onPromptLogin={handlePromptLogin}
                onOpenShareLocationPage={openShareLocationPage}
                onStopSharingLocation={handleStopSharingLocation}
                onViewProfile={handleViewProfileOverlay}
                isGuest={isGuest}
                isActive={activeNav.id === 'spot-map'}
                onAddSpot={handleAddSpot}
                onAddJamSesh={handleAddJamSesh}
                onAddEvent={handleAddEvent}
                onToggleEventInterest={handleToggleEventInterest}
                onOpenEventChat={handleOpenEventChat}
                onOpenShareSpot={openShareSpotPage}
                onOpenShareJamSesh={openShareJamSeshPage}
                onOpenShareEvent={openShareEventPage}
                onAddJamSeshComment={handleAddJamSeshComment}
                onAddEventComment={handleAddEventComment}
                onAddSpotPhoto={handleAddSpotPhoto}
                onAddSpotComment={handleAddSpotComment}
                onAddSpotMediaComment={handleAddSpotMediaComment}
                onToggleSpotMediaLike={handleToggleSpotMediaLike}
                onRateSpot={handleRateSpot}
                onAddShop={handleAddShop}
                pinUploadProgress={pinUploadProgress}
                onRateShop={handleRateShop}
                onAddShopPhoto={handleAddShopPhoto}
                onAddShopComment={handleAddShopComment}
                onEditShop={isRidazModerator ? handleEditShop : undefined}
                onDeleteSpot={handleModDeleteSpot}
                onDeleteJamSesh={handleModDeleteJamSesh}
                onDeleteEvent={handleModDeleteEvent}
                onDeleteShop={handleModDeleteShop}
                onDeleteSpotComment={(spotId, commentId) => handleModDeleteComment('spots', spotId, commentId)}
                onToggleFavoriteSpot={handleToggleFavoriteSpot}
                posts={posts}
                zips={zips}
                onSubmitCorrection={handleSubmitCorrection}
                onReportSpot={(spotId) => handleReportContent('spot', spotId)}
                onReportSpotComment={(spotId, commentId) => handleReportContent('comment', commentId, { parentType: 'spots', parentId: spotId })}
                onReportJamSesh={(seshId) => handleReportContent('sesh', seshId)}
                onReportEvent={(eventId) => handleReportContent('event', eventId)}
                isPickingLocation={isPickingLocationFor !== null}
                pickSportId={isPickingLocationFor === 'post' ? postDraft?.sport?.id : isPickingLocationFor === 'zip' ? zipDraft?.sport?.id : undefined}
                onLocationPicked={handleLocationPicked}
                onCancelLocationPick={handleCancelLocationPick}
                checkPinRateLimit={getPinRateLimitStatus}
                hasReturnTarget={returnTo !== null}
                onReturnToOrigin={handleReturnFromMap}
                returnLabel={returnTo?.type === 'zip' ? 'Back to zip' : 'Back to post'}
              />
            </Suspense>
          </div>
        )}
      </main>
      {ratingProfileUser && (
        <div className="absolute inset-0 z-[1100] overflow-y-auto bg-black" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
          <Suspense fallback={null}>
            <RatingProfilePage
              user={users.find((u) => u.id === ratingProfileUser.id) || ratingProfileUser}
              reviews={listingReviews}
              isSelf={ratingProfileUser.id === currentUser?.id}
              onBack={() => setRatingProfileUser(null)}
              onViewProfile={(u) => { setRatingProfileUser(null); handleViewProfile(u); }}
            />
          </Suspense>
        </div>
      )}
      {isNotificationsOpen && (
        <div className="absolute inset-0 z-[800] overflow-y-auto bg-black" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
          <Suspense fallback={null}>
            <Notifications currentUser={currentUser} posts={posts} allUsers={users} onViewProfile={handleViewProfile} onBack={() => setIsNotificationsOpen(false)} onMarkAsSeen={handleMarkNotificationsAsSeen} onMarkCommentsAsSeen={handleMarkCommentsAsSeen} onMarkLikesAsSeen={handleMarkLikesAsSeen} onMarkTagsAsSeen={handleMarkTagsAsSeen} newCounts={{ comments: notificationCounts.comments, tags: notificationCounts.tags, likes: notificationCounts.likes }} filterType={notificationFilterType} />
          </Suspense>
        </div>
      )}
      {postViewer && (
        <PostViewerPage
          posts={postViewer.posts.map((viewerPost) => posts.find((post) => post.id === viewerPost.id) || viewerPost)}
          startIndex={postViewer.startIndex}
          sourceNavId={postViewer.sourceNav.id}
          currentUser={currentUser}
          allUsers={users}
          onClose={closePostViewer}
          onPromptLogin={handlePromptLogin}
          onViewProfile={handleViewProfileOverlay}
          onToggleLike={handleToggleLike}
          onReactToPost={handleReactToPost}
          onAddComment={handleAddComment}
          onOpenComments={openPostComments}
          onSharePost={handleSharePost}
          onOpenSharePage={openSharePostPage}
          onToggleSave={handleToggleSave}
          onDeletePost={handleDeletePost} onBanUser={handleBanUser} onReportContent={handleReportContent}
          onOpenLocation={handleOpenPostLocation}
        />
      )}
      {sharePostPage && (
        <SharePostPage
          post={posts.find((post) => post.id === sharePostPage.postId) || sharePostPage.fallbackPost}
          currentUser={currentUser}
          allUsers={users}
          onClose={() => closeSharePostPage()}
          onSharePost={handleSharePost}
        />
      )}
      {shareZipPage && (
        <ShareZipPage
          zip={zips.find((zip) => zip.id === shareZipPage.zipId) || shareZipPage.fallbackZip}
          currentUser={currentUser}
          allUsers={users}
          onClose={() => closeShareZipPage()}
          onShareZip={handleShareZip}
        />
      )}
      {shareSpotPage && (
        <ShareSpotPage
          spot={spots.find((spot) => spot.id === shareSpotPage.spotId) || shareSpotPage.fallbackSpot}
          currentUser={currentUser}
          allUsers={users}
          onClose={() => closeShareSpotPage()}
          onShareSpot={handleShareSpot}
        />
      )}
      {shareJamSeshPage && (
        <ShareJamSeshPage
          jamSesh={jamSeshes.find((sesh) => sesh.id === shareJamSeshPage.jamSeshId) || shareJamSeshPage.fallbackJamSesh}
          currentUser={currentUser}
          allUsers={users}
          onClose={() => closeShareJamSeshPage()}
          onShareJamSesh={handleShareJamSesh}
        />
      )}
      {shareEventPage && (
        <ShareEventPage
          event={events.find((evt) => evt.id === shareEventPage.eventId) || shareEventPage.fallbackEvent}
          currentUser={currentUser}
          allUsers={users}
          onClose={() => closeShareEventPage()}
          onShareEvent={handleShareEvent}
        />
      )}
      {shareLocationPageOpen && (
        <ShareLocationPage
          currentUser={currentUser}
          threads={messageThreads}
          activeThreadIds={activeOwnedLocationThreadIds}
          onClose={() => closeShareLocationPage()}
          onShareLocation={handleShareLocation}
          onStopSharingAll={handleStopSharingLocation}
        />
      )}
      {activeCommentsPost && (
        <PostCommentsPage
          post={activeCommentsPost}
          currentUser={currentUser}
          allUsers={users}
          onClose={() => closePostComments()}
          onPromptLogin={handlePromptLogin}
          onViewProfile={handleViewProfile}
          onToggleLike={handleToggleLike}
          onToggleSave={handleToggleSave}
          onAddComment={handleAddComment}
          onToggleCommentLike={handleToggleCommentLike}
          onDeleteComment={(postId, commentId) => handleModDeleteComment('posts', postId, commentId)}
          onReportComment={(postId, commentId) => handleReportContent('comment', commentId, { parentType: 'posts', parentId: postId })}
        />
      )}
      {activeCommentsZip && (
        <ZipCommentsPage
          zip={activeCommentsZip}
          currentUser={currentUser}
          allUsers={users}
          onClose={() => closeZipComments()}
          onPromptLogin={handlePromptLogin}
          onViewProfile={handleViewProfile}
          onToggleLike={handleToggleZipLike}
          onAddComment={handleAddZipComment}
          onDeleteComment={(zipId, commentId) => handleModDeleteComment('zips', zipId, commentId)}
          onReportComment={(zipId, commentId) => handleReportContent('comment', commentId, { parentType: 'zips', parentId: zipId })}
          onReportZip={(zipId) => handleReportContent('zip', zipId)}
        />
      )}
      {isDeveloperDashboardOpen && isRidazModerator && (
        <DeveloperDashboard
          onClose={() => setIsDeveloperDashboardOpen(false)}
          onDeletePost={handleDeletePost}
          onDeleteZip={handleModDeleteZip}
          onDeleteComment={handleModDeleteComment}
          onDeleteSpot={handleModDeleteSpot}
          onDeleteJamSesh={handleModDeleteJamSesh}
          onDeleteEvent={handleModDeleteEvent}
          onDeleteListing={handleDeleteListing}
          onBanUser={handleBanUser}
          onUnbanUser={handleUnbanUser}
          shops={shops}
          parks={spots.filter(s => s.category === 'skatepark')}
          onAcceptShopSubmission={handleAcceptShopSubmission}
          onAcceptParkSubmission={handleAcceptParkSubmission}
          allUsers={users}
          posts={posts}
          zips={zips}
          spots={spots}
          jamSeshes={jamSeshes}
          events={events}
          listings={listings}
          onViewUser={(user) => { setIsDeveloperDashboardOpen(false); handleViewProfile(user); }}
          contentCounts={{
            posts: posts.length,
            zips: zips.length,
            spots: spots.filter((sp) => sp.category !== 'skatepark').length,
            parks: spots.filter((sp) => sp.category === 'skatepark').length,
            shops: shops.length,
            seshes: jamSeshes.length,
            events: events.length,
            listings: listings.length,
            threads: messageThreads.length,
          }}
        />
      )}
      {currentUser && (
        <CallWatcher
          threads={messageThreads}
          currentUser={currentUser}
          allUsers={users}
          busy={Boolean(callSession)}
          disabled={nativeRingReady}
          onAccept={(thread) => setCallSession({ thread, type: thread.activeCall?.type || 'audio' })}
        />
      )}
      {callSession && currentUser && (
        <Suspense fallback={null}>
          <CallScreen
            thread={callSession.thread}
            currentUser={currentUser}
            allUsers={users}
            type={callSession.type}
            joinOnly={callSession.joinOnly}
            onClose={() => setCallSession(null)}
          />
        </Suspense>
      )}
      {profileOverlay && currentUser && (
        <div className="fixed inset-0 z-[1300] bg-app-backdrop overflow-hidden">
          <Suspense fallback={<ProfileSkeleton />}>
            <Profile
              user={profileOverlay}
              currentUser={currentUser}
              posts={posts}
              spots={spots}
              stories={stories}
              allUsers={users}
              onLogout={handleLogout}
              onDeleteAccount={handleDeleteAccount}
              onUpdateUser={handleUpdateUser}
              onStartChat={async (u: User) => { setProfileOverlay(null); await handleMessageFromProfile(u, true); }}
              onToggleFollow={handleToggleFollow}
              onViewProfile={handleViewProfile}
              onToggleLike={handleToggleLike}
              onReactToPost={handleReactToPost}
              onAddComment={handleAddComment}
              onOpenComments={openPostComments}
              onSharePost={handleSharePost}
              onOpenSharePage={openSharePostPage}
              onPromptLogin={handlePromptLogin}
              onToggleSave={handleToggleSave}
              onDeletePost={handleDeletePost} onBanUser={handleBanUser} onReportContent={handleReportContent}
              onOpenPostViewer={(list, idx) => openPostViewer(list, idx)}
              onOpenNewsViewer={openNewsViewer}
              onOpenSpot={handleOpenSpotInMap}
              messageThreads={messageThreads}
              onOpenPrivacyPolicy={() => openLegalPage('privacy', { returnTarget: 'overlay-profile-settings' })}
              onOpenCustomerSupport={() => openLegalPage('support', { returnTarget: 'overlay-profile-settings' })}
              onOpenTerms={() => openLegalPage('terms', { returnTarget: 'overlay-profile-settings' })}
              onOpenMarketing={() => openLegalPage('marketing', { returnTarget: 'overlay-profile-settings' })}
              startInSettingsMode={legalReturnTarget === 'overlay-profile-settings'}
              onSettingsModeChange={(open) => { if (!open && legalReturnTarget === 'overlay-profile-settings') setLegalReturnTarget(null); }}
              onBlock={handleBlockUser}
              onUnblock={handleUnblockUser}
              onReportUser={handleReportUser}
              onReportProblem={handleReportProblem}
              onOpenDeveloperDashboard={() => setIsDeveloperDashboardOpen(true)}
              isDeleteAccountPageOpen={isDeleteAccountPageOpen}
              onOpenDeleteAccountPage={openDeleteAccountPage}
              onCloseDeleteAccountPage={closeDeleteAccountPage}
              onClose={() => setProfileOverlay(null)}
            />
          </Suspense>
        </div>
      )}
      {returnTo && activeNav.id === 'spot-map' && !selectedThread && !isCreateGroupOpen && (
        <button
          type="button"
          onClick={handleReturnFromMap}
          className="absolute left-4 z-[950] flex items-center gap-1.5 rounded-full bg-white px-3.5 py-2 text-xs font-extrabold text-black shadow-lg shadow-black/40 transition hover:bg-gray-200 active:scale-95"
          style={{ bottom: 'calc(var(--sab-native, env(safe-area-inset-bottom)) + 4.75rem)' }}
          aria-label={returnTo.type === 'zip' ? 'Back to zip' : 'Back to post'}
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {returnTo.type === 'zip' ? 'Back to zip' : 'Back to post'}
        </button>
      )}
      {(!selectedThread && !isCreateGroupOpen) &&
        <BottomNav elevated={!!postViewer} activeNav={activeNav} setActiveNav={(item) => { if (postViewer) closePostViewer(); handleNavChange(item); }} unreadCount={totalUnreadCount} notificationsCount={unseenNotifications} isGuest={isGuest} onPromptLogin={handlePromptLogin} accentColor={currentUser?.profileAccentColor} />
      }
      {showOGPopup && <OGWelcomePopup onClose={() => setShowOGPopup(false)} />}
    </div>
  );
};

export default App;
